import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import AppIcon from "../components/AppIcon";
import SchoolBrand from "../components/SchoolBrand";
import StatusToast from "../components/StatusToast";
import "../styles/dashboard.css";

const money = (value) => `INR ${Number(value || 0).toLocaleString("en-IN")}`;
const getFeeTypeLabel = (feeType = "tuition") => {
  if (feeType === "transport") return "Transport";
  if (feeType === "annual") return "Annual";
  if (feeType === "admission") return "Admission";
  return "Tuition";
};
const getPaymentPeriodLabel = (feeType = "tuition") => (["annual", "admission"].includes(feeType) ? "Academic Year" : "Month");
const getOneTimeFeeEntry = (student, feeType = "annual") => {
  const fee = feeType === "admission" ? student?.fees?.admissionFee : student?.fees?.annualFee;
  const academicYear = fee?.academicYear || student?.academicYear || "";
  if (!academicYear) return null;
  return {
    monthKey: academicYear,
    monthLabel: academicYear,
    amount: Number(fee?.amount || 0),
    status: fee?.status || "pending",
    paidAt: fee?.paidAt || null,
    dueDate: fee?.dueDate || null
  };
};

const initialStudentForm = {
  id: "",
  name: "",
  photoUrl: "",
  class: "",
  section: "",
  rollNo: "",
  admissionNo: "",
  academicYear: "",
  dateOfBirth: "",
  gender: "",
  bloodGroup: "",
  address: "",
  city: "",
  state: "",
  country: "",
  pincode: "",
  emergencyContactName: "",
  emergencyContactPhone: "",
  notes: "",
  monthlyFee: "",
  annualFee: "",
  admissionFee: "",
  transportEnabled: false,
  transportMonthlyFee: "",
  transportRouteName: "",
  transportPickupPoint: "",
  transportDropPoint: "",
  transportVehicleNo: "",
  parentName: "",
  parentEmail: "",
  parentPhone: "",
  parentAddress: "",
  parentPassword: "",
  regenerateSchedule: false
};

const initialTeacherForm = {
  id: "",
  name: "",
  photoUrl: "",
  employeeId: "",
  email: "",
  phone: "",
  gender: "",
  dateOfBirth: "",
  qualification: "",
  experienceYears: "",
  address: "",
  city: "",
  state: "",
  country: "",
  pincode: "",
  subjects: "",
  classTeacherClass: "",
  classTeacherSection: "",
  classTeacherOf: "",
  notes: "",
  monthlySalary: "",
  password: "",
  isActive: true
};

const initialEventForm = {
  id: "",
  title: "",
  description: ""
};

const initialNoticeForm = {
  id: "",
  title: "",
  details: "",
  noticeDate: "",
  targetType: "all",
  targetTeachers: [],
  targetStudents: []
};

const initialExamForm = {
  id: "",
  title: "",
  className: "",
  section: "",
  subject: "",
  maxMarks: 100,
  examDate: "",
  academicYear: ""
};

const initialTimetableForm = {
  className: "",
  section: "",
  dayOfWeek: 1,
  period: 1,
  startTime: "",
  endTime: "",
  subject: "",
  teacherName: "",
  room: ""
};

const initialHallTicketForm = {
  examId: "",
  studentId: "",
  seatNumber: "",
  room: "",
  centerName: "",
  instructions: ""
};

const initialTcForm = {
  studentId: "",
  issueDate: "",
  lastAttendanceDate: "",
  reason: "",
  destinationSchool: "",
  conduct: "Good",
  remarks: "",
  deactivateStudent: true
};

const formatLocalDateInput = (date = new Date()) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const formatLocalMonthInput = (date = new Date()) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
};

const getTodayInput = () => formatLocalDateInput(new Date());
const getDefaultAcademicYearText = (date = new Date()) => {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const startYear = month >= 4 ? year : year - 1;
  return `${startYear}-${String((startYear + 1) % 100).padStart(2, "0")}`;
};
const getAcademicYearFromMonthKey = (monthKey = "") => {
  const match = String(monthKey || "").match(/^(\d{4})-(\d{2})$/);
  if (!match) return "";
  const year = Number(match[1]);
  const month = Number(match[2]);
  const startYear = month >= 4 ? year : year - 1;
  return `${startYear}-${String((startYear + 1) % 100).padStart(2, "0")}`;
};
const formatPaymentDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};
const getNextAcademicYearText = (academicYear = "") => {
  const input = String(academicYear || "").trim();
  const match = input.match(/^(\d{4})/);
  const startYear = match ? Number(match[1]) + 1 : Number(getDefaultAcademicYearText().slice(0, 4)) + 1;
  return `${startYear}-${String((startYear + 1) % 100).padStart(2, "0")}`;
};

const initialOfflinePaymentForm = {
  studentId: "",
  feeType: "tuition",
  monthKey: "",
  amount: "",
  paymentMethod: "Cash",
  status: "paid",
  paymentDate: getTodayInput(),
  remarks: ""
};

const initialInventoryForm = {
  id: "",
  name: "",
  sku: "",
  category: "",
  subCategory: "",
  unit: "pcs",
  quantity: "",
  minStock: "",
  reorderLevel: "",
  unitCost: "",
  supplierName: "",
  supplierPhone: "",
  location: "",
  notes: ""
};

const initialInventoryAdjustForm = {
  itemId: "",
  type: "in",
  quantity: "",
  reason: "",
  referenceNo: "",
  unitCost: ""
};

const monthLabels = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const SECTION_OPTIONS = ["A", "B", "C", "D", "E"];

const tokenize = (value = "") =>
  String(value)
    .toLowerCase()
    .trim()
    .split(/\s+/)
    .filter(Boolean);

const toDateInputValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return formatLocalDateInput(date);
};

const matchesAllTokens = (haystack, tokens) => {
  if (tokens.length === 0) return true;
  const source = String(haystack || "").toLowerCase();
  return tokens.every((token) => source.includes(token));
};

const escapeExcelCell = (value) => {
  const text = Array.from(String(value ?? ""))
    .map((char) => {
      const code = char.charCodeAt(0);
      return code < 32 || code === 127 ? " " : char;
    })
    .join("")
    .trim();
  const safeText = /^[=+\-@]/.test(text) ? `'${text}` : text;
  return safeText
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
};

const formatExportDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-IN");
};

const makeDownloadName = (value) => String(value || "students")
  .replace(/[^a-z0-9]+/gi, "-")
  .replace(/^-+|-+$/g, "")
  .toLowerCase();

const getReadableAdminError = (err, fallbackMessage) => {
  const message = String(err?.response?.data?.message || "").trim();
  if (!message) return fallbackMessage;

  const lowered = message.toLowerCase();
  if (lowered.includes("email is already used")) {
    return "This email is already linked to another account. Please use a different email.";
  }
  if (lowered.includes("teacher email already in use")) {
    return "This teacher email is already in use. Please use a different email.";
  }
  if (lowered.includes("parent email already in use")) {
    return "This parent email is already in use. Please use a different email.";
  }
  if (lowered.includes("duplicate key")) {
    return "This record already exists with the same unique details. Please review the form.";
  }
  if (lowered.includes("invalid parentid")) {
    return "The selected parent account is not valid. Please choose or create a valid parent.";
  }
  if (lowered.includes("student not found")) {
    return "The selected student could not be found. Please refresh and try again.";
  }
  if (lowered.includes("teacher not found")) {
    return "The selected teacher could not be found. Please refresh and try again.";
  }
  if (lowered.includes("missing fields")) {
    return "Please complete all required fields before saving.";
  }
  if (lowered.includes("not authorized")) {
    return "You are not authorized for this action. Please log in again.";
  }
  if (lowered.includes("session")) {
    return "Session issue detected. Please re-login if this continues.";
  }

  return message;
};

const formatDateKey = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const getMonthTitle = (date) =>
  date.toLocaleDateString("en-US", { month: "long", year: "numeric" });

const getCalendarMatrix = (monthDate) => {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const first = new Date(year, month, 1);
  const startDay = first.getDay();
  const start = new Date(year, month, 1 - startDay);
  const weeks = [];
  const todayKey = formatDateKey(new Date());

  for (let w = 0; w < 6; w += 1) {
    const row = [];
    for (let d = 0; d < 7; d += 1) {
      const cell = new Date(start);
      cell.setDate(start.getDate() + (w * 7) + d);
      row.push({
        day: cell.getDate(),
        dateKey: formatDateKey(cell),
        isCurrentMonth: cell.getMonth() === month,
        isToday: formatDateKey(cell) === todayKey
      });
    }
    weeks.push(row);
  }
  return weeks;
};

const timeAgo = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const ms = Date.now() - date.getTime();
  const mins = Math.max(1, Math.floor(ms / 60000));
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days > 1 ? "s" : ""} ago`;
};

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });

function AreaGraph({ data = [] }) {
  const width = 980;
  const height = 320;
  const left = 48;
  const right = 24;
  const top = 20;
  const bottom = 42;

  const points = useMemo(() => {
    const chartData = data.length === 12
      ? data
      : Array.from({ length: 12 }, (_, idx) => ({ month: idx + 1, collections: 0, fees: 0, expenses: 0 }));

    const usableWidth = width - left - right;
    const usableHeight = height - top - bottom;
    const maxValue = Math.max(100, ...chartData.flatMap((d) => [d.collections || 0, d.fees || 0, d.expenses || 0]));

    const mk = (key) => chartData.map((entry, index) => {
      const x = left + (index * usableWidth) / (chartData.length - 1);
      const y = top + usableHeight - ((entry[key] || 0) / maxValue) * usableHeight;
      return { x, y, label: monthLabels[(entry.month || index + 1) - 1] || monthLabels[index] };
    });

    return { collections: mk("collections"), fees: mk("fees"), expenses: mk("expenses"), usableHeight };
  }, [data]);

  const areaPath = (series) => {
    if (series.length === 0) return "";
    const first = series[0];
    const last = series[series.length - 1];
    const baseline = height - bottom;
    const line = series.map((p, idx) => `${idx === 0 ? "M" : "L"}${p.x} ${p.y}`).join(" ");
    return `${line} L${last.x} ${baseline} L${first.x} ${baseline} Z`;
  };

  const smoothLinePath = (series) => {
    if (series.length < 2) return "";
    let d = `M${series[0].x} ${series[0].y}`;
    for (let i = 1; i < series.length; i += 1) {
      const prev = series[i - 1];
      const curr = series[i];
      const cx = (prev.x + curr.x) / 2;
      d += ` Q${cx} ${prev.y} ${curr.x} ${curr.y}`;
    }
    return d;
  };

  return (
    <svg className="admin-area-chart" viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Fees collection and expenses chart">
      <defs>
        <linearGradient id="gradCollections" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#2890f0" stopOpacity="0.45" />
          <stop offset="100%" stopColor="#2890f0" stopOpacity="0.05" />
        </linearGradient>
        <linearGradient id="gradFees" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#f0515e" stopOpacity="0.38" />
          <stop offset="100%" stopColor="#f0515e" stopOpacity="0.05" />
        </linearGradient>
        <linearGradient id="gradExpenses" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#f3c818" stopOpacity="0.4" />
          <stop offset="100%" stopColor="#f3c818" stopOpacity="0.06" />
        </linearGradient>
      </defs>
      {[0, 25, 50, 75, 100].map((tick) => {
        const y = top + points.usableHeight - (tick / 100) * points.usableHeight;
        return <line key={tick} x1={left} y1={y} x2={width - right} y2={y} stroke="#e5eaf2" strokeWidth="1" strokeDasharray="4 6" />;
      })}

      <path d={areaPath(points.expenses)} fill="url(#gradExpenses)" />
      <path d={areaPath(points.fees)} fill="url(#gradFees)" />
      <path d={areaPath(points.collections)} fill="url(#gradCollections)" />

      <path d={smoothLinePath(points.expenses)} fill="none" stroke="#dca900" strokeWidth="2.5" />
      <path d={smoothLinePath(points.fees)} fill="none" stroke="#f0515e" strokeWidth="2.5" />
      <path d={smoothLinePath(points.collections)} fill="none" stroke="#2591f0" strokeWidth="2.5" />

      {points.collections.map((point) => (
        <g key={`pt-${point.x}`}>
          <circle cx={point.x} cy={point.y} r="4" fill="#2591f0" stroke="#fff" strokeWidth="2" />
          <text x={point.x} y={height - 14} textAnchor="middle" fill="#7a8394" fontSize="11">{point.label}</text>
        </g>
      ))}
    </svg>
  );
}

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [activeTab, setActiveTab] = useState("dashboard");

  const [overview, setOverview] = useState({ totalStudents: 0, totalTeachers: 0, totalParents: 0, yearlyEarnings: 0, chart: [] });
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [revenue, setRevenue] = useState({ dailyRevenue: 0, monthlyRevenue: 0, yearlyRevenue: 0, monthlyBreakdown: [] });
  const [recentPayments, setRecentPayments] = useState([]);
  const [proofs, setProofs] = useState([]);
  const [proofStatus, setProofStatus] = useState("pending");
  const [pendingProofCounts, setPendingProofCounts] = useState({ tuition: 0, annual: 0, admission: 0, transport: 0 });
  const [events, setEvents] = useState([]);
  const [notices, setNotices] = useState([]);
  const [selectedDateKey, setSelectedDateKey] = useState(() => formatDateKey(new Date()));
  const [eventForm, setEventForm] = useState({ ...initialEventForm });
  const [noticeForm, setNoticeForm] = useState({ ...initialNoticeForm });
  const [savingEvent, setSavingEvent] = useState(false);
  const [savingNotice, setSavingNotice] = useState(false);
  const [examForm, setExamForm] = useState(initialExamForm);
  const [savingExam, setSavingExam] = useState(false);
  const [exams, setExams] = useState([]);
  const [selectedExamId, setSelectedExamId] = useState("");
  const [examMarksRows, setExamMarksRows] = useState([]);
  const [savingExamMarks, setSavingExamMarks] = useState(false);
  const [studentAttendanceDate, setStudentAttendanceDate] = useState(() => getTodayInput());
  const [studentAttendanceClass, setStudentAttendanceClass] = useState("");
  const [studentAttendanceSection, setStudentAttendanceSection] = useState("");
  const [studentAttendanceSearch, setStudentAttendanceSearch] = useState("");
  const [studentAttendanceRows, setStudentAttendanceRows] = useState([]);
  const [studentAttendanceMonthlyRows, setStudentAttendanceMonthlyRows] = useState([]);
  const [studentAttendanceMonth, setStudentAttendanceMonth] = useState(() => formatLocalMonthInput(new Date()));
  const [studentAttendanceLoading, setStudentAttendanceLoading] = useState(false);
  const [studentAttendanceMonthlyLoading, setStudentAttendanceMonthlyLoading] = useState(false);
  const [studentAttendanceTotals, setStudentAttendanceTotals] = useState({ present: 0, absent: 0, leave: 0, notMarked: 0 });
  const [studentAttendanceView, setStudentAttendanceView] = useState("daily");
  const [studentAttendancePage, setStudentAttendancePage] = useState(1);
  const [studentAttendanceMonthlyPage, setStudentAttendanceMonthlyPage] = useState(1);
  const [studentAttendancePagination, setStudentAttendancePagination] = useState({ page: 1, limit: 25, totalRecords: 0, totalPages: 1 });
  const [studentAttendanceMonthlyPagination, setStudentAttendanceMonthlyPagination] = useState({ page: 1, limit: 25, totalRecords: 0, totalPages: 1 });
  const [markingStudentAttendanceId, setMarkingStudentAttendanceId] = useState("");
  const [timetableForm, setTimetableForm] = useState(initialTimetableForm);
  const [timetableEntries, setTimetableEntries] = useState([]);
  const [savingTimetable, setSavingTimetable] = useState(false);
  const [defaulterMonth, setDefaulterMonth] = useState(() => formatLocalMonthInput(new Date()));
  const [defaulterRows, setDefaulterRows] = useState([]);
  const [transportDefaulterMonth, setTransportDefaulterMonth] = useState(() => formatLocalMonthInput(new Date()));
  const [transportDefaulterRows, setTransportDefaulterRows] = useState([]);
  const [sendingReminderId, setSendingReminderId] = useState("");
  const [leaveStatusFilter, setLeaveStatusFilter] = useState("");
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [reviewingLeaveId, setReviewingLeaveId] = useState("");
  const [hallTicketRows, setHallTicketRows] = useState([]);
  const [hallTicketExamFilter, setHallTicketExamFilter] = useState("");
  const [hallTicketForm, setHallTicketForm] = useState(initialHallTicketForm);
  const [savingHallTicket, setSavingHallTicket] = useState(false);
  const [tcRows, setTcRows] = useState([]);
  const [tcPagination, setTcPagination] = useState({ page: 1, limit: 20, totalRecords: 0, totalPages: 1 });
  const [tcPage, setTcPage] = useState(1);
  const [tcSearch, setTcSearch] = useState("");
  const [tcStatusFilter, setTcStatusFilter] = useState("");
  const [tcForm, setTcForm] = useState(() => ({ ...initialTcForm, issueDate: getTodayInput() }));
  const [savingTc, setSavingTc] = useState(false);
  const [tcPreview, setTcPreview] = useState({ open: false, certificate: null });
  const [tcCancelModal, setTcCancelModal] = useState({ open: false, certificateId: "", tcNo: "", reason: "" });
  const [processingTcCancel, setProcessingTcCancel] = useState(false);
  const [archiveBeforeDate, setArchiveBeforeDate] = useState(() => {
    const dt = new Date();
    dt.setFullYear(dt.getFullYear() - 1);
    return formatLocalDateInput(dt);
  });
  const [archiveEntities, setArchiveEntities] = useState({
    transactions: true,
    paymentProofs: true,
    notices: true,
    events: false,
    homeworkSubmissions: false,
    reminderLogs: true,
    leaveRequests: false
  });
  const [archiveSummary, setArchiveSummary] = useState([]);
  const [runningArchive, setRunningArchive] = useState(false);
  const [studentPage, setStudentPage] = useState(1);
  const [teacherPage, setTeacherPage] = useState(1);
  const [feePage, setFeePage] = useState(1);
  const [transportPage, setTransportPage] = useState(1);
  const PAGE_SIZE = 20;
  const [visibleMonth, setVisibleMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  const [studentForm, setStudentForm] = useState(initialStudentForm);
  const [teacherForm, setTeacherForm] = useState(initialTeacherForm);

  const [studentSearch, setStudentSearch] = useState("");
  const [studentExportClass, setStudentExportClass] = useState("");
  const [promotionFilter, setPromotionFilter] = useState({
    fromClass: "",
    fromSection: "",
    currentYear: "",
    nextYear: getNextAcademicYearText(getDefaultAcademicYearText()),
    resetRollNumbers: true,
    startRollNo: 1
  });
  const [promotionRows, setPromotionRows] = useState([]);
  const [promotionLoading, setPromotionLoading] = useState(false);
  const [promotionSaving, setPromotionSaving] = useState(false);
  const [promotionSummary, setPromotionSummary] = useState(null);
  const [teacherSearch, setTeacherSearch] = useState("");
  const [feeSearchId, setFeeSearchId] = useState("");
  const [feeSearchPhone, setFeeSearchPhone] = useState("");
  const [feeMonthKey, setFeeMonthKey] = useState("");
  const [feeAcademicYear, setFeeAcademicYear] = useState("");
  const [annualSearchId, setAnnualSearchId] = useState("");
  const [annualSearchPhone, setAnnualSearchPhone] = useState("");
  const [admissionSearchId, setAdmissionSearchId] = useState("");
  const [admissionSearchPhone, setAdmissionSearchPhone] = useState("");
  const [transportSearchId, setTransportSearchId] = useState("");
  const [transportSearchPhone, setTransportSearchPhone] = useState("");
  const [transportMonthKey, setTransportMonthKey] = useState("");
  const [offlinePaymentForm, setOfflinePaymentForm] = useState(initialOfflinePaymentForm);
  const [savingOfflinePayment, setSavingOfflinePayment] = useState(false);
  const [inventorySummary, setInventorySummary] = useState({
    totalItems: 0,
    totalUnits: 0,
    totalValue: 0,
    lowStockCount: 0,
    outOfStockCount: 0,
    categoriesCount: 0
  });
  const [inventoryItems, setInventoryItems] = useState([]);
  const [inventoryCategories, setInventoryCategories] = useState([]);
  const [inventorySearch, setInventorySearch] = useState("");
  const [inventoryCategoryFilter, setInventoryCategoryFilter] = useState("");
  const [inventoryStatusFilter, setInventoryStatusFilter] = useState("all");
  const [inventoryForm, setInventoryForm] = useState(initialInventoryForm);
  const [savingInventory, setSavingInventory] = useState(false);
  const [inventoryAdjustForm, setInventoryAdjustForm] = useState(initialInventoryAdjustForm);
  const [savingInventoryAdjust, setSavingInventoryAdjust] = useState(false);
  const [inventoryMovements, setInventoryMovements] = useState([]);
  const [selectedInventoryItem, setSelectedInventoryItem] = useState(null);
  const [inventoryPage, setInventoryPage] = useState(1);
  const [annualPage, setAnnualPage] = useState(1);
  const [admissionPage, setAdmissionPage] = useState(1);
  const [paymentHistoryRows, setPaymentHistoryRows] = useState([]);
  const [paymentHistoryLoading, setPaymentHistoryLoading] = useState(false);
  const [paymentHistoryPage, setPaymentHistoryPage] = useState(1);
  const [paymentHistoryPagination, setPaymentHistoryPagination] = useState({ page: 1, limit: 50, totalRecords: 0, totalPages: 1 });
  const [paymentHistoryTotals, setPaymentHistoryTotals] = useState([]);
  const [paymentHistoryFilters, setPaymentHistoryFilters] = useState({
    search: "",
    feeType: "",
    status: "paid",
    method: "",
    month: "",
    academicYear: "",
    from: "",
    to: ""
  });
  const [paymentReportFilters, setPaymentReportFilters] = useState({
    className: "",
    month: "",
    feeType: "tuition"
  });
  const [quickMarkConfirm, setQuickMarkConfirm] = useState({
    open: false,
    student: null,
    entry: null,
    feeType: "tuition",
    status: "paid"
  });
  const [processingQuickMark, setProcessingQuickMark] = useState(false);
  const [paymentDetailsModal, setPaymentDetailsModal] = useState({
    open: false,
    loading: false,
    error: "",
    student: null,
    entry: null,
    feeType: "tuition",
    transaction: null
  });
  const [actionConfirm, setActionConfirm] = useState({
    open: false,
    title: "",
    message: "",
    actionType: "",
    targetId: ""
  });
  const [proofPreview, setProofPreview] = useState({
    open: false,
    proof: null,
    imageFailed: false,
    zoom: 1
  });
  const [processingActionConfirm, setProcessingActionConfirm] = useState(false);
  const [savingStudent, setSavingStudent] = useState(false);
  const [savingTeacher, setSavingTeacher] = useState(false);
  const [showPasswords, setShowPasswords] = useState({ parent: false, teacher: false });
  const [noticeTeacherSearch, setNoticeTeacherSearch] = useState("");
  const [noticeStudentSearch, setNoticeStudentSearch] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState({
    students: false,
    teachers: false,
    fees: false,
    annual: false,
    admission: false,
    transport: false,
    operations: false
  });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [adminProfile, setAdminProfile] = useState(null);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [showAdminPassword, setShowAdminPassword] = useState(false);
  const [adminPasswordForm, setAdminPasswordForm] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [changingAdminPassword, setChangingAdminPassword] = useState(false);
  const profileMenuRef = useRef(null);
  const [attendanceMonth, setAttendanceMonth] = useState(() => formatLocalMonthInput(new Date()));
  const [attendanceDate, setAttendanceDate] = useState(() => formatLocalDateInput(new Date()));
  const [attendanceSearch, setAttendanceSearch] = useState("");
  const [markingAttendanceId, setMarkingAttendanceId] = useState("");
  const [loadingAttendance, setLoadingAttendance] = useState(false);
  const [attendanceData, setAttendanceData] = useState({
    teachers: [],
    totals: { totalTeachers: 0, presentDays: 0, absentDays: 0, halfDays: 0, leaveDays: 0, estimatedPayout: 0 },
    daysInMonth: 30,
    workingDays: 26,
    sundayDateKeys: []
  });

  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  }, []);

  const fetchOverview = async () => {
    const { data } = await api.get("/admin/overview");
    setOverview(data || { totalStudents: 0, totalTeachers: 0, totalParents: 0, yearlyEarnings: 0, chart: [] });
  };

  const fetchAdminProfile = async () => {
    const { data } = await api.get("/admin/profile");
    setAdminProfile(data.profile || null);
  };

  const fetchStudents = async () => {
    const { data } = await api.get("/admin/students");
    setStudents(data.students || []);
  };

  const fetchTeachers = async () => {
    const { data } = await api.get("/admin/teachers");
    setTeachers(data.teachers || []);
  };

  const fetchRevenue = async () => {
    const { data } = await api.get("/admin/revenue");
    setRevenue(data || { dailyRevenue: 0, monthlyRevenue: 0, yearlyRevenue: 0, monthlyBreakdown: [] });
    const now = new Date();
    const from = new Date(now);
    from.setDate(now.getDate() - 30);
    const fromStr = from.toISOString().slice(0, 10);
    const toStr = now.toISOString().slice(0, 10);
    try {
      const recent = await api.get(`/admin/revenue?from=${fromStr}&to=${toStr}`);
      setRecentPayments(recent.data?.range?.payments || []);
    } catch {
      setRecentPayments([]);
    }
  };

  const fetchPaymentHistory = async (page = paymentHistoryPage) => {
    setPaymentHistoryLoading(true);
    try {
      const query = new URLSearchParams();
      query.set("page", String(page));
      query.set("limit", "50");
      Object.entries(paymentHistoryFilters).forEach(([key, value]) => {
        const normalized = String(value || "").trim();
        if (normalized) query.set(key, normalized);
      });
      const { data } = await api.get(`/admin/payment-history?${query.toString()}`);
      setPaymentHistoryRows(data.payments || []);
      setPaymentHistoryPagination(data.pagination || { page, limit: 50, totalRecords: 0, totalPages: 1 });
      setPaymentHistoryTotals(data.totals || []);
    } finally {
      setPaymentHistoryLoading(false);
    }
  };

  const fetchProofs = async (status = "pending", feeType = "") => {
    const query = new URLSearchParams();
    query.set("status", status);
    if (["tuition", "transport", "annual", "admission"].includes(String(feeType || "").toLowerCase())) {
      query.set("feeType", String(feeType).toLowerCase());
    }
    const { data } = await api.get(`/fees/proofs?${query.toString()}`);
    setProofs(data.proofs || []);
  };

  const fetchPendingProofCounts = async () => {
    const [tuitionRes, annualRes, admissionRes, transportRes] = await Promise.all([
      api.get("/fees/proofs?status=pending&feeType=tuition"),
      api.get("/fees/proofs?status=pending&feeType=annual"),
      api.get("/fees/proofs?status=pending&feeType=admission"),
      api.get("/fees/proofs?status=pending&feeType=transport"),
    ]);
    setPendingProofCounts({
      tuition: Number(tuitionRes.data?.proofs?.length || 0),
      annual: Number(annualRes.data?.proofs?.length || 0),
      admission: Number(admissionRes.data?.proofs?.length || 0),
      transport: Number(transportRes.data?.proofs?.length || 0),
    });
  };

  const fetchInventorySummary = async () => {
    const { data } = await api.get("/inventory/summary");
    setInventorySummary(data || {
      totalItems: 0,
      totalUnits: 0,
      totalValue: 0,
      lowStockCount: 0,
      outOfStockCount: 0,
      categoriesCount: 0
    });
  };

  const fetchInventoryItems = async () => {
    const query = new URLSearchParams();
    if (inventorySearch.trim()) query.set("search", inventorySearch.trim());
    if (inventoryCategoryFilter) query.set("category", inventoryCategoryFilter);
    if (inventoryStatusFilter) query.set("status", inventoryStatusFilter);
    const { data } = await api.get(`/inventory/items?${query.toString()}`);
    setInventoryItems(data.items || []);
    setInventoryCategories(data.categories || []);
  };

  const fetchInventoryMovements = async (itemId) => {
    if (!itemId) {
      setInventoryMovements([]);
      return;
    }
    const { data } = await api.get(`/inventory/items/${itemId}/movements?limit=15`);
    setInventoryMovements(data.movements || []);
  };

  const fetchEvents = async () => {
    const monthStart = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), 1);
    const monthEnd = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + 1, 0);
    const from = monthStart.toISOString().slice(0, 10);
    const to = monthEnd.toISOString().slice(0, 10);
    const { data } = await api.get(`/admin/events?from=${from}&to=${to}`);
    setEvents(data.events || []);
  };

  const fetchNotices = async () => {
    const { data } = await api.get("/admin/notices");
    setNotices(data.notices || []);
  };

  const fetchExams = async () => {
    const { data } = await api.get("/admin/exams");
    setExams(data.exams || []);
  };

  const fetchExamMarks = async (examId) => {
    if (!examId) {
      setExamMarksRows([]);
      return;
    }
    const { data } = await api.get(`/admin/exams/${examId}/marks`);
    setExamMarksRows((data.students || []).map((row) => ({
      studentId: row.student?.id,
      name: row.student?.name || "",
      rollNo: row.student?.rollNo || "",
      marks: row.marks === "" ? "" : Number(row.marks || 0),
      remarks: row.remarks || ""
    })));
  };

  const fetchStudentAttendance = async () => {
    setStudentAttendanceLoading(true);
    try {
      const query = new URLSearchParams({
        date: studentAttendanceDate,
        className: studentAttendanceClass,
        section: studentAttendanceSection,
        search: studentAttendanceSearch,
        page: String(studentAttendancePage),
        limit: "25"
      });
      const { data } = await api.get(`/admin/attendance/students?${query.toString()}`);
      setStudentAttendanceRows((data.records || []).map((item) => ({ ...item, attendanceStatus: item.attendanceStatus || "not_marked" })));
      setStudentAttendanceTotals(data.totals || { present: 0, absent: 0, leave: 0, notMarked: 0 });
      setStudentAttendancePagination(data.pagination || { page: 1, limit: 25, totalRecords: 0, totalPages: 1 });
    } finally {
      setStudentAttendanceLoading(false);
    }
  };

  const fetchStudentAttendanceMonthly = async () => {
    setStudentAttendanceMonthlyLoading(true);
    try {
      const query = new URLSearchParams({
        month: studentAttendanceMonth,
        className: studentAttendanceClass,
        section: studentAttendanceSection,
        search: studentAttendanceSearch,
        page: String(studentAttendanceMonthlyPage),
        limit: "25"
      });
      const { data } = await api.get(`/admin/attendance/students/monthly?${query.toString()}`);
      setStudentAttendanceMonthlyRows(data.summary || []);
      setStudentAttendanceMonthlyPagination(data.pagination || { page: 1, limit: 25, totalRecords: 0, totalPages: 1 });
    } finally {
      setStudentAttendanceMonthlyLoading(false);
    }
  };

  const fetchTimetable = async () => {
    const { data } = await api.get("/admin/timetable");
    setTimetableEntries(data.entries || []);
  };

  const fetchDefaulters = async () => {
    const { data } = await api.get(`/admin/defaulters?month=${defaulterMonth}`);
    setDefaulterRows(data.rows || []);
  };

  const fetchTransportDefaulters = async () => {
    const { data } = await api.get(`/admin/defaulters?month=${transportDefaulterMonth}&feeType=transport`);
    setTransportDefaulterRows(data.rows || []);
  };

  const fetchLeaveRequests = async () => {
    const { data } = await api.get(`/admin/leave-requests?status=${leaveStatusFilter}`);
    setLeaveRequests(data.requests || []);
  };

  const fetchHallTickets = async () => {
    const query = hallTicketExamFilter ? `?examId=${hallTicketExamFilter}` : "";
    const { data } = await api.get(`/admin/hall-tickets${query}`);
    setHallTicketRows(data.rows || []);
  };

  const fetchTransferCertificates = async () => {
    const query = new URLSearchParams();
    query.set("page", String(tcPage));
    query.set("limit", "20");
    if (String(tcStatusFilter || "").trim()) query.set("status", String(tcStatusFilter || "").trim());
    if (String(tcSearch || "").trim()) query.set("search", String(tcSearch || "").trim());
    const { data } = await api.get(`/admin/tc?${query.toString()}`);
    setTcRows(data.rows || []);
    setTcPagination(data.pagination || { page: 1, limit: 20, totalRecords: 0, totalPages: 1 });
  };

  const fetchArchiveSummary = async () => {
    const { data } = await api.get("/admin/archive/summary");
    setArchiveSummary(data.rows || []);
  };

  const fetchPromotionPreview = async () => {
    if (!String(promotionFilter.fromClass || "").trim()) {
      setError("Select class to load promotion preview.");
      return;
    }
    setPromotionLoading(true);
    setPromotionSummary(null);
    setError("");
    try {
      const query = new URLSearchParams();
      query.set("fromClass", String(promotionFilter.fromClass || "").trim());
      if (String(promotionFilter.fromSection || "").trim()) query.set("fromSection", String(promotionFilter.fromSection || "").trim());
      if (String(promotionFilter.currentYear || "").trim()) query.set("currentYear", String(promotionFilter.currentYear || "").trim());
      const { data } = await api.get(`/admin/students/promotion/preview?${query.toString()}`);
      const startRollNo = Math.max(1, Number(promotionFilter.startRollNo || 1));
      const rows = (data.rows || []).map((row, index) => ({
        studentId: row.studentId,
        name: row.name,
        parentName: row.parentName || "",
        currentClass: row.class,
        currentSection: row.section || "",
        currentRollNo: row.rollNo || "",
        currentYear: row.academicYear || "",
        promote: true,
        nextClass: row.suggestedNextClass || row.class,
        nextSection: row.suggestedNextSection || row.section || "",
        nextRollNo: promotionFilter.resetRollNumbers ? (startRollNo + index) : (row.suggestedNextRollNo || ""),
        monthlyFee: Number(row.currentMonthlyFee || 0),
        transportEnabled: !!row.transportEnabled,
        transportMonthlyFee: Number(row.currentTransportMonthlyFee || 0)
      }));
      setPromotionRows(rows);
      setSuccess(`Loaded ${rows.length} students for promotion preview.`);
    } catch (err) {
      setError(getReadableAdminError(err, "Failed to load promotion preview"));
    } finally {
      setPromotionLoading(false);
    }
  };

  const applyBulkPromotion = async () => {
    if (!String(promotionFilter.nextYear || "").trim()) {
      setError("Next academic year is required.");
      return;
    }
    const selectedRows = promotionRows.filter((row) => row.promote);
    if (selectedRows.length === 0) {
      setError("Select at least one student to promote.");
      return;
    }
    setPromotionSaving(true);
    setPromotionSummary(null);
    setError("");
    try {
      const payload = {
        nextYear: String(promotionFilter.nextYear || "").trim(),
        resetRollNumbers: !!promotionFilter.resetRollNumbers,
        startRollNo: Math.max(1, Number(promotionFilter.startRollNo || 1)),
        regenerateSchedule: true,
        updates: promotionRows.map((row) => ({
          studentId: row.studentId,
          promote: !!row.promote,
          nextClass: row.nextClass,
          nextSection: row.nextSection || "",
          nextRollNo: row.nextRollNo,
          monthlyFee: Number(row.monthlyFee || 0),
          transportMonthlyFee: row.transportEnabled ? Number(row.transportMonthlyFee || 0) : 0
        }))
      };
      const { data } = await api.post("/admin/students/promotion/bulk", payload);
      setPromotionSummary(data || null);
      await Promise.all([fetchStudents(), fetchOverview(), fetchRevenue()]);
      setSuccess(data?.message || "Bulk promotion completed.");
      await fetchPromotionPreview();
    } catch (err) {
      setError(getReadableAdminError(err, "Failed to apply bulk promotion"));
    } finally {
      setPromotionSaving(false);
    }
  };

  const fetchTeacherAttendance = async (silent = false) => {
    if (!silent) setLoadingAttendance(true);
    try {
      const { data } = await api.get(`/admin/teacher-attendance?month=${attendanceMonth}&search=${encodeURIComponent(attendanceSearch)}`);
      setAttendanceData({
        teachers: data.teachers || [],
        totals: data.totals || { totalTeachers: 0, presentDays: 0, absentDays: 0, halfDays: 0, leaveDays: 0, estimatedPayout: 0 },
        daysInMonth: data.daysInMonth || 30,
        workingDays: data.workingDays || 26,
        sundayDateKeys: data.sundayDateKeys || []
      });
    } finally {
      if (!silent) setLoadingAttendance(false);
    }
  };

  const loadAll = async () => {
    setError("");
    await Promise.all([
      fetchOverview(),
      fetchStudents(),
      fetchTeachers(),
      fetchRevenue(),
      fetchEvents(),
      fetchNotices(),
      fetchExams(),
      fetchPendingProofCounts(),
      fetchAdminProfile().catch(() => {})
    ]);
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("adminToken");
    localStorage.removeItem("teacherToken");
    localStorage.removeItem("parentToken");
    localStorage.removeItem("role");
    localStorage.removeItem("user");
    navigate("/login");
  };

  const copyToClipboard = async (value, label) => {
    const text = String(value || "").trim();
    if (!text || text === "-") {
      setError(`${label} not available to copy.`);
      return;
    }
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const fallback = document.createElement("textarea");
        fallback.value = text;
        fallback.setAttribute("readonly", "");
        fallback.style.position = "absolute";
        fallback.style.left = "-9999px";
        document.body.appendChild(fallback);
        fallback.select();
        document.execCommand("copy");
        document.body.removeChild(fallback);
      }
      setSuccess(`${label} copied.`);
    } catch {
      setError(`Failed to copy ${label.toLowerCase()}.`);
    }
  };

  useEffect(() => {
    let token = localStorage.getItem("adminToken");
    if (!token) {
      const fallback = localStorage.getItem("token");
      const role = localStorage.getItem("role");
      if (fallback && ["admin", "superadmin"].includes(role || "")) {
        token = fallback;
        localStorage.setItem("adminToken", fallback);
      }
    }
    if (!token) {
      navigate("/login");
      return;
    }

    const init = async () => {
      try {
        await loadAll();
      } catch (err) {
        if ([401, 403].includes(err.response?.status)) {
          setError("Session issue detected. Please re-login if this continues.");
        } else {
          setError(getReadableAdminError(err, "Failed to load admin dashboard"));
        }
      } finally {
        setLoading(false);
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  useEffect(() => {
    if (!error && !success) return undefined;
    const timer = setTimeout(() => {
      setError("");
      setSuccess("");
    }, 3000);
    return () => clearTimeout(timer);
  }, [error, success]);

  useEffect(() => {
    const onDocClick = (event) => {
      if (!profileMenuRef.current) return;
      if (!profileMenuRef.current.contains(event.target)) {
        setProfileMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token") || localStorage.getItem("adminToken");
    if (!token) return;
    fetchEvents().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleMonth]);

  useEffect(() => {
    if (!["fee-collection", "annual-collection", "admission-collection", "create-payment"].includes(activeTab)) return undefined;
    const timer = setInterval(() => {
      Promise.all([fetchStudents(), fetchOverview(), fetchRevenue()]).catch(() => {});
    }, 15000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (!["upi-proofs", "annual-upi-proofs", "admission-upi-proofs", "transport-upi-proofs"].includes(activeTab)) return undefined;
    const feeType = getProofFeeTypeForTab(activeTab);
    fetchProofs(proofStatus, feeType).catch(() => {});
    const timer = setInterval(() => fetchProofs(proofStatus, feeType).catch(() => {}), 15000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, proofStatus]);

  useEffect(() => {
    const token = localStorage.getItem("token") || localStorage.getItem("adminToken");
    if (!token) return undefined;
    fetchPendingProofCounts().catch(() => {});
    const timer = setInterval(() => fetchPendingProofCounts().catch(() => {}), 15000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeTab !== "teacher-attendance") return undefined;
    fetchTeacherAttendance().catch(() => {});
    const timer = setInterval(() => fetchTeacherAttendance(true).catch(() => {}), 20000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, attendanceMonth, attendanceSearch]);

  useEffect(() => {
    if (activeTab !== "student-attendance") return undefined;
    if (studentAttendanceView === "daily") {
      fetchStudentAttendance().catch(() => {});
    } else {
      fetchStudentAttendanceMonthly().catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, studentAttendanceView, studentAttendanceDate, studentAttendanceClass, studentAttendanceSection, studentAttendanceSearch, studentAttendanceMonth, studentAttendancePage, studentAttendanceMonthlyPage]);

  useEffect(() => {
    setStudentAttendancePage(1);
    setStudentAttendanceMonthlyPage(1);
  }, [studentAttendanceClass, studentAttendanceSection, studentAttendanceSearch, studentAttendanceDate, studentAttendanceMonth]);

  useEffect(() => {
    if (activeTab !== "exams") return undefined;
    fetchExams().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== "timetable") return undefined;
    fetchTimetable().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== "defaulters") return undefined;
    fetchDefaulters().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, defaulterMonth]);

  useEffect(() => {
    if (activeTab !== "transport-defaulters") return undefined;
    fetchTransportDefaulters().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, transportDefaulterMonth]);

  useEffect(() => {
    if (activeTab !== "leave-requests") return undefined;
    fetchLeaveRequests().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, leaveStatusFilter]);

  useEffect(() => {
    if (activeTab !== "hall-tickets") return undefined;
    fetchHallTickets().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, hallTicketExamFilter]);

  useEffect(() => {
    if (activeTab !== "transfer-certificate") return undefined;
    fetchTransferCertificates().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, tcPage, tcStatusFilter, tcSearch]);

  useEffect(() => {
    if (activeTab !== "archive") return undefined;
    fetchArchiveSummary().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== "payment-history") return undefined;
    fetchPaymentHistory(paymentHistoryPage).catch((err) => {
      setError(getReadableAdminError(err, "Failed to load payment history"));
    });
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, paymentHistoryPage, paymentHistoryFilters]);

  useEffect(() => {
    if (activeTab !== "inventory") return undefined;
    Promise.all([fetchInventorySummary(), fetchInventoryItems()]).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, inventorySearch, inventoryCategoryFilter, inventoryStatusFilter]);

  useEffect(() => {
    if (activeTab !== "exams" || !selectedExamId) return;
    fetchExamMarks(selectedExamId).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedExamId]);

  useEffect(() => {
    setStudentPage(1);
  }, [studentSearch, students.length]);

  useEffect(() => {
    setTeacherPage(1);
  }, [teacherSearch, teachers.length]);

  useEffect(() => {
    setFeePage(1);
  }, [feeSearchId, feeSearchPhone, feeAcademicYear, feeMonthKey, students.length]);

  useEffect(() => {
    setPaymentHistoryPage(1);
  }, [paymentHistoryFilters]);

  useEffect(() => {
    setTransportPage(1);
  }, [transportSearchId, transportSearchPhone, transportMonthKey, students.length]);

  useEffect(() => {
    setInventoryPage(1);
  }, [inventorySearch, inventoryCategoryFilter, inventoryStatusFilter, inventoryItems.length]);

  const promotionRowCount = promotionRows.length;

  useEffect(() => {
    if (!promotionFilter.resetRollNumbers) return;
    if (promotionRowCount === 0) return;
    const startRollNo = Math.max(1, Number(promotionFilter.startRollNo || 1));
    setPromotionRows((prev) => prev.map((row, index) => ({ ...row, nextRollNo: startRollNo + index })));
  }, [promotionFilter.resetRollNumbers, promotionFilter.startRollNo, promotionRowCount]);

  const filteredStudents = useMemo(() => {
    const tokens = tokenize(studentSearch);
    return students.filter((student) => {
      const parent = student.parent || {};
      const text = [
        student.name,
        student.class,
        student.section,
        student.rollNo,
        student.admissionNo,
        student.academicYear,
        parent.name,
        parent.email,
        parent.phone
      ].join(" ");
      return matchesAllTokens(text, tokens);
    });
  }, [students, studentSearch]);

  const filteredTeachers = useMemo(() => {
    const tokens = tokenize(teacherSearch);
    return teachers.filter((teacher) => {
      const text = [
        teacher.name,
        teacher.employeeId,
        teacher.email,
        teacher.phone,
        teacher.classTeacherOf,
        (teacher.subjects || []).join(" ")
      ].join(" ");
      return matchesAllTokens(text, tokens);
    });
  }, [teachers, teacherSearch]);

  const filteredFeeStudents = useMemo(() => {
    const idTokens = tokenize(feeSearchId);
    const phoneTokens = tokenize(feeSearchPhone);
    return students.filter((student) => {
      const parent = student.parent || {};
      const idHaystack = [
        student.id,
        student.rollNo,
        student.admissionNo,
        student.name,
        student.class,
        student.section
      ].join(" ");
      const phoneHaystack = [parent.phone, parent.email].join(" ");
      return matchesAllTokens(idHaystack, idTokens) && matchesAllTokens(phoneHaystack, phoneTokens);
    });
  }, [students, feeSearchId, feeSearchPhone]);

  const filteredAnnualStudents = useMemo(() => {
    const idTokens = tokenize(annualSearchId);
    const phoneTokens = tokenize(annualSearchPhone);
    return students.filter((student) => {
      const parent = student.parent || {};
      const idHaystack = [
        student.id,
        student.rollNo,
        student.admissionNo,
        student.name,
        student.class,
        student.section,
        student.fees?.annualFee?.academicYear
      ].join(" ");
      const phoneHaystack = [parent.phone, parent.email].join(" ");
      return matchesAllTokens(idHaystack, idTokens) && matchesAllTokens(phoneHaystack, phoneTokens);
    });
  }, [students, annualSearchId, annualSearchPhone]);

  const filteredAdmissionStudents = useMemo(() => {
    const idTokens = tokenize(admissionSearchId);
    const phoneTokens = tokenize(admissionSearchPhone);
    return students.filter((student) => {
      const parent = student.parent || {};
      const idHaystack = [
        student.id,
        student.rollNo,
        student.admissionNo,
        student.name,
        student.class,
        student.section,
        student.fees?.admissionFee?.academicYear
      ].join(" ");
      const phoneHaystack = [parent.phone, parent.email].join(" ");
      return matchesAllTokens(idHaystack, idTokens) && matchesAllTokens(phoneHaystack, phoneTokens);
    });
  }, [students, admissionSearchId, admissionSearchPhone]);

  const filteredTransportStudents = useMemo(() => {
    const idTokens = tokenize(transportSearchId);
    const phoneTokens = tokenize(transportSearchPhone);
    return students.filter((student) => {
      if (!student.transport?.enabled) return false;
      const idHaystack = [
        student.id,
        student.rollNo,
        student.admissionNo,
        student.name,
        student.class,
        student.section
      ].join(" ");
      const parent = student.parent || {};
      const phoneHaystack = [parent.phone, parent.email].join(" ");
      return matchesAllTokens(idHaystack, idTokens) && matchesAllTokens(phoneHaystack, phoneTokens);
    });
  }, [students, transportSearchId, transportSearchPhone]);

  const studentTotalPages = Math.max(1, Math.ceil(filteredStudents.length / PAGE_SIZE));
  const teacherTotalPages = Math.max(1, Math.ceil(filteredTeachers.length / PAGE_SIZE));
  const feeTotalPages = Math.max(1, Math.ceil(filteredFeeStudents.length / PAGE_SIZE));
  const annualTotalPages = Math.max(1, Math.ceil(filteredAnnualStudents.length / PAGE_SIZE));
  const admissionTotalPages = Math.max(1, Math.ceil(filteredAdmissionStudents.length / PAGE_SIZE));
  const transportTotalPages = Math.max(1, Math.ceil(filteredTransportStudents.length / PAGE_SIZE));
  const inventoryTotalPages = Math.max(1, Math.ceil(inventoryItems.length / PAGE_SIZE));

  const pagedStudents = useMemo(() => {
    const start = (Math.min(studentPage, studentTotalPages) - 1) * PAGE_SIZE;
    return filteredStudents.slice(start, start + PAGE_SIZE);
  }, [filteredStudents, studentPage, studentTotalPages, PAGE_SIZE]);

  const pagedTeachers = useMemo(() => {
    const start = (Math.min(teacherPage, teacherTotalPages) - 1) * PAGE_SIZE;
    return filteredTeachers.slice(start, start + PAGE_SIZE);
  }, [filteredTeachers, teacherPage, teacherTotalPages, PAGE_SIZE]);

  const pagedFeeStudents = useMemo(() => {
    const start = (Math.min(feePage, feeTotalPages) - 1) * PAGE_SIZE;
    return filteredFeeStudents.slice(start, start + PAGE_SIZE);
  }, [filteredFeeStudents, feePage, feeTotalPages, PAGE_SIZE]);

  const pagedAnnualStudents = useMemo(() => {
    const start = (Math.min(annualPage, annualTotalPages) - 1) * PAGE_SIZE;
    return filteredAnnualStudents.slice(start, start + PAGE_SIZE);
  }, [filteredAnnualStudents, annualPage, annualTotalPages, PAGE_SIZE]);

  const pagedAdmissionStudents = useMemo(() => {
    const start = (Math.min(admissionPage, admissionTotalPages) - 1) * PAGE_SIZE;
    return filteredAdmissionStudents.slice(start, start + PAGE_SIZE);
  }, [filteredAdmissionStudents, admissionPage, admissionTotalPages, PAGE_SIZE]);

  const pagedTransportStudents = useMemo(() => {
    const start = (Math.min(transportPage, transportTotalPages) - 1) * PAGE_SIZE;
    return filteredTransportStudents.slice(start, start + PAGE_SIZE);
  }, [filteredTransportStudents, transportPage, transportTotalPages, PAGE_SIZE]);

  const pagedInventoryItems = useMemo(() => {
    const start = (Math.min(inventoryPage, inventoryTotalPages) - 1) * PAGE_SIZE;
    return inventoryItems.slice(start, start + PAGE_SIZE);
  }, [inventoryItems, inventoryPage, inventoryTotalPages, PAGE_SIZE]);

  const studentStats = useMemo(() => {
    const active = filteredStudents.filter((student) => student.isActive).length;
    const inactive = Math.max(0, filteredStudents.length - active);
    return { total: filteredStudents.length, active, inactive };
  }, [filteredStudents]);

  const exportableStudentRows = useMemo(() => {
    if (!studentExportClass) return [];
    return students
      .filter((student) => String(student.class || "").trim() === String(studentExportClass).trim())
      .sort((a, b) => {
        const sectionCompare = String(a.section || "").localeCompare(String(b.section || ""), undefined, { sensitivity: "base" });
        if (sectionCompare !== 0) return sectionCompare;
        return Number(a.rollNo || 0) - Number(b.rollNo || 0)
          || String(a.name || "").localeCompare(String(b.name || ""), undefined, { sensitivity: "base" });
      });
  }, [students, studentExportClass]);

  const teacherStats = useMemo(() => {
    const active = filteredTeachers.filter((teacher) => teacher.isActive).length;
    const inactive = Math.max(0, filteredTeachers.length - active);
    return { total: filteredTeachers.length, active, inactive };
  }, [filteredTeachers]);

  const attendanceTeachers = useMemo(() => attendanceData.teachers || [], [attendanceData.teachers]);
  const isAttendanceDateSunday = useMemo(() => {
    if (!attendanceDate) return false;
    const date = new Date(`${attendanceDate}T00:00:00`);
    return !Number.isNaN(date.getTime()) && date.getDay() === 0;
  }, [attendanceDate]);

  const getAttendanceStatus = (teacher, dateKey) => {
    const row = (teacher.attendance || []).find((item) => item.dateKey === dateKey);
    return row?.status || "";
  };


  const feeStats = useMemo(() => {
    let paid = 0;
    let pending = 0;
    filteredFeeStudents.forEach((student) => {
      const month = (student.fees?.monthlyFees || []).find((item) => item.monthKey === feeMonthKey);
      if (month?.status === "paid") paid += 1;
      else pending += 1;
    });
    return { total: filteredFeeStudents.length, paid, pending };
  }, [filteredFeeStudents, feeMonthKey]);

  const annualStats = useMemo(() => {
    let paid = 0;
    let pending = 0;
    filteredAnnualStudents.forEach((student) => {
      if (student.fees?.annualFee?.status === "paid") paid += 1;
      else pending += 1;
    });
    return { total: filteredAnnualStudents.length, paid, pending };
  }, [filteredAnnualStudents]);

  const admissionStats = useMemo(() => {
    let paid = 0;
    let pending = 0;
    filteredAdmissionStudents.forEach((student) => {
      if (student.fees?.admissionFee?.status === "paid") paid += 1;
      else pending += 1;
    });
    return { total: filteredAdmissionStudents.length, paid, pending };
  }, [filteredAdmissionStudents]);

  const transportStats = useMemo(() => {
    let paid = 0;
    let pending = 0;
    filteredTransportStudents.forEach((student) => {
      const month = (student.transport?.monthlyFees || []).find((item) => item.monthKey === transportMonthKey);
      if (month?.status === "paid") paid += 1;
      else pending += 1;
    });
    return { total: filteredTransportStudents.length, paid, pending };
  }, [filteredTransportStudents, transportMonthKey]);

  const paymentStudent = useMemo(
    () => students.find((student) => String(student.id) === String(offlinePaymentForm.studentId)) || null,
    [students, offlinePaymentForm.studentId]
  );

  const paymentMonthOptions = useMemo(() => {
    if (offlinePaymentForm.feeType === "transport") {
      return paymentStudent?.transport?.monthlyFees || [];
    }
    if (["annual", "admission"].includes(offlinePaymentForm.feeType)) {
      const oneTimeEntry = getOneTimeFeeEntry(paymentStudent, offlinePaymentForm.feeType);
      return oneTimeEntry ? [oneTimeEntry] : [];
    }
    return paymentStudent?.fees?.monthlyFees || [];
  }, [paymentStudent, offlinePaymentForm.feeType]);

  const feeMonthOptions = useMemo(() => {
    const map = new Map();
    students.forEach((student) => {
      (student.fees?.monthlyFees || []).forEach((month) => {
        if (!map.has(month.monthKey)) map.set(month.monthKey, month.monthLabel);
      });
    });
    return Array.from(map.entries())
      .map(([monthKey, monthLabel]) => ({ monthKey, monthLabel }))
      .sort((a, b) => a.monthKey.localeCompare(b.monthKey));
  }, [students]);

  const feeAcademicYearOptions = useMemo(() => {
    const years = new Set();
    feeMonthOptions.forEach((month) => {
      const academicYear = getAcademicYearFromMonthKey(month.monthKey);
      if (academicYear) years.add(academicYear);
    });
    return Array.from(years).sort((a, b) => b.localeCompare(a, undefined, { numeric: true }));
  }, [feeMonthOptions]);

  const filteredFeeMonthOptions = useMemo(() => {
    if (!feeAcademicYear) return feeMonthOptions;
    return feeMonthOptions.filter((month) => getAcademicYearFromMonthKey(month.monthKey) === feeAcademicYear);
  }, [feeMonthOptions, feeAcademicYear]);

  const transportMonthOptions = useMemo(() => {
    const map = new Map();
    students.forEach((student) => {
      if (!student.transport?.enabled) return;
      (student.transport?.monthlyFees || []).forEach((month) => {
        if (!map.has(month.monthKey)) map.set(month.monthKey, month.monthLabel);
      });
    });
    return Array.from(map.entries())
      .map(([monthKey, monthLabel]) => ({ monthKey, monthLabel }))
      .sort((a, b) => a.monthKey.localeCompare(b.monthKey));
  }, [students]);

  const studentClassOptions = useMemo(() => {
    const map = new Map();
    students.forEach((student) => {
      const key = `${student.class}__${student.section || ""}`;
      if (!map.has(key)) map.set(key, { className: student.class, section: student.section || "" });
    });
    return Array.from(map.values()).sort((a, b) => `${a.className}-${a.section}`.localeCompare(`${b.className}-${b.section}`));
  }, [students]);

  const promotionClassOptions = useMemo(() => {
    const set = new Set();
    students.forEach((student) => {
      if (String(student.class || "").trim()) set.add(String(student.class).trim());
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }));
  }, [students]);

  const promotionSectionOptions = useMemo(() => {
    const set = new Set();
    students
      .filter((student) => !promotionFilter.fromClass || String(student.class) === String(promotionFilter.fromClass))
      .forEach((student) => {
        if (String(student.section || "").trim()) set.add(String(student.section).trim());
      });
    return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  }, [students, promotionFilter.fromClass]);

  const promotionYearOptions = useMemo(() => {
    const set = new Set();
    students.forEach((student) => {
      if (String(student.academicYear || "").trim()) set.add(String(student.academicYear).trim());
    });
    const rows = Array.from(set).sort((a, b) => b.localeCompare(a, undefined, { numeric: true, sensitivity: "base" }));
    if (!rows.includes(getDefaultAcademicYearText())) rows.push(getDefaultAcademicYearText());
    return rows;
  }, [students]);

  const hallTicketExamOptions = useMemo(() => exams || [], [exams]);

  const hallTicketStudentOptions = useMemo(() => {
    if (!hallTicketForm.examId) return [];
    const rows = hallTicketRows.filter((row) => String(row.exam?.id) === String(hallTicketForm.examId));
    return rows.map((row) => row.student).filter(Boolean);
  }, [hallTicketRows, hallTicketForm.examId]);

  useEffect(() => {
    if (feeMonthOptions.length === 0) return;
    const currentMonthKey = formatLocalMonthInput(new Date());
    const currentAcademicYear = getAcademicYearFromMonthKey(currentMonthKey);
    if (!feeAcademicYear && feeAcademicYearOptions.length > 0) {
      setFeeAcademicYear(feeAcademicYearOptions.includes(currentAcademicYear) ? currentAcademicYear : feeAcademicYearOptions[0]);
      return;
    }
    const availableMonths = filteredFeeMonthOptions.length > 0 ? filteredFeeMonthOptions : feeMonthOptions;
    if (availableMonths.some((item) => item.monthKey === feeMonthKey)) return;
    const preferred = availableMonths.find((item) => item.monthKey === currentMonthKey) || availableMonths[0];
    setFeeMonthKey(preferred.monthKey);
  }, [feeMonthOptions, filteredFeeMonthOptions, feeAcademicYear, feeAcademicYearOptions, feeMonthKey]);

  useEffect(() => {
    if (transportMonthOptions.length === 0) return;
    if (transportMonthOptions.some((item) => item.monthKey === transportMonthKey)) return;
    const currentMonthKey = formatLocalMonthInput(new Date());
    const preferred = transportMonthOptions.find((item) => item.monthKey === currentMonthKey) || transportMonthOptions[0];
    setTransportMonthKey(preferred.monthKey);
  }, [transportMonthOptions, transportMonthKey]);

  useEffect(() => {
    if (!paymentStudent) return;
    const preferredMonth = paymentMonthOptions.find((month) => month.status !== "paid")
      || paymentMonthOptions[0];
    if (!preferredMonth) return;

    setOfflinePaymentForm((prev) => {
      if (prev.monthKey && paymentMonthOptions.some((month) => month.monthKey === prev.monthKey)) return prev;
      return {
        ...prev,
        monthKey: preferredMonth.monthKey,
        amount: preferredMonth.amount
      };
    });
  }, [paymentStudent, paymentMonthOptions]);

  const filteredNoticeTeachers = useMemo(() => {
    const keyword = noticeTeacherSearch.trim().toLowerCase();
    if (!keyword) return teachers;
    return teachers.filter((teacher) =>
      [teacher.name, teacher.email, teacher.employeeId].join(" ").toLowerCase().includes(keyword)
    );
  }, [teachers, noticeTeacherSearch]);

  const filteredNoticeStudents = useMemo(() => {
    const keyword = noticeStudentSearch.trim().toLowerCase();
    if (!keyword) return students;
    return students.filter((student) =>
      [student.name, student.class, student.section, student.rollNo].join(" ").toLowerCase().includes(keyword)
    );
  }, [students, noticeStudentSearch]);

  const dueEvents = useMemo(() => {
    const events = [];
    students.forEach((student) => {
      (student.fees?.monthlyFees || []).forEach((month) => {
        if (month.status !== "pending" || !month.dueDate) return;
        const dueDate = new Date(month.dueDate);
        if (Number.isNaN(dueDate.getTime())) return;
        events.push({
          dateKey: formatDateKey(dueDate),
          label: `${student.name} fee due`,
          student: student.name
        });
      });
    });
    return events;
  }, [students]);

  const eventsByDate = useMemo(() => {
    const map = {};
    events.forEach((event) => {
      const key = formatDateKey(new Date(event.eventDate));
      if (!map[key]) map[key] = [];
      map[key].push(event);
    });
    return map;
  }, [events]);

  const selectedDateEvents = useMemo(() => {
    if (!selectedDateKey) return [];
    return eventsByDate[selectedDateKey] || [];
  }, [eventsByDate, selectedDateKey]);

  const noticeItems = useMemo(() => {
    if (notices.length > 0) {
      return notices.slice(0, 4).map((notice) => ({
        title: notice.title,
        value: `${new Date(notice.noticeDate || notice.createdAt).toLocaleDateString()} | ${notice.targetType || "all"}`,
        tone: "info"
      }));
    }

    const pendingCount = dueEvents.length;
    return [
      { title: "Monthly Dues Pending", value: `${pendingCount}`, tone: "warn" },
      { title: "Active Students", value: `${students.filter((s) => s.isActive).length}`, tone: "ok" },
      { title: "Active Teachers", value: `${teachers.filter((t) => t.isActive).length}`, tone: "ok" }
    ];
  }, [dueEvents, students, teachers, notices]);

  const recentActivities = useMemo(() => {
    const paymentActivities = recentPayments.slice(0, 6).map((payment) => ({
      id: payment._id,
      title: `${payment.student?.name || "Student"} paid ${money(payment.amount)}`,
      meta: payment.monthKey || "Monthly fee",
      when: timeAgo(payment.paidAt || payment.createdAt),
      tone: "ok"
    }));

    const fallback = [
      {
        id: "students",
        title: `${students.length} students currently listed`,
        meta: "Student registry",
        when: "Live",
        tone: "info"
      },
      {
        id: "teachers",
        title: `${teachers.length} teachers currently listed`,
        meta: "Teacher registry",
        when: "Live",
        tone: "info"
      }
    ];

    return paymentActivities.length > 0 ? paymentActivities : fallback;
  }, [recentPayments, students.length, teachers.length]);

  const revenueInsights = useMemo(() => {
    const rows = revenue.monthlyBreakdown || [];
    const maxRevenue = rows.reduce((max, row) => Math.max(max, Number(row.total || 0)), 0);
    const maxPayments = rows.reduce((max, row) => Math.max(max, Number(row.payments || 0)), 0);
    const totalPayments = rows.reduce((sum, row) => sum + Number(row.payments || 0), 0);
    const topMonth = rows.reduce(
      (best, row) => (Number(row.total || 0) > Number(best.total || 0) ? row : best),
      rows[0] || { month: "-", total: 0, payments: 0 }
    );
    return { rows, maxRevenue, maxPayments, totalPayments, topMonth };
  }, [revenue]);

  const paymentHistoryStats = useMemo(() => {
    return paymentHistoryTotals.reduce((acc, row) => {
      const key = row._id || "unknown";
      const count = Number(row.count || 0);
      const amount = Number(row.totalAmount || 0);
      acc[key] = { count, amount };
      acc.totalCount += count;
      acc.totalAmount += amount;
      return acc;
    }, { totalCount: 0, totalAmount: 0 });
  }, [paymentHistoryTotals]);

  const startStudentCreate = () => {
    setStudentForm(initialStudentForm);
    setActiveTab("student-form");
  };

  const startSiblingCreate = (student) => {
    const parent = student.parent || {};
    setStudentForm({
      ...initialStudentForm,
      parentName: parent.name || "",
      parentEmail: parent.email || "",
      parentPhone: parent.phone || "",
      parentAddress: parent.address || "",
      parentPassword: parent.loginPassword || "",
      academicYear: student.academicYear || "",
      class: student.class || "",
      section: student.section || "",
      monthlyFee: student.fees?.monthlyFees?.[0]?.amount || "",
      annualFee: student.fees?.annualFee?.amount || "",
      admissionFee: student.fees?.admissionFee?.amount || "",
      transportEnabled: !!student.transport?.enabled,
      transportMonthlyFee: student.transport?.monthlyAmount || "",
      transportRouteName: student.transport?.routeName || "",
      transportPickupPoint: student.transport?.pickupPoint || "",
      transportDropPoint: student.transport?.dropPoint || "",
      transportVehicleNo: student.transport?.vehicleNo || ""
    });
    setActiveTab("student-form");
  };

  const startStudentEdit = (student) => {
    const parent = student.parent || {};
    setStudentForm({
      id: student.id,
      name: student.name || "",
      photoUrl: student.photoUrl || "",
      class: student.class || "",
      section: student.section || "",
      rollNo: student.rollNo || "",
      admissionNo: student.admissionNo || "",
      academicYear: student.academicYear || "",
      dateOfBirth: toDateInputValue(student.dateOfBirth),
      gender: student.gender || "",
      bloodGroup: student.bloodGroup || "",
      address: student.address || "",
      city: student.city || "",
      state: student.state || "",
      country: student.country || "",
      pincode: student.pincode || "",
      emergencyContactName: student.emergencyContactName || "",
      emergencyContactPhone: student.emergencyContactPhone || "",
      notes: student.notes || "",
      monthlyFee: student.fees?.monthlyFees?.find((month) => month.status !== "paid")?.amount || student.fees?.monthlyFees?.[0]?.amount || "",
      annualFee: student.fees?.annualFee?.amount || "",
      admissionFee: student.fees?.admissionFee?.amount || "",
      parentName: parent.name || "",
      parentEmail: parent.email || "",
      parentPhone: parent.phone || "",
      parentAddress: parent.address || "",
      parentPassword: parent.loginPassword || "",
      regenerateSchedule: false,
      transportEnabled: !!student.transport?.enabled,
      transportMonthlyFee: student.transport?.monthlyAmount || "",
      transportRouteName: student.transport?.routeName || "",
      transportPickupPoint: student.transport?.pickupPoint || "",
      transportDropPoint: student.transport?.dropPoint || "",
      transportVehicleNo: student.transport?.vehicleNo || ""
    });
    setActiveTab("student-form");
  };

  const requestActionConfirm = ({ title, message, actionType, targetId }) => {
    setActionConfirm({
      open: true,
      title,
      message,
      actionType,
      targetId
    });
  };

  const runAdminRequestWithTokenRetry = async (requestFactory) => {
    const primary = localStorage.getItem("adminToken") || localStorage.getItem("token");
    const secondary = localStorage.getItem("token") || localStorage.getItem("adminToken");
    const tokens = [...new Set([primary, secondary].filter(Boolean))];
    if (tokens.length === 0) throw new Error("No admin token found");

    let lastError = null;
    for (const token of tokens) {
      try {
        const result = await requestFactory(token);
        if (localStorage.getItem("token") !== token) localStorage.setItem("token", token);
        if (localStorage.getItem("adminToken") !== token) localStorage.setItem("adminToken", token);
        return result;
      } catch (err) {
        if (![401, 403].includes(err.response?.status)) throw err;
        lastError = err;
      }
    }
    throw lastError || new Error("Admin request failed");
  };

  const getProofFeeTypeForTab = (tabKey) => {
    if (tabKey === "transport-upi-proofs") return "transport";
    if (tabKey === "annual-upi-proofs") return "annual";
    if (tabKey === "admission-upi-proofs") return "admission";
    return "tuition";
  };

  const runConfirmedAction = async () => {
    setProcessingActionConfirm(true);
    setError("");
    try {
      if (actionConfirm.actionType === "delete-student") {
        await runAdminRequestWithTokenRetry((token) =>
          api.delete(`/admin/students/${actionConfirm.targetId}`, { headers: { Authorization: `Bearer ${token}` } })
        );
        await Promise.all([fetchStudents(), fetchOverview(), fetchRevenue()]);
      } else if (actionConfirm.actionType === "delete-teacher") {
        await runAdminRequestWithTokenRetry((token) =>
          api.delete(`/admin/teachers/${actionConfirm.targetId}`, { headers: { Authorization: `Bearer ${token}` } })
        );
        await Promise.all([fetchTeachers(), fetchOverview()]);
      } else if (actionConfirm.actionType === "delete-event") {
        await runAdminRequestWithTokenRetry((token) =>
          api.delete(`/admin/events/${actionConfirm.targetId}`, { headers: { Authorization: `Bearer ${token}` } })
        );
        await fetchEvents();
      } else if (actionConfirm.actionType === "delete-notice") {
        await runAdminRequestWithTokenRetry((token) =>
          api.delete(`/admin/notices/${actionConfirm.targetId}`, { headers: { Authorization: `Bearer ${token}` } })
        );
        await fetchNotices();
      } else if (actionConfirm.actionType === "delete-inventory") {
        await runAdminRequestWithTokenRetry((token) =>
          api.delete(`/inventory/items/${actionConfirm.targetId}`, { headers: { Authorization: `Bearer ${token}` } })
        );
        await Promise.all([fetchInventorySummary(), fetchInventoryItems()]);
      } else if (actionConfirm.actionType === "approve-proof") {
        await api.post(`/fees/proofs/${actionConfirm.targetId}/decide`, { action: "approve" });
        await Promise.all([fetchProofs(proofStatus, getProofFeeTypeForTab(activeTab)), fetchPendingProofCounts()]);
        await Promise.all([fetchStudents(), fetchOverview(), fetchRevenue()]);
      } else if (actionConfirm.actionType === "reject-proof") {
        await api.post(`/fees/proofs/${actionConfirm.targetId}/decide`, { action: "reject", remarks: actionConfirm.message });
        await Promise.all([fetchProofs(proofStatus, getProofFeeTypeForTab(activeTab)), fetchPendingProofCounts()]);
      }
      setSuccess("Action completed successfully.");
      setActionConfirm({ open: false, title: "", message: "", actionType: "", targetId: "" });
    } catch (err) {
      if ([401, 403].includes(err.response?.status)) {
        setError("Session issue detected. Please re-login if this continues.");
      } else {
        setError(getReadableAdminError(err, "Action failed"));
      }
    } finally {
      setProcessingActionConfirm(false);
    }
  };

  const deleteStudent = async (studentId) => {
    requestActionConfirm({
      title: "Delete Student",
      message: "This will delete the student and linked payments. Continue?",
      actionType: "delete-student",
      targetId: studentId
    });
  };

  const changeAdminPassword = async (e) => {
    e.preventDefault();
    setError("");
    const currentPassword = String(adminPasswordForm.currentPassword || "");
    const newPassword = String(adminPasswordForm.newPassword || "");
    const confirmPassword = String(adminPasswordForm.confirmPassword || "");

    if (!currentPassword || !newPassword || !confirmPassword) {
      setError("Please fill current password, new password, and confirm password.");
      return;
    }
    if (newPassword.length < 6) {
      setError("New password must be at least 6 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("New password and confirm password do not match.");
      return;
    }

    setChangingAdminPassword(true);
    try {
      await runAdminRequestWithTokenRetry((token) =>
        api.put("/admin/profile/password", { currentPassword, newPassword }, { headers: { Authorization: `Bearer ${token}` } })
      );
      setAdminPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
      await fetchAdminProfile();
      setShowAdminPassword(false);
      setSuccess("Admin password changed successfully.");
    } catch (err) {
      setError(getReadableAdminError(err, "Failed to change password"));
    } finally {
      setChangingAdminPassword(false);
    }
  };

  const startInventoryCreate = () => {
    setInventoryForm(initialInventoryForm);
  };

  const startInventoryEdit = (item) => {
    setInventoryForm({
      id: item.id,
      name: item.name || "",
      sku: item.sku || "",
      category: item.category || "",
      subCategory: item.subCategory || "",
      unit: item.unit || "pcs",
      quantity: item.quantity ?? "",
      minStock: item.minStock ?? "",
      reorderLevel: item.reorderLevel ?? "",
      unitCost: item.unitCost ?? "",
      supplierName: item.supplierName || "",
      supplierPhone: item.supplierPhone || "",
      location: item.location || "",
      notes: item.notes || ""
    });
  };

  const submitInventoryForm = async (e) => {
    e.preventDefault();
    setSavingInventory(true);
    setError("");
    try {
      const payload = {
        name: inventoryForm.name,
        sku: inventoryForm.sku,
        category: inventoryForm.category,
        subCategory: inventoryForm.subCategory,
        unit: inventoryForm.unit,
        quantity: Number(inventoryForm.quantity || 0),
        minStock: Number(inventoryForm.minStock || 0),
        reorderLevel: Number(inventoryForm.reorderLevel || 0),
        unitCost: Number(inventoryForm.unitCost || 0),
        supplierName: inventoryForm.supplierName,
        supplierPhone: inventoryForm.supplierPhone,
        location: inventoryForm.location,
        notes: inventoryForm.notes
      };
      if (inventoryForm.id) {
        await api.put(`/inventory/items/${inventoryForm.id}`, payload);
        setSuccess("Inventory item updated.");
      } else {
        await api.post("/inventory/items", payload);
        setSuccess("Inventory item created.");
      }
      await Promise.all([fetchInventorySummary(), fetchInventoryItems()]);
      setInventoryForm(initialInventoryForm);
    } catch (err) {
      setError(getReadableAdminError(err, "Failed to save inventory item"));
    } finally {
      setSavingInventory(false);
    }
  };

  const requestDeleteInventory = (itemId) => {
    requestActionConfirm({
      title: "Delete Inventory Item",
      message: "This will archive the item from active inventory. Continue?",
      actionType: "delete-inventory",
      targetId: itemId
    });
  };

  const startStockAdjust = (item) => {
    setSelectedInventoryItem(item);
    setInventoryAdjustForm({
      itemId: item.id,
      type: "in",
      quantity: "",
      reason: "",
      referenceNo: "",
      unitCost: item.unitCost || ""
    });
    fetchInventoryMovements(item.id).catch(() => {});
  };

  const submitStockAdjust = async (e) => {
    e.preventDefault();
    if (!inventoryAdjustForm.itemId) {
      setError("Select an item first");
      return;
    }
    setSavingInventoryAdjust(true);
    setError("");
    try {
      await api.post(`/inventory/items/${inventoryAdjustForm.itemId}/adjust`, {
        type: inventoryAdjustForm.type,
        quantity: Number(inventoryAdjustForm.quantity || 0),
        reason: inventoryAdjustForm.reason,
        referenceNo: inventoryAdjustForm.referenceNo,
        unitCost: Number(inventoryAdjustForm.unitCost || 0)
      });
      await Promise.all([
        fetchInventorySummary(),
        fetchInventoryItems(),
        fetchInventoryMovements(inventoryAdjustForm.itemId)
      ]);
      setInventoryAdjustForm((prev) => ({ ...prev, quantity: "", reason: "", referenceNo: "" }));
      setSuccess("Stock updated successfully.");
    } catch (err) {
      setError(getReadableAdminError(err, "Failed to adjust stock"));
    } finally {
      setSavingInventoryAdjust(false);
    }
  };

  const startOfflinePaymentForStudent = (student, feeType = "tuition") => {
    const months = feeType === "transport"
      ? (student.transport?.monthlyFees || [])
      : ["annual", "admission"].includes(feeType)
        ? [getOneTimeFeeEntry(student, feeType)].filter(Boolean)
        : (student.fees?.monthlyFees || []);
    const selectedMonthKey = feeType === "transport"
      ? transportMonthKey
      : ["annual", "admission"].includes(feeType)
        ? (getOneTimeFeeEntry(student, feeType)?.monthKey || "")
        : feeMonthKey;
    const preferredMonth = months.find((month) => month.monthKey === selectedMonthKey)
      || months.find((month) => month.status !== "paid")
      || months[0];
    setOfflinePaymentForm({
      ...initialOfflinePaymentForm,
      studentId: student.id,
      feeType,
      monthKey: preferredMonth?.monthKey || "",
      amount: preferredMonth?.amount || 0
    });
    setActiveTab("create-payment");
  };

  const requestQuickMarkFeeStatus = (student, status, feeType = "tuition") => {
    const entry = feeType === "transport"
      ? (student.transport?.monthlyFees || []).find((item) => item.monthKey === transportMonthKey)
      : ["annual", "admission"].includes(feeType)
        ? getOneTimeFeeEntry(student, feeType)
        : (student.fees?.monthlyFees || []).find((item) => item.monthKey === feeMonthKey);
    if (!entry) {
      setError(`${getPaymentPeriodLabel(feeType)} is not available for this student.`);
      return;
    }
    if (status === "paid" && entry.status === "paid") {
      setError(`Selected ${getPaymentPeriodLabel(feeType).toLowerCase()} is already marked as paid.`);
      return;
    }
    if (status === "pending" && entry.status !== "paid") {
      setError(`Selected ${getPaymentPeriodLabel(feeType).toLowerCase()} is already pending.`);
      return;
    }
    setQuickMarkConfirm({ open: true, student, entry, feeType, status });
  };

  const quickMarkFeeStatus = async () => {
    const { student, entry, feeType, status } = quickMarkConfirm;
    if (!student || !entry) return;
    setProcessingQuickMark(true);
    try {
      await api.post("/admin/fees/offline-payment", {
        studentId: student.id,
        feeType,
        monthKey: entry.monthKey,
        amount: entry.amount,
        paymentMethod: "Offline",
        status,
        paymentDate: getTodayInput(),
        remarks: "Quick update from fee collection table"
      });
      await Promise.all([fetchStudents(), fetchOverview(), fetchRevenue()]);
      setSuccess("Fee status updated successfully.");
      setQuickMarkConfirm({ open: false, student: null, entry: null, feeType: "tuition", status: "paid" });
    } catch (err) {
      setError(getReadableAdminError(err, "Failed to update fee status"));
    } finally {
      setProcessingQuickMark(false);
    }
  };

  const openPaymentDetails = async (student, feeType = "tuition") => {
    const entry = feeType === "transport"
      ? (student.transport?.monthlyFees || []).find((item) => item.monthKey === transportMonthKey)
      : ["annual", "admission"].includes(feeType)
        ? getOneTimeFeeEntry(student, feeType)
        : (student.fees?.monthlyFees || []).find((item) => item.monthKey === feeMonthKey);
    if (!entry) {
      setError(`${getPaymentPeriodLabel(feeType)} is not available for this student.`);
      return;
    }

    setPaymentDetailsModal({
      open: true,
      loading: true,
      error: "",
      student,
      entry,
      feeType,
      transaction: null
    });

    try {
      const { data } = await api.get(`/fees/transactions?studentId=${student.id}`);
      const transactions = data.transactions || [];
      const matchingTransactions = transactions.filter((tx) => {
        if (["annual", "admission"].includes(feeType)) {
          return String(tx.feeType || "") === String(feeType)
            && String(tx.academicYear || "") === String(entry.monthKey || student.academicYear || "");
        }
        return String(tx.feeType || "tuition") === String(feeType)
          && String(tx.monthKey || "") === String(entry.monthKey || "");
      });
      const transaction = matchingTransactions.find((tx) => tx.status === "paid") || matchingTransactions[0] || null;

      setPaymentDetailsModal((prev) => ({
        ...prev,
        loading: false,
        transaction
      }));
    } catch (err) {
      setPaymentDetailsModal((prev) => ({
        ...prev,
        loading: false,
        error: getReadableAdminError(err, "Failed to load payment details")
      }));
    }
  };

  const submitOfflinePayment = async (e) => {
    e.preventDefault();
    setSavingOfflinePayment(true);
    setError("");

    try {
      await api.post("/admin/fees/offline-payment", {
        studentId: offlinePaymentForm.studentId,
        feeType: offlinePaymentForm.feeType,
        monthKey: offlinePaymentForm.monthKey,
        amount: offlinePaymentForm.amount ? Number(offlinePaymentForm.amount) : undefined,
        paymentMethod: offlinePaymentForm.paymentMethod,
        status: offlinePaymentForm.status,
        paymentDate: offlinePaymentForm.paymentDate ? `${offlinePaymentForm.paymentDate}T00:00:00.000Z` : undefined,
        remarks: offlinePaymentForm.remarks
      });

      const nextTab = offlinePaymentForm.feeType === "transport"
        ? "transport-collection"
        : ["annual", "admission"].includes(offlinePaymentForm.feeType)
          ? `${offlinePaymentForm.feeType}-collection`
          : "fee-collection";
      setOfflinePaymentForm(initialOfflinePaymentForm);
      setActiveTab(nextTab);
      await Promise.all([fetchStudents(), fetchOverview(), fetchRevenue()]);
      setSuccess("Payment saved successfully.");
    } catch (err) {
      setError(getReadableAdminError(err, "Failed to save student payment"));
    } finally {
      setSavingOfflinePayment(false);
    }
  };

  const submitStudent = async (e) => {
    e.preventDefault();
    setSavingStudent(true);
    setError("");

    const payload = {
      name: studentForm.name,
      photoUrl: studentForm.photoUrl,
      class: studentForm.class,
      section: studentForm.section,
      rollNo: studentForm.rollNo ? Number(studentForm.rollNo) : undefined,
      admissionNo: studentForm.admissionNo,
      academicYear: studentForm.academicYear,
      dateOfBirth: studentForm.dateOfBirth || undefined,
      gender: studentForm.gender,
      bloodGroup: studentForm.bloodGroup,
      address: studentForm.address,
      city: studentForm.city,
      state: studentForm.state,
      country: studentForm.country,
      pincode: studentForm.pincode,
      emergencyContactName: studentForm.emergencyContactName,
      emergencyContactPhone: studentForm.emergencyContactPhone,
      notes: studentForm.notes,
      monthlyFee: studentForm.monthlyFee ? Number(studentForm.monthlyFee) : undefined,
      annualFee: studentForm.annualFee ? Number(studentForm.annualFee) : 0,
      admissionFee: studentForm.admissionFee ? Number(studentForm.admissionFee) : 0,
      transportEnabled: !!studentForm.transportEnabled,
      transportMonthlyFee: studentForm.transportMonthlyFee ? Number(studentForm.transportMonthlyFee) : 0,
      transportRouteName: studentForm.transportRouteName,
      transportPickupPoint: studentForm.transportPickupPoint,
      transportDropPoint: studentForm.transportDropPoint,
      transportVehicleNo: studentForm.transportVehicleNo,
      parentName: studentForm.parentName,
      parentEmail: studentForm.parentEmail,
      parentPhone: studentForm.parentPhone,
      parentAddress: studentForm.parentAddress,
      parentPassword: studentForm.parentPassword,
      regenerateSchedule: studentForm.regenerateSchedule
    };

    Object.keys(payload).forEach((key) => payload[key] === undefined && delete payload[key]);

    try {
      if (studentForm.id) {
        await api.put(`/admin/students/${studentForm.id}`, payload);
      } else {
        await api.post("/admin/students", payload);
      }

      setStudentForm(initialStudentForm);
      setActiveTab("students");
      await Promise.all([fetchStudents(), fetchOverview(), fetchRevenue()]);
      setSuccess(studentForm.id ? "Student updated successfully." : "Student created successfully.");
    } catch (err) {
      if (err.response?.status === 413) {
        setError("Image is too large for upload. Please use a smaller image.");
      } else {
        setError(getReadableAdminError(err, "Failed to save student"));
      }
    } finally {
      setSavingStudent(false);
    }
  };

  const handleStudentPhotoFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Please select an image file.");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setError("Image size should be less than 2MB.");
      return;
    }
    try {
      const dataUrl = await fileToDataUrl(file);
      setStudentForm((prev) => ({ ...prev, photoUrl: dataUrl }));
      setSuccess("Student image uploaded.");
    } catch {
      setError("Failed to upload student image.");
    } finally {
      e.target.value = "";
    }
  };

  const startTeacherCreate = () => {
    setTeacherForm(initialTeacherForm);
    setActiveTab("teacher-form");
  };

  const startTeacherEdit = (teacher) => {
    const classTeacherParts = String(teacher.classTeacherOf || "").split("-");
    setTeacherForm({
      id: teacher.id,
      name: teacher.name || "",
      photoUrl: teacher.photoUrl || "",
      employeeId: teacher.employeeId || "",
      email: teacher.email || "",
      phone: teacher.phone || "",
      gender: teacher.gender || "",
      dateOfBirth: toDateInputValue(teacher.dateOfBirth),
      qualification: teacher.qualification || "",
      experienceYears: teacher.experienceYears || "",
      address: teacher.address || "",
      city: teacher.city || "",
      state: teacher.state || "",
      country: teacher.country || "",
      pincode: teacher.pincode || "",
      subjects: (teacher.subjects || []).join(", "),
      classTeacherClass: classTeacherParts[0] || "",
      classTeacherSection: classTeacherParts[1] || "",
      classTeacherOf: teacher.classTeacherOf || "",
      notes: teacher.notes || "",
      monthlySalary: teacher.monthlySalary || "",
      password: teacher.loginPassword || "",
      isActive: teacher.isActive !== false
    });
    setActiveTab("teacher-form");
  };

  const submitTeacher = async (e) => {
    e.preventDefault();
    setSavingTeacher(true);
    setError("");

    const classTeacherOf = [
      teacherForm.classTeacherClass,
      teacherForm.classTeacherSection
    ].filter(Boolean).join("-");

    const payload = {
      name: teacherForm.name,
      photoUrl: teacherForm.photoUrl,
      employeeId: teacherForm.employeeId,
      email: teacherForm.email,
      phone: teacherForm.phone,
      gender: teacherForm.gender,
      dateOfBirth: teacherForm.dateOfBirth || undefined,
      qualification: teacherForm.qualification,
      experienceYears: teacherForm.experienceYears ? Number(teacherForm.experienceYears) : 0,
      address: teacherForm.address,
      city: teacherForm.city,
      state: teacherForm.state,
      country: teacherForm.country,
      pincode: teacherForm.pincode,
      subjects: teacherForm.subjects,
      classTeacherOf,
      notes: teacherForm.notes,
      monthlySalary: teacherForm.monthlySalary ? Number(teacherForm.monthlySalary) : 0,
      password: teacherForm.password || undefined,
      isActive: teacherForm.isActive
    };

    Object.keys(payload).forEach((key) => payload[key] === undefined && delete payload[key]);

    try {
      if (teacherForm.id) {
        await api.put(`/admin/teachers/${teacherForm.id}`, payload);
      } else {
        await api.post("/admin/teachers", payload);
      }

      setTeacherForm(initialTeacherForm);
      setActiveTab("teachers");
      await Promise.all([fetchTeachers(), fetchOverview()]);
      setSuccess(teacherForm.id ? "Teacher updated successfully." : "Teacher created successfully.");
    } catch (err) {
      if (err.response?.status === 413) {
        setError("Image is too large for upload. Please use a smaller image.");
      } else {
        setError(getReadableAdminError(err, "Failed to save teacher"));
      }
    } finally {
      setSavingTeacher(false);
    }
  };

  const handleTeacherPhotoFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Please select an image file.");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setError("Image size should be less than 2MB.");
      return;
    }
    try {
      const dataUrl = await fileToDataUrl(file);
      setTeacherForm((prev) => ({ ...prev, photoUrl: dataUrl }));
      setSuccess("Teacher image uploaded.");
    } catch {
      setError("Failed to upload teacher image.");
    } finally {
      e.target.value = "";
    }
  };

  const deleteTeacher = async (teacherId) => {
    requestActionConfirm({
      title: "Delete Teacher",
      message: "This will delete the teacher record. Continue?",
      actionType: "delete-teacher",
      targetId: teacherId
    });
  };

  const markTeacherAttendance = async (teacher, status) => {
    if (!teacher?.id || !attendanceDate) return;
    if (isAttendanceDateSunday) {
      setError("Attendance is not marked on Sundays.");
      return;
    }
    setMarkingAttendanceId(`${teacher.id}:${status}`);
    setError("");
    try {
      await api.post("/admin/teacher-attendance/mark", {
        teacherId: teacher.id,
        dateKey: attendanceDate,
        status,
        remark: ""
      });
      await fetchTeacherAttendance();
      setSuccess(`Attendance updated for ${teacher.name}.`);
    } catch (err) {
      setError(getReadableAdminError(err, "Failed to update attendance"));
    } finally {
      setMarkingAttendanceId("");
    }
  };

  const markStudentAttendance = async (studentId, status) => {
    setMarkingStudentAttendanceId(String(studentId));
    setError("");
    try {
      await api.post("/admin/attendance/students/mark", {
        dateKey: studentAttendanceDate,
        records: [{ studentId, status }]
      });
      setStudentAttendanceRows((prev) => prev.map((row) => (String(row.id) === String(studentId) ? { ...row, attendanceStatus: status } : row)));
      await fetchStudentAttendance();
      if (studentAttendanceMonth === studentAttendanceDate.slice(0, 7)) {
        fetchStudentAttendanceMonthly().catch(() => {});
      }
      setSuccess("Attendance saved automatically.");
    } catch (err) {
      setError(getReadableAdminError(err, "Failed to mark attendance"));
    } finally {
      setMarkingStudentAttendanceId("");
    }
  };

  const submitExam = async (e) => {
    e.preventDefault();
    setSavingExam(true);
    setError("");
    try {
      const payload = {
        title: examForm.title,
        className: examForm.className,
        section: examForm.section,
        subject: examForm.subject,
        maxMarks: Number(examForm.maxMarks || 100),
        examDate: examForm.examDate ? `${examForm.examDate}T00:00:00.000Z` : undefined,
        academicYear: examForm.academicYear
      };
      await api.post("/admin/exams", payload);
      setExamForm(initialExamForm);
      await fetchExams();
      setSuccess("Exam created successfully.");
    } catch (err) {
      setError(getReadableAdminError(err, "Failed to create exam"));
    } finally {
      setSavingExam(false);
    }
  };

  const saveExamMarks = async () => {
    if (!selectedExamId) return;
    setSavingExamMarks(true);
    setError("");
    try {
      await api.post(`/admin/exams/${selectedExamId}/marks`, {
        records: examMarksRows.map((row) => ({
          studentId: row.studentId,
          marks: row.marks === "" ? 0 : Number(row.marks || 0),
          remarks: row.remarks || ""
        }))
      });
      await fetchExamMarks(selectedExamId);
      setSuccess("Exam marks saved successfully.");
    } catch (err) {
      setError(getReadableAdminError(err, "Failed to save marks"));
    } finally {
      setSavingExamMarks(false);
    }
  };

  const submitTimetableEntry = async (e) => {
    e.preventDefault();
    setSavingTimetable(true);
    setError("");
    try {
      await api.post("/admin/timetable", {
        ...timetableForm,
        dayOfWeek: Number(timetableForm.dayOfWeek || 1),
        period: Number(timetableForm.period || 1)
      });
      setTimetableForm(initialTimetableForm);
      await fetchTimetable();
      setSuccess("Timetable entry saved.");
    } catch (err) {
      setError(getReadableAdminError(err, "Failed to save timetable entry"));
    } finally {
      setSavingTimetable(false);
    }
  };

  const deleteTimetableEntry = async (id) => {
    try {
      await api.delete(`/admin/timetable/${id}`);
      await fetchTimetable();
      setSuccess("Timetable entry deleted.");
    } catch (err) {
      setError(getReadableAdminError(err, "Failed to delete timetable entry"));
    }
  };

  const sendDefaulterReminder = async (row) => {
    setSendingReminderId(String(row.studentId));
    setError("");
    try {
      const { data } = await api.post("/admin/defaulters/remind", {
        studentId: row.studentId,
        monthKey: row.monthKey,
        channel: "sms",
        feeType: "tuition",
        message: `Fee reminder for ${row.monthKey}. Pending amount: ${money(row.amount)}`
      });
      setSuccess(data?.message || "Reminder SMS processed.");
    } catch (err) {
      setError(getReadableAdminError(err, "Failed to send reminder"));
    } finally {
      setSendingReminderId("");
    }
  };

  const sendTransportDefaulterReminder = async (row) => {
    setSendingReminderId(`transport-${String(row.studentId)}`);
    setError("");
    try {
      const { data } = await api.post("/admin/defaulters/remind", {
        studentId: row.studentId,
        monthKey: row.monthKey,
        channel: "sms",
        feeType: "transport",
        message: `Transport fee reminder for ${row.monthKey}. Pending amount: ${money(row.amount)}`
      });
      setSuccess(data?.message || "Transport reminder SMS processed.");
    } catch (err) {
      setError(getReadableAdminError(err, "Failed to send reminder"));
    } finally {
      setSendingReminderId("");
    }
  };

  const reviewLeaveRequest = async (requestId, status) => {
    setReviewingLeaveId(String(requestId));
    setError("");
    try {
      await api.post(`/admin/leave-requests/${requestId}/review`, { status });
      setLeaveStatusFilter("");
      await fetchLeaveRequests();
      setSuccess(`Leave request ${status}.`);
    } catch (err) {
      setError(getReadableAdminError(err, "Failed to review leave request"));
    } finally {
      setReviewingLeaveId("");
    }
  };

  const saveHallTicket = async (e) => {
    e.preventDefault();
    setSavingHallTicket(true);
    setError("");
    try {
      await api.post("/admin/hall-tickets", hallTicketForm);
      setHallTicketForm(initialHallTicketForm);
      await fetchHallTickets();
      setSuccess("Hall ticket saved successfully.");
    } catch (err) {
      setError(getReadableAdminError(err, "Failed to save hall ticket"));
    } finally {
      setSavingHallTicket(false);
    }
  };

  const openTcPreview = async (certificateId) => {
    setError("");
    try {
      const { data } = await api.get(`/admin/tc/${certificateId}`);
      setTcPreview({ open: true, certificate: data.certificate || null });
    } catch (err) {
      setError(getReadableAdminError(err, "Failed to load transfer certificate"));
    }
  };

  const printTransferCertificate = (certificate) => {
    if (!certificate) return;
    const student = certificate.student || {};
    const issueDate = certificate.issueDate ? new Date(certificate.issueDate).toLocaleDateString("en-IN") : "-";
    const dob = student.dateOfBirth ? new Date(student.dateOfBirth).toLocaleDateString("en-IN") : "-";
    const lastAttendance = certificate.lastAttendanceDate ? new Date(certificate.lastAttendanceDate).toLocaleDateString("en-IN") : "-";
    const fullAddress = [student.address, student.city, student.state, student.country, student.pincode].filter(Boolean).join(", ") || "-";
    const win = window.open("", "_blank", "width=980,height=760");
    if (!win) {
      setError("Unable to open print window. Please allow popups for this site.");
      return;
    }
    const schoolName = "MPIPS School Management System";
    const schoolAddress = "Dharamshala";
    const html = `
      <html>
        <head>
          <title>Transfer Certificate - ${certificate.tcNo || ""}</title>
          <style>
            body { font-family: "Segoe UI", Arial, sans-serif; margin: 24px; color: #1f2937; }
            .sheet { max-width: 820px; margin: 0 auto; border: 1px solid #cfd8e3; border-radius: 12px; padding: 24px; }
            .head { text-align: center; border-bottom: 1px solid #e5e7eb; padding-bottom: 14px; margin-bottom: 14px; }
            .head h1 { margin: 0; font-size: 24px; font-weight: 600; }
            .head p { margin: 6px 0 0; color: #6b7280; }
            .title { text-align: center; margin: 18px 0; font-size: 18px; font-weight: 600; letter-spacing: 0.8px; }
            .meta { display: flex; justify-content: space-between; gap: 18px; margin-bottom: 12px; }
            .meta div { font-size: 14px; }
            .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px 24px; margin-top: 8px; }
            .row { font-size: 14px; }
            .label { color: #6b7280; display: block; margin-bottom: 2px; }
            .value { font-weight: 500; }
            .remarks { margin-top: 14px; padding-top: 10px; border-top: 1px dashed #d1d5db; font-size: 14px; }
            .footer { margin-top: 40px; display: flex; justify-content: space-between; font-size: 14px; }
            .sign { text-align: center; min-width: 220px; }
            .line { border-top: 1px solid #374151; margin-top: 36px; padding-top: 4px; }
            @media print { body { margin: 0; } .sheet { border: 0; border-radius: 0; padding: 18px; } }
          </style>
        </head>
        <body>
          <div class="sheet">
            <div class="head">
              <h1>${schoolName}</h1>
              <p>${schoolAddress}</p>
            </div>
            <div class="title">TRANSFER CERTIFICATE</div>
            <div class="meta">
              <div><strong>TC No:</strong> ${certificate.tcNo || "-"}</div>
              <div><strong>Issue Date:</strong> ${issueDate}</div>
            </div>
            <div class="grid">
              <div class="row"><span class="label">Student Name</span><span class="value">${student.name || "-"}</span></div>
              <div class="row"><span class="label">Admission No</span><span class="value">${student.admissionNo || "-"}</span></div>
              <div class="row"><span class="label">Class / Section</span><span class="value">${student.class || "-"}-${student.section || "NA"}</span></div>
              <div class="row"><span class="label">Roll No</span><span class="value">${student.rollNo || "-"}</span></div>
              <div class="row"><span class="label">Academic Year</span><span class="value">${student.academicYear || "-"}</span></div>
              <div class="row"><span class="label">Date Of Birth</span><span class="value">${dob}</span></div>
              <div class="row"><span class="label">Parent Name</span><span class="value">${student.parentName || "-"}</span></div>
              <div class="row"><span class="label">Parent Phone</span><span class="value">${student.parentPhone || "-"}</span></div>
              <div class="row"><span class="label">Last Attendance Date</span><span class="value">${lastAttendance}</span></div>
              <div class="row"><span class="label">Conduct</span><span class="value">${certificate.conduct || "-"}</span></div>
              <div class="row" style="grid-column: 1 / -1;"><span class="label">Address</span><span class="value">${fullAddress}</span></div>
              <div class="row" style="grid-column: 1 / -1;"><span class="label">Reason</span><span class="value">${certificate.reason || "-"}</span></div>
              <div class="row" style="grid-column: 1 / -1;"><span class="label">Destination School</span><span class="value">${certificate.destinationSchool || "-"}</span></div>
            </div>
            <div class="remarks"><strong>Remarks:</strong> ${certificate.remarks || "-"}</div>
            <div class="footer">
              <div class="sign"><div class="line">Class Teacher</div></div>
              <div class="sign"><div class="line">Principal / Admin</div></div>
            </div>
          </div>
        </body>
      </html>
    `;
    win.document.open();
    win.document.write(html);
    win.document.close();
    win.focus();
    win.print();
  };

  const submitTransferCertificate = async (e) => {
    e.preventDefault();
    if (!tcForm.studentId) {
      setError("Please select a student.");
      return;
    }
    setSavingTc(true);
    setError("");
    try {
      const payload = {
        studentId: tcForm.studentId,
        issueDate: tcForm.issueDate,
        lastAttendanceDate: tcForm.lastAttendanceDate || undefined,
        reason: tcForm.reason,
        destinationSchool: tcForm.destinationSchool,
        conduct: tcForm.conduct,
        remarks: tcForm.remarks,
        deactivateStudent: !!tcForm.deactivateStudent
      };
      const { data } = await api.post("/admin/tc/generate", payload);
      setTcForm({ ...initialTcForm, issueDate: getTodayInput() });
      setSuccess(data?.message || "Transfer certificate generated.");
      await Promise.all([fetchTransferCertificates(), fetchStudents(), fetchOverview()]);
      if (data?.certificate) setTcPreview({ open: true, certificate: data.certificate });
    } catch (err) {
      setError(getReadableAdminError(err, "Failed to generate transfer certificate"));
    } finally {
      setSavingTc(false);
    }
  };

  const cancelTransferCertificate = async () => {
    if (!tcCancelModal.certificateId) return;
    setProcessingTcCancel(true);
    setError("");
    try {
      await api.put(`/admin/tc/${tcCancelModal.certificateId}/cancel`, {
        cancelReason: tcCancelModal.reason
      });
      setTcCancelModal({ open: false, certificateId: "", tcNo: "", reason: "" });
      await fetchTransferCertificates();
      if (tcPreview.certificate?.id === tcCancelModal.certificateId) {
        setTcPreview((prev) => ({
          ...prev,
          certificate: prev.certificate ? { ...prev.certificate, status: "cancelled", cancelReason: tcCancelModal.reason } : prev.certificate
        }));
      }
      setSuccess("Transfer certificate cancelled.");
    } catch (err) {
      setError(getReadableAdminError(err, "Failed to cancel transfer certificate"));
    } finally {
      setProcessingTcCancel(false);
    }
  };

  const runArchive = async () => {
    const entities = Object.entries(archiveEntities)
      .filter(([, enabled]) => enabled)
      .map(([key]) => key);
    if (entities.length === 0) {
      setError("Select at least one entity to archive.");
      return;
    }
    setRunningArchive(true);
    setError("");
    try {
      const { data } = await api.post("/admin/archive/run", {
        beforeDate: archiveBeforeDate,
        entities
      });
      await fetchArchiveSummary();
      const changed = (data.results || []).reduce((acc, row) => acc + Number(row.archivedCount || 0), 0);
      setSuccess(`Archive completed. ${changed} record(s) archived.`);
    } catch (err) {
      setError(getReadableAdminError(err, "Failed to run archive process"));
    } finally {
      setRunningArchive(false);
    }
  };

  const submitEvent = async (e) => {
    e.preventDefault();
    if (!selectedDateKey) return;
    setSavingEvent(true);
    setError("");
    try {
      const payload = {
        title: eventForm.title,
        description: eventForm.description,
        eventDate: `${selectedDateKey}T00:00:00.000Z`
      };

      if (eventForm.id) {
        await api.put(`/admin/events/${eventForm.id}`, payload);
      } else {
        await api.post("/admin/events", payload);
      }
      setEventForm(initialEventForm);
      await fetchEvents();
      setSuccess(eventForm.id ? "Event updated successfully." : "Event added successfully.");
    } catch (err) {
      setError(getReadableAdminError(err, `Failed to ${eventForm.id ? "update" : "add"} event`));
    } finally {
      setSavingEvent(false);
    }
  };

  const startEditEvent = (event) => {
    setSelectedDateKey(formatDateKey(new Date(event.eventDate)));
    setEventForm({
      id: event.id,
      title: event.title || "",
      description: event.description || ""
    });
  };

  const deleteEvent = async (eventId) => {
    requestActionConfirm({
      title: "Delete Event",
      message: "This will permanently remove the event. Continue?",
      actionType: "delete-event",
      targetId: eventId
    });
  };

  const exportStudentsToExcel = () => {
    if (!studentExportClass) {
      setError("Please select a class before exporting.");
      return;
    }
    if (exportableStudentRows.length === 0) {
      setError("No students found for the selected class.");
      return;
    }

    const headers = [
      "S.No",
      "Student Name",
      "Class",
      "Section",
      "Roll No",
      "Admission No",
      "Academic Year",
      "Date Of Birth",
      "Gender",
      "Blood Group",
      "Parent Name",
      "Parent Email",
      "Parent Phone",
      "Address",
      "City",
      "State",
      "Pincode",
      "Monthly Fee",
      "Annual Fee",
      "Admission Fee",
      "Transport Enabled",
      "Transport Monthly Fee",
      "Route Name",
      "Pickup Point",
      "Status"
    ];
    const rows = exportableStudentRows.map((student, index) => {
      const parent = student.parent || {};
      return [
        index + 1,
        student.name,
        student.class,
        student.section || "",
        student.rollNo || "",
        student.admissionNo || "",
        student.academicYear || "",
        formatExportDate(student.dateOfBirth),
        student.gender || "",
        student.bloodGroup || "",
        parent.name || "",
        parent.email || "",
        parent.phone || "",
        student.address || parent.address || "",
        student.city || "",
        student.state || "",
        student.pincode || "",
        student.fees?.monthlyFees?.[0]?.amount || 0,
        student.fees?.annualFee?.amount || 0,
        student.fees?.admissionFee?.amount || 0,
        student.transport?.enabled ? "Yes" : "No",
        student.transport?.monthlyAmount || 0,
        student.transport?.routeName || "",
        student.transport?.pickupPoint || "",
        student.isActive ? "Active" : "Inactive"
      ];
    });

    const tableHtml = `
      <html>
        <head>
          <meta charset="UTF-8" />
          <style>
            table { border-collapse: collapse; font-family: Arial, sans-serif; font-size: 12px; }
            th { background: #e9eff8; font-weight: 700; }
            th, td { border: 1px solid #b8c5d8; padding: 6px 8px; mso-number-format:"\\@"; }
          </style>
        </head>
        <body>
          <table>
            <thead><tr>${headers.map((header) => `<th>${escapeExcelCell(header)}</th>`).join("")}</tr></thead>
            <tbody>
              ${rows.map((row) => `<tr>${row.map((cell) => `<td>${escapeExcelCell(cell)}</td>`).join("")}</tr>`).join("")}
            </tbody>
          </table>
        </body>
      </html>
    `;
    const blob = new Blob(["\ufeff", tableHtml], { type: "application/vnd.ms-excel;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `students-class-${makeDownloadName(studentExportClass)}-${formatLocalDateInput(new Date())}.xls`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    setSuccess(`Exported ${exportableStudentRows.length} students from class ${studentExportClass}.`);
  };

  const downloadMonthlyPaymentReportAsExcel = ({ rows, monthKey, monthLabel, feeType, className, totals }) => {
    const headers = [
      "S.No",
      "Student",
      "Class",
      "Section",
      "Roll No",
      "Admission No",
      "Academic Year",
      "Parent",
      "Parent Phone",
      "Parent Email",
      "Fee Type",
      "Month",
      "Amount",
      "Status",
      "Mode of Payment",
      "Paid Date & Time",
      "Payment ID",
      "Remarks",
      "Screenshot"
    ];
    const bodyRows = rows.map((row, index) => [
      index + 1,
      row.student?.name || "",
      row.student?.class || "",
      row.student?.section || "",
      row.student?.rollNo || "",
      row.student?.admissionNo || "",
      row.student?.academicYear || "",
      row.parent?.name || "",
      row.parent?.phone || "",
      row.parent?.email || "",
      getFeeTypeLabel(row.feeType || feeType),
      row.monthLabel || row.monthKey || monthLabel || monthKey,
      row.amount || 0,
      row.status === "paid" ? "Paid" : "Pending",
      row.status === "paid" ? (row.paymentMethod || "-") : "-",
      row.status === "paid" ? formatPaymentDateTime(row.paidAt) : "-",
      row.paymentId || "",
      row.remarks || "",
      row.screenshotUrl || ""
    ]);
    const summaryRows = [
      ["Summary", "", "", "", "", "", "", "", "", "", "", "Total Amount", totals?.totalAmount || 0, "", "", "", "", "", ""],
      ["Paid", totals?.paidCount || 0, "", "", "", "", "", "", "", "", "", "Paid Amount", totals?.paidAmount || 0, "", "", "", "", "", ""],
      ["Pending", totals?.pendingCount || 0, "", "", "", "", "", "", "", "", "", "Pending Amount", totals?.pendingAmount || 0, "", "", "", "", "", ""]
    ];
    const tableHtml = `
      <html>
        <head>
          <meta charset="UTF-8" />
          <style>
            table { border-collapse: collapse; font-family: Arial, sans-serif; font-size: 12px; }
            th { background: #e9eff8; font-weight: 700; }
            th, td { border: 1px solid #b8c5d8; padding: 6px 8px; mso-number-format:"\\@"; }
          </style>
        </head>
        <body>
          <table>
            <thead><tr>${headers.map((header) => `<th>${escapeExcelCell(header)}</th>`).join("")}</tr></thead>
            <tbody>
              ${bodyRows.map((row) => `<tr>${row.map((cell) => `<td>${escapeExcelCell(cell)}</td>`).join("")}</tr>`).join("")}
              ${summaryRows.map((row) => `<tr>${row.map((cell) => `<td>${escapeExcelCell(cell)}</td>`).join("")}</tr>`).join("")}
            </tbody>
          </table>
        </body>
      </html>
    `;
    const blob = new Blob(["\ufeff", tableHtml], { type: "application/vnd.ms-excel;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `monthly-payment-report-class-${makeDownloadName(className)}-${makeDownloadName(feeType)}-${makeDownloadName(monthKey)}-${formatLocalDateInput(new Date())}.xls`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const getMonthlyPaymentReportQuery = () => {
    const monthKey = String(paymentReportFilters.month || "").trim();
    const className = String(paymentReportFilters.className || "").trim();
    if (!monthKey) {
      throw new Error("Please select a month before exporting monthly payment data.");
    }
    if (!className) {
      throw new Error("Please select a class before exporting monthly payment data.");
    }
    const selectedFeeType = String(paymentReportFilters.feeType || "tuition").trim().toLowerCase();
    const feeType = selectedFeeType || "tuition";
    if (!["tuition", "transport"].includes(feeType)) {
      throw new Error("Monthly export supports Tuition or Transport only. Please select Tuition or Transport.");
    }
    const query = new URLSearchParams();
    query.set("month", monthKey);
    query.set("className", className);
    query.set("feeType", feeType);
    return { query, monthKey, className, feeType };
  };

  const fetchMonthlyPaymentReport = async () => {
    const { query } = getMonthlyPaymentReportQuery();
    const { data } = await api.get(`/admin/monthly-payment-report?${query.toString()}`);
    if (!(data.rows || []).length) {
      throw new Error("No students found for the selected class and month.");
    }
    return data;
  };

  const exportMonthlyPaymentsToExcel = async () => {
    setPaymentHistoryLoading(true);
    setError("");
    try {
      const data = await fetchMonthlyPaymentReport();
      const rows = data.rows || [];
      downloadMonthlyPaymentReportAsExcel({
        rows,
        monthKey: data.monthKey,
        monthLabel: data.monthLabel,
        feeType: data.feeType || "tuition",
        className: data.className,
        totals: data.totals || {}
      });
      setSuccess(`Exported ${rows.length} ${getFeeTypeLabel(data.feeType || "tuition").toLowerCase()} rows for class ${data.className}, ${data.monthLabel || data.monthKey}.`);
    } catch (err) {
      setError(err.message || getReadableAdminError(err, "Failed to export monthly payment data"));
    } finally {
      setPaymentHistoryLoading(false);
    }
  };

  const printMonthlyPaymentsPdf = async () => {
    const reportWindow = window.open("", "_blank");
    if (!reportWindow) {
      setError("Please allow pop-ups to open the PDF report.");
      return;
    }
    setPaymentHistoryLoading(true);
    setError("");
    try {
      const data = await fetchMonthlyPaymentReport();
      const rows = data.rows || [];
      const reportDate = formatPaymentDateTime(new Date());
      const tableRows = rows.map((row, index) => `
        <tr>
          <td>${escapeExcelCell(index + 1)}</td>
          <td>
            <strong>${escapeExcelCell(row.student?.name || "")}</strong>
            <div class="muted">Adm: ${escapeExcelCell(row.student?.admissionNo || "-")}</div>
          </td>
          <td>${escapeExcelCell(row.student?.class || "")}-${escapeExcelCell(row.student?.section || "NA")}</td>
          <td>${escapeExcelCell(row.student?.rollNo || "-")}</td>
          <td>
            ${escapeExcelCell(row.parent?.name || "-")}
            <div class="muted">${escapeExcelCell(row.parent?.phone || row.parent?.email || "-")}</div>
          </td>
          <td>${escapeExcelCell(row.monthLabel || row.monthKey || "")}</td>
          <td class="amount">${escapeExcelCell(money(row.amount || 0))}</td>
          <td><span class="status ${row.status === "paid" ? "paid" : "pending"}">${escapeExcelCell(row.status === "paid" ? "Paid" : "Pending")}</span></td>
          <td>${escapeExcelCell(row.status === "paid" ? (row.paymentMethod || "-") : "-")}</td>
          <td>${escapeExcelCell(row.status === "paid" ? formatPaymentDateTime(row.paidAt) : "-")}</td>
          <td>${escapeExcelCell(row.paymentId || "-")}</td>
        </tr>
      `).join("");
      reportWindow.document.open();
      reportWindow.document.write(`
        <!doctype html>
        <html>
          <head>
            <title>Class ${escapeExcelCell(data.className)} ${escapeExcelCell(data.monthLabel || data.monthKey)} Payment Report</title>
            <style>
              * { box-sizing: border-box; }
              body { margin: 0; padding: 28px; color: #16243d; font-family: Arial, sans-serif; background: #fff; }
              .header { display: flex; justify-content: space-between; gap: 20px; align-items: flex-start; border-bottom: 2px solid #1f4f8f; padding-bottom: 16px; margin-bottom: 18px; }
              h1 { margin: 0; font-size: 22px; color: #0b2b5c; }
              .subtitle { margin-top: 6px; color: #53647f; font-size: 13px; }
              .meta { text-align: right; font-size: 12px; color: #53647f; line-height: 1.7; }
              .cards { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin: 16px 0; }
              .card { border: 1px solid #d8e2f0; border-radius: 6px; padding: 10px; background: #f8fbff; }
              .label { color: #63728a; font-size: 11px; text-transform: uppercase; font-weight: 700; }
              .value { margin-top: 4px; font-size: 16px; font-weight: 800; color: #0b2b5c; }
              table { width: 100%; border-collapse: collapse; font-size: 11px; }
              th { background: #eef4fb; color: #1c3458; text-align: left; font-size: 10px; text-transform: uppercase; }
              th, td { border: 1px solid #d8e2f0; padding: 7px; vertical-align: top; }
              tr:nth-child(even) td { background: #fbfdff; }
              .muted { color: #6f7f96; font-size: 10px; margin-top: 3px; }
              .amount { white-space: nowrap; font-weight: 700; }
              .status { display: inline-block; border-radius: 999px; padding: 3px 8px; font-size: 10px; font-weight: 800; }
              .paid { color: #116b38; background: #dff5e8; }
              .pending { color: #b42318; background: #ffe4e1; }
              @media print {
                body { padding: 16px; }
                .no-print { display: none; }
                table { page-break-inside: auto; }
                tr { page-break-inside: avoid; page-break-after: auto; }
              }
            </style>
          </head>
          <body>
            <div class="header">
              <div>
                <h1>MPIPS Monthly Payment Status Report</h1>
                <div class="subtitle">Class ${escapeExcelCell(data.className)} | ${escapeExcelCell(getFeeTypeLabel(data.feeType))} | ${escapeExcelCell(data.monthLabel || data.monthKey)}</div>
              </div>
              <div class="meta">
                Generated: ${escapeExcelCell(reportDate)}<br />
                Total Students: ${escapeExcelCell(rows.length)}
              </div>
            </div>
            <div class="cards">
              <div class="card"><div class="label">Total Fees</div><div class="value">${escapeExcelCell(money(data.totals?.totalAmount || 0))}</div></div>
              <div class="card"><div class="label">Paid</div><div class="value">${escapeExcelCell(data.totals?.paidCount || 0)} / ${escapeExcelCell(money(data.totals?.paidAmount || 0))}</div></div>
              <div class="card"><div class="label">Pending</div><div class="value">${escapeExcelCell(data.totals?.pendingCount || 0)} / ${escapeExcelCell(money(data.totals?.pendingAmount || 0))}</div></div>
              <div class="card"><div class="label">Month</div><div class="value">${escapeExcelCell(data.monthLabel || data.monthKey)}</div></div>
            </div>
            <table>
              <thead>
                <tr>
                  <th>S.No</th><th>Student</th><th>Class</th><th>Roll</th><th>Parent</th><th>Month</th><th>Amount</th><th>Status</th><th>Mode</th><th>Paid Date & Time</th><th>Payment ID</th>
                </tr>
              </thead>
              <tbody>${tableRows}</tbody>
            </table>
            <script>window.onload = () => { window.focus(); window.print(); };</script>
          </body>
        </html>
      `);
      reportWindow.document.close();
      setSuccess(`Opened PDF report for class ${data.className}, ${data.monthLabel || data.monthKey}.`);
    } catch (err) {
      reportWindow.close();
      setError(err.message || getReadableAdminError(err, "Failed to open monthly payment PDF"));
    } finally {
      setPaymentHistoryLoading(false);
    }
  };

  const startNoticeEdit = (notice) => {
    setNoticeForm({
      id: notice.id,
      title: notice.title || "",
      details: notice.details || "",
      noticeDate: toDateInputValue(notice.noticeDate || notice.createdAt),
      targetType: notice.targetType || "all",
      targetTeachers: (notice.targetTeachers || []).map((t) => String(t)),
      targetStudents: (notice.targetStudents || []).map((s) => String(s))
    });
    setActiveTab("notices");
  };

  const submitNotice = async (e) => {
    e.preventDefault();
    setSavingNotice(true);
    setError("");
    try {
      const payload = {
        title: noticeForm.title,
        details: noticeForm.details,
        noticeDate: noticeForm.noticeDate ? `${noticeForm.noticeDate}T00:00:00.000Z` : undefined,
        targetType: noticeForm.targetType,
        targetTeachers: noticeForm.targetTeachers,
        targetStudents: noticeForm.targetStudents
      };
      if (noticeForm.id) {
        await runAdminRequestWithTokenRetry((token) =>
          api.put(`/admin/notices/${noticeForm.id}`, payload, { headers: { Authorization: `Bearer ${token}` } })
        );
      } else {
        await runAdminRequestWithTokenRetry((token) =>
          api.post("/admin/notices", payload, { headers: { Authorization: `Bearer ${token}` } })
        );
      }
      setNoticeForm({ ...initialNoticeForm });
      await fetchNotices();
      setSuccess(noticeForm.id ? "Notice updated successfully." : "Notice created successfully.");
    } catch (err) {
      setError(getReadableAdminError(err, "Failed to save notice"));
    } finally {
      setSavingNotice(false);
    }
  };

  const deleteNotice = async (noticeId) => {
    requestActionConfirm({
      title: "Delete Notice",
      message: "This will permanently remove the notice. Continue?",
      actionType: "delete-notice",
      targetId: noticeId
    });
  };

  const requestProofDecision = (proof, action) => {
    const feeTypeLabel = proof.feeType === "transport" ? "transport fee" : proof.feeType === "annual" ? "annual fee" : proof.feeType === "admission" ? "admission fee" : "tuition fee";
    requestActionConfirm({
      title: action === "approve" ? "Approve UPI Proof" : "Reject UPI Proof",
      message: action === "approve"
        ? `Approve ${feeTypeLabel} payment for ${proof.student?.name || "student"} (${proof.monthKey})?`
        : `Reject ${feeTypeLabel} proof for ${proof.student?.name || "student"} (${proof.monthKey})?`,
      actionType: action === "approve" ? "approve-proof" : "reject-proof",
      targetId: proof.id
    });
  };

  const openProofPreview = (proof) => {
    setProofPreview({ open: true, proof, imageFailed: false, zoom: 1 });
  };

  const closeProofPreview = () => {
    setProofPreview({ open: false, proof: null, imageFailed: false, zoom: 1 });
  };

  const openPaymentHistoryProof = (payment) => {
    if (!payment?.paymentProof?.screenshotUrl) return;
    openProofPreview({
      ...payment.paymentProof,
      id: payment.paymentProof.id || payment.paymentProof._id,
      screenshotUrl: payment.paymentProof.screenshotUrl,
      student: payment.student,
      parent: payment.parent,
      feeType: payment.feeType,
      monthKey: payment.paymentProof.monthKey || payment.monthKey || payment.academicYear || "",
      coverageStartKey: payment.paymentProof.coverageStartKey || payment.monthKey || payment.academicYear || "",
      coverageEndKey: payment.paymentProof.coverageEndKey || payment.monthKey || payment.academicYear || "",
      amount: payment.paymentProof.amount || payment.amount,
      txnId: payment.paymentProof.txnId || payment.razorpay_payment_id || "",
      remarks: payment.paymentProof.remarks || payment.remarks || ""
    });
  };

  const adjustProofZoom = (nextZoom) => {
    setProofPreview((prev) => ({
      ...prev,
      zoom: Math.min(3, Math.max(0.5, Number(nextZoom) || 1))
    }));
  };

  if (loading) {
    return (
      <div className="dash-root">
        <div className="dash-main" style={{ padding: 24 }}>
          <p>Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  const adminName = adminProfile?.name || user?.name || "Admin";
  const adminRole = adminProfile?.role || user?.role || "superadmin";
  const adminPassword = adminProfile?.loginPassword || "Not available";
  const adminInitials = adminName
    .split(" ")
    .map((item) => item[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className={`dash-root ${sidebarCollapsed ? "sidebar-collapsed" : ""}`}>
      <StatusToast
        message={error || success}
        type={error ? "error" : "success"}
        onClose={() => {
          setError("");
          setSuccess("");
        }}
      />
      {profileModalOpen && (
        <div className="confirm-modal-backdrop" role="presentation">
          <div className="confirm-modal-card profile-modal-card" role="dialog" aria-modal="true" aria-label="Admin profile">
            <button type="button" className="profile-modal-close" onClick={() => setProfileModalOpen(false)} aria-label="Close profile">
              <AppIcon name="close" />
            </button>
            <div className="profile-modal-head">
              <span className="profile-head-avatar">{adminInitials || "AD"}</span>
              <div className="profile-head-meta">
                <h3>Admin Profile</h3>
                <span className="profile-role-badge">{adminRole}</span>
              </div>
            </div>
            <div className="profile-grid">
              <div className="profile-row"><strong>Name:</strong><span>{adminName}</span></div>
              <div className="profile-row">
                <strong>Email:</strong>
                <span className="profile-value-wrap">
                  <span>{adminProfile?.email || user?.email || "-"}</span>
                  <button type="button" className="profile-copy-btn" onClick={() => copyToClipboard(adminProfile?.email || user?.email, "Email")} title="Copy email">
                    <AppIcon name="copy" />
                  </button>
                </span>
              </div>
              <div className="profile-row"><strong>Role:</strong><span>{adminRole}</span></div>
              <div className="profile-row">
                <strong>Phone:</strong>
                <span className="profile-value-wrap">
                  <span>{adminProfile?.phone || "-"}</span>
                  <button type="button" className="profile-copy-btn" onClick={() => copyToClipboard(adminProfile?.phone, "Phone")} title="Copy phone">
                    <AppIcon name="copy" />
                  </button>
                </span>
              </div>
              <div className="profile-row"><strong>Address:</strong><span>{adminProfile?.address || "-"}</span></div>
              <div className="profile-row profile-password-row">
                <strong>Password:</strong>
                <span className="profile-value-wrap">
                  <span>{showAdminPassword ? adminPassword : "********"}</span>
                  <button type="button" className="profile-copy-btn" onClick={() => setShowAdminPassword((prev) => !prev)} title={showAdminPassword ? "Hide password" : "Show password"}>
                    <AppIcon name={showAdminPassword ? "eyeOff" : "eye"} />
                  </button>
                </span>
              </div>
            </div>
            <div className="profile-password-form-wrap">
              <h4>Change Password</h4>
              <form className="profile-password-form" onSubmit={changeAdminPassword}>
                <input
                  type="password"
                  placeholder="Current Password"
                  value={adminPasswordForm.currentPassword}
                  onChange={(e) => setAdminPasswordForm((prev) => ({ ...prev, currentPassword: e.target.value }))}
                  autoComplete="current-password"
                />
                <input
                  type="password"
                  placeholder="New Password"
                  value={adminPasswordForm.newPassword}
                  onChange={(e) => setAdminPasswordForm((prev) => ({ ...prev, newPassword: e.target.value }))}
                  autoComplete="new-password"
                />
                <input
                  type="password"
                  placeholder="Confirm New Password"
                  value={adminPasswordForm.confirmPassword}
                  onChange={(e) => setAdminPasswordForm((prev) => ({ ...prev, confirmPassword: e.target.value }))}
                  autoComplete="new-password"
                />
                <div className="profile-password-form-actions">
                  <button type="submit" disabled={changingAdminPassword}>
                    {changingAdminPassword ? "Updating..." : "Update Password"}
                  </button>
                </div>
              </form>
            </div>
            <div className="confirm-modal-actions">
              <button type="button" onClick={() => setProfileModalOpen(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
      <aside className="dash-sidebar">
        <SchoolBrand />

        <div className="dash-sidebar-inner">
          <nav className="dash-nav">
            <div className="dash-nav-group">
              <button className={activeTab === "dashboard" ? "dash-nav-btn active" : "dash-nav-btn"} onClick={() => setActiveTab("dashboard")}>
                <span className="nav-row"><AppIcon name="dashboard" className="nav-icon" /><span className="nav-text">Dashboard</span></span>
              </button>
            </div>

            <div className="dash-nav-group">
              <button className="dash-nav-btn has-submenu" onClick={() => setSidebarOpen((prev) => ({ ...prev, students: !prev.students }))}>
                <span className="nav-row">
                  <span className="nav-row-main"><AppIcon name="students" className="nav-icon" /><span className="nav-text">Students</span></span>
                  <AppIcon name="chevronRight" className={sidebarOpen.students ? "submenu-arrow open" : "submenu-arrow"} />
                </span>
              </button>
              <div className={sidebarOpen.students ? "dash-submenu open" : "dash-submenu"}>
                  <button className={activeTab === "students" ? "dash-submenu-btn active" : "dash-submenu-btn"} onClick={() => setActiveTab("students")}>
                    <span className="nav-row"><AppIcon name="list" className="nav-icon" /><span className="nav-text">All Students</span></span>
                  </button>
                  <button className={activeTab === "student-form" ? "dash-submenu-btn active" : "dash-submenu-btn"} onClick={startStudentCreate}>
                    <span className="nav-row"><AppIcon name="account" className="nav-icon" /><span className="nav-text">Student Form</span></span>
                  </button>
                  <button className={activeTab === "student-attendance" ? "dash-submenu-btn active" : "dash-submenu-btn"} onClick={() => setActiveTab("student-attendance")}>
                    <span className="nav-row"><AppIcon name="check" className="nav-icon" /><span className="nav-text">Student Attendance</span></span>
                  </button>
                  <button className={activeTab === "student-promotion" ? "dash-submenu-btn active" : "dash-submenu-btn"} onClick={() => setActiveTab("student-promotion")}>
                    <span className="nav-row"><AppIcon name="history" className="nav-icon" /><span className="nav-text">Student Promotion</span></span>
                  </button>
                  <button className={activeTab === "transfer-certificate" ? "dash-submenu-btn active" : "dash-submenu-btn"} onClick={() => setActiveTab("transfer-certificate")}>
                    <span className="nav-row"><AppIcon name="assignment" className="nav-icon" /><span className="nav-text">Transfer Certificate</span></span>
                  </button>
                  <button className={activeTab === "exams" ? "dash-submenu-btn active" : "dash-submenu-btn"} onClick={() => setActiveTab("exams")}>
                    <span className="nav-row"><AppIcon name="assignment" className="nav-icon" /><span className="nav-text">Exams & Marks</span></span>
                  </button>
                  <button className={activeTab === "hall-tickets" ? "dash-submenu-btn active" : "dash-submenu-btn"} onClick={() => setActiveTab("hall-tickets")}>
                    <span className="nav-row"><AppIcon name="notice" className="nav-icon" /><span className="nav-text">Hall Tickets</span></span>
                  </button>
              </div>
            </div>

            <div className="dash-nav-group">
              <button className="dash-nav-btn has-submenu" onClick={() => setSidebarOpen((prev) => ({ ...prev, teachers: !prev.teachers }))}>
                <span className="nav-row">
                  <span className="nav-row-main"><AppIcon name="teachers" className="nav-icon" /><span className="nav-text">Teachers</span></span>
                  <AppIcon name="chevronRight" className={sidebarOpen.teachers ? "submenu-arrow open" : "submenu-arrow"} />
                </span>
              </button>
              <div className={sidebarOpen.teachers ? "dash-submenu open" : "dash-submenu"}>
                  <button className={activeTab === "teachers" ? "dash-submenu-btn active" : "dash-submenu-btn"} onClick={() => setActiveTab("teachers")}>
                    <span className="nav-row"><AppIcon name="list" className="nav-icon" /><span className="nav-text">All Teachers</span></span>
                  </button>
                  <button className={activeTab === "teacher-form" ? "dash-submenu-btn active" : "dash-submenu-btn"} onClick={startTeacherCreate}>
                    <span className="nav-row"><AppIcon name="account" className="nav-icon" /><span className="nav-text">Teacher Form</span></span>
                  </button>
                  <button className={activeTab === "teacher-attendance" ? "dash-submenu-btn active" : "dash-submenu-btn"} onClick={() => setActiveTab("teacher-attendance")}>
                    <span className="nav-row"><AppIcon name="check" className="nav-icon" /><span className="nav-text">Teacher Attendance</span></span>
                  </button>
                  <button className={activeTab === "leave-requests" ? "dash-submenu-btn active" : "dash-submenu-btn"} onClick={() => setActiveTab("leave-requests")}>
                    <span className="nav-row"><AppIcon name="history" className="nav-icon" /><span className="nav-text">Leave Requests</span></span>
                  </button>
              </div>
            </div>

            <div className="dash-nav-group">
              <button className="dash-nav-btn has-submenu" onClick={() => setSidebarOpen((prev) => ({ ...prev, fees: !prev.fees }))}>
                <span className="nav-row">
                  <span className="nav-row-main"><AppIcon name="fee" className="nav-icon" /><span className="nav-text">Fees</span>{pendingProofCounts.tuition > 0 && <span className="dash-badge">{pendingProofCounts.tuition}</span>}</span>
                  <AppIcon name="chevronRight" className={sidebarOpen.fees ? "submenu-arrow open" : "submenu-arrow"} />
                </span>
              </button>
              <div className={sidebarOpen.fees ? "dash-submenu open" : "dash-submenu"}>
                <button className={activeTab === "fee-collection" ? "dash-submenu-btn active" : "dash-submenu-btn"} onClick={() => setActiveTab("fee-collection")}>
                  <span className="nav-row"><AppIcon name="fee" className="nav-icon" /><span className="nav-text">Fee Collection</span></span>
                </button>
                <button className={activeTab === "create-payment" ? "dash-submenu-btn active" : "dash-submenu-btn"} onClick={() => setActiveTab("create-payment")}>
                  <span className="nav-row"><AppIcon name="assignment" className="nav-icon" /><span className="nav-text">Create Student Payment</span></span>
                </button>
                <button className={activeTab === "payment-history" ? "dash-submenu-btn active" : "dash-submenu-btn"} onClick={() => setActiveTab("payment-history")}>
                  <span className="nav-row"><AppIcon name="history" className="nav-icon" /><span className="nav-text">Payment History</span></span>
                </button>
                <button className={activeTab === "upi-proofs" ? "dash-submenu-btn active" : "dash-submenu-btn"} onClick={() => setActiveTab("upi-proofs")}>
                  <span className="nav-row"><AppIcon name="history" className="nav-icon" /><span className="nav-text">Tuition UPI Proofs</span>{pendingProofCounts.tuition > 0 && <span className="dash-badge">{pendingProofCounts.tuition}</span>}</span>
                </button>
                <button className={activeTab === "defaulters" ? "dash-submenu-btn active" : "dash-submenu-btn"} onClick={() => setActiveTab("defaulters")}>
                  <span className="nav-row"><AppIcon name="notice" className="nav-icon" /><span className="nav-text">Fee Defaulters</span></span>
                </button>
                <button className={activeTab === "revenue" ? "dash-submenu-btn active" : "dash-submenu-btn"} onClick={() => setActiveTab("revenue")}>
                  <span className="nav-row"><AppIcon name="revenue" className="nav-icon" /><span className="nav-text">Revenue</span></span>
                </button>
              </div>
            </div>

            <div className="dash-nav-group">
              <button className="dash-nav-btn has-submenu" onClick={() => setSidebarOpen((prev) => ({ ...prev, annual: !prev.annual }))}>
                <span className="nav-row">
                  <span className="nav-row-main"><AppIcon name="assignment" className="nav-icon" /><span className="nav-text">Annual Fee</span>{pendingProofCounts.annual > 0 && <span className="dash-badge">{pendingProofCounts.annual}</span>}</span>
                  <AppIcon name="chevronRight" className={sidebarOpen.annual ? "submenu-arrow open" : "submenu-arrow"} />
                </span>
              </button>
              <div className={sidebarOpen.annual ? "dash-submenu open" : "dash-submenu"}>
                <button className={activeTab === "annual-collection" ? "dash-submenu-btn active" : "dash-submenu-btn"} onClick={() => setActiveTab("annual-collection")}>
                  <span className="nav-row"><AppIcon name="fee" className="nav-icon" /><span className="nav-text">Annual Collection</span></span>
                </button>
                <button className={activeTab === "annual-upi-proofs" ? "dash-submenu-btn active" : "dash-submenu-btn"} onClick={() => setActiveTab("annual-upi-proofs")}>
                  <span className="nav-row"><AppIcon name="history" className="nav-icon" /><span className="nav-text">Annual UPI Proofs</span>{pendingProofCounts.annual > 0 && <span className="dash-badge">{pendingProofCounts.annual}</span>}</span>
                </button>
              </div>
            </div>

            <div className="dash-nav-group">
              <button className="dash-nav-btn has-submenu" onClick={() => setSidebarOpen((prev) => ({ ...prev, admission: !prev.admission }))}>
                <span className="nav-row">
                  <span className="nav-row-main"><AppIcon name="assignment" className="nav-icon" /><span className="nav-text">Admission Fee</span>{pendingProofCounts.admission > 0 && <span className="dash-badge">{pendingProofCounts.admission}</span>}</span>
                  <AppIcon name="chevronRight" className={sidebarOpen.admission ? "submenu-arrow open" : "submenu-arrow"} />
                </span>
              </button>
              <div className={sidebarOpen.admission ? "dash-submenu open" : "dash-submenu"}>
                <button className={activeTab === "admission-collection" ? "dash-submenu-btn active" : "dash-submenu-btn"} onClick={() => setActiveTab("admission-collection")}>
                  <span className="nav-row"><AppIcon name="fee" className="nav-icon" /><span className="nav-text">Admission Collection</span></span>
                </button>
                <button className={activeTab === "admission-upi-proofs" ? "dash-submenu-btn active" : "dash-submenu-btn"} onClick={() => setActiveTab("admission-upi-proofs")}>
                  <span className="nav-row"><AppIcon name="history" className="nav-icon" /><span className="nav-text">Admission UPI Proofs</span>{pendingProofCounts.admission > 0 && <span className="dash-badge">{pendingProofCounts.admission}</span>}</span>
                </button>
              </div>
            </div>

            <div className="dash-nav-group">
              <button className="dash-nav-btn has-submenu" onClick={() => setSidebarOpen((prev) => ({ ...prev, transport: !prev.transport }))}>
                <span className="nav-row">
                  <span className="nav-row-main"><AppIcon name="transport" className="nav-icon" /><span className="nav-text">Transport</span>{pendingProofCounts.transport > 0 && <span className="dash-badge">{pendingProofCounts.transport}</span>}</span>
                  <AppIcon name="chevronRight" className={sidebarOpen.transport ? "submenu-arrow open" : "submenu-arrow"} />
                </span>
              </button>
              <div className={sidebarOpen.transport ? "dash-submenu open" : "dash-submenu"}>
                <button className={activeTab === "transport-collection" ? "dash-submenu-btn active" : "dash-submenu-btn"} onClick={() => setActiveTab("transport-collection")}>
                  <span className="nav-row"><AppIcon name="transport" className="nav-icon" /><span className="nav-text">Transport Collection</span></span>
                </button>
                <button className={activeTab === "transport-upi-proofs" ? "dash-submenu-btn active" : "dash-submenu-btn"} onClick={() => setActiveTab("transport-upi-proofs")}>
                  <span className="nav-row"><AppIcon name="history" className="nav-icon" /><span className="nav-text">Transport UPI Proofs</span>{pendingProofCounts.transport > 0 && <span className="dash-badge">{pendingProofCounts.transport}</span>}</span>
                </button>
                <button className={activeTab === "transport-defaulters" ? "dash-submenu-btn active" : "dash-submenu-btn"} onClick={() => setActiveTab("transport-defaulters")}>
                  <span className="nav-row"><AppIcon name="transport" className="nav-icon" /><span className="nav-text">Transport Defaulters</span></span>
                </button>
              </div>
            </div>

            <div className="dash-nav-group">
              <button className="dash-nav-btn has-submenu" onClick={() => setSidebarOpen((prev) => ({ ...prev, operations: !prev.operations }))}>
                <span className="nav-row">
                  <span className="nav-row-main"><AppIcon name="assignment" className="nav-icon" /><span className="nav-text">Operations</span></span>
                  <AppIcon name="chevronRight" className={sidebarOpen.operations ? "submenu-arrow open" : "submenu-arrow"} />
                </span>
              </button>
              <div className={sidebarOpen.operations ? "dash-submenu open" : "dash-submenu"}>
                <button className={activeTab === "inventory" ? "dash-submenu-btn active" : "dash-submenu-btn"} onClick={() => setActiveTab("inventory")}>
                  <span className="nav-row"><AppIcon name="assignment" className="nav-icon" /><span className="nav-text">Inventory</span></span>
                </button>
                <button className={activeTab === "timetable" ? "dash-submenu-btn active" : "dash-submenu-btn"} onClick={() => setActiveTab("timetable")}>
                  <span className="nav-row"><AppIcon name="history" className="nav-icon" /><span className="nav-text">Timetable</span></span>
                </button>
                <button className={activeTab === "archive" ? "dash-submenu-btn active" : "dash-submenu-btn"} onClick={() => setActiveTab("archive")}>
                  <span className="nav-row"><AppIcon name="history" className="nav-icon" /><span className="nav-text">Archive</span></span>
                </button>
              </div>
            </div>

            <div className="dash-nav-group">
              <button className={activeTab === "notices" ? "dash-nav-btn active" : "dash-nav-btn"} onClick={() => setActiveTab("notices")}>
                <span className="nav-row"><AppIcon name="notice" className="nav-icon" /><span className="nav-text">Notices</span></span>
              </button>
            </div>
          </nav>
        </div>

        <button className="dash-logout" onClick={logout}>
          <span className="nav-row"><AppIcon name="logout" className="nav-icon" /><span className="nav-text">Logout</span></span>
        </button>
      </aside>

      <section className="dash-main">
        <header className="dash-topbar">
          <button
            type="button"
            className="sidebar-collapse-toggle"
            onClick={() => setSidebarCollapsed((prev) => !prev)}
            aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <AppIcon name="chevronRight" className={sidebarCollapsed ? "collapse-icon collapsed" : "collapse-icon"} />
          </button>
          <h1 className="dash-top-title admin-top-title">Welcome To MPIPS School Management System Admin Dashboard</h1>
          <div className="top-user-wrap" ref={profileMenuRef}>
            <button type="button" className="top-user-btn" onClick={() => setProfileMenuOpen((prev) => !prev)}>
              <span className="top-user-avatar">{adminInitials || "AD"}</span>
              <span className="top-user-meta">
                <strong>{adminName}</strong>
                <small>{adminRole}</small>
              </span>
              <AppIcon name="chevronRight" className={profileMenuOpen ? "submenu-arrow open" : "submenu-arrow"} />
            </button>
            {profileMenuOpen && (
              <div className="top-user-dropdown">
                <button type="button" onClick={() => { setProfileModalOpen(true); setProfileMenuOpen(false); }}>View Profile</button>
                <button type="button" onClick={logout}>Logout</button>
              </div>
            )}
          </div>
        </header>

        <main className="dash-content">
          <p className="dash-breadcrumb">Home - Admin</p>
          {activeTab === "dashboard" && (
            <section>
              <div className="admin-metric-grid">
                <article className="admin-metric-card">
                  <div className="metric-icon metric-green"><AppIcon name="students" size={26} /></div>
                  <div><h3>{overview.totalStudents || 0}</h3><p>Total Students</p></div>
                </article>
                <article className="admin-metric-card">
                  <div className="metric-icon metric-blue"><AppIcon name="teachers" size={26} /></div>
                  <div><h3>{overview.totalTeachers || 0}</h3><p>Total Teachers</p></div>
                </article>
                <article className="admin-metric-card">
                  <div className="metric-icon metric-yellow"><AppIcon name="child" size={26} /></div>
                  <div><h3>{overview.totalParents || 0}</h3><p>Total Parents</p></div>
                </article>
                <article className="admin-metric-card">
                  <div className="metric-icon metric-cyan"><AppIcon name="fee" size={26} /></div>
                  <div><h3>{money(overview.yearlyEarnings)}</h3><p>Yearly Total Earnings</p></div>
                </article>
              </div>

              <div className="dash-panel" style={{ marginTop: 12 }}>
                <div className="panel-head">
                  <h3>Fees Collection & Expenses</h3>
                  <div className="chart-legend">
                    <span><i style={{ background: "#2591f0" }} />Collections</span>
                    <span><i style={{ background: "#f0515e" }} />Fees</span>
                    <span><i style={{ background: "#f3c818" }} />Expenses</span>
                  </div>
                </div>
                <AreaGraph data={overview.chart || []} />
              </div>

              <div className="dashboard-lower-grid">
                <div className="dash-panel">
                  <div className="panel-head">
                    <h3>Event Calendar</h3>
                    <div className="calendar-nav">
                      <button
                        type="button"
                        className="table-btn"
                        onClick={() => setVisibleMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))}
                      >
                        Prev
                      </button>
                      <strong>{getMonthTitle(visibleMonth)}</strong>
                      <button
                        type="button"
                        className="table-btn"
                        onClick={() => setVisibleMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                  <div className="calendar-grid">
                    {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((name) => (
                      <div key={name} className="calendar-head">{name}</div>
                    ))}
                    {getCalendarMatrix(visibleMonth).flat().map((cell) => (
                      <button
                        key={cell.dateKey}
                        type="button"
                        onClick={() => setSelectedDateKey(cell.dateKey)}
                        className={`calendar-cell calendar-cell-btn ${cell.isCurrentMonth ? "" : "muted"} ${cell.isToday ? "today" : ""} ${selectedDateKey === cell.dateKey ? "selected" : ""}`}
                      >
                        <span>{cell.day}</span>
                        {(eventsByDate[cell.dateKey] || []).length > 0 && (
                          <small className="calendar-badge">{eventsByDate[cell.dateKey].length} event</small>
                        )}
                      </button>
                    ))}
                  </div>
                  <div className="calendar-event-editor">
                    <h4>{selectedDateKey ? `Events For ${selectedDateKey}` : "Select a day to add event"}</h4>
                    {selectedDateKey && (
                      <>
                        <form onSubmit={submitEvent} className="calendar-event-form">
                          <input
                            value={eventForm.title}
                            onChange={(e) => setEventForm((prev) => ({ ...prev, title: e.target.value }))}
                            placeholder="Event title"
                            required
                          />
                          <input
                            value={eventForm.description}
                            onChange={(e) => setEventForm((prev) => ({ ...prev, description: e.target.value }))}
                            placeholder="Description (optional)"
                          />
                          <button type="submit" className="table-btn" disabled={savingEvent}>
                            {savingEvent ? (eventForm.id ? "Updating..." : "Adding...") : (eventForm.id ? "Update Event" : "Add Event")}
                          </button>
                          {eventForm.id && (
                            <button
                              type="button"
                              className="table-btn"
                              onClick={() => setEventForm(initialEventForm)}
                            >
                              Cancel
                            </button>
                          )}
                        </form>
                        <div className="day-events-list">
                          {selectedDateEvents.length === 0 && <p className="dash-subtitle" style={{ padding: 0 }}>No events for selected day.</p>}
                          {selectedDateEvents.map((event) => (
                            <div key={event.id} className="day-event-item">
                              <div>
                                <strong>{event.title}</strong>
                                {event.description ? <p>{event.description}</p> : null}
                              </div>
                              <div>
                                <button type="button" className="table-btn" onClick={() => startEditEvent(event)}>Edit</button>
                                <button type="button" className="table-btn danger" onClick={() => deleteEvent(event.id)}>Delete</button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div className="dashboard-side-stack">
                  <div className="dash-panel">
                    <h3>Notice Board</h3>
                    <div className="notice-list">
                      {noticeItems.map((item) => (
                        <div key={item.title} className={`notice-item ${item.tone}`}>
                          <p>{item.title}</p>
                          <strong>{item.value}</strong>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="dash-panel">
                    <h3>Recent Activities</h3>
                    <div className="activity-list">
                      {recentActivities.map((activity) => (
                        <div key={activity.id} className="activity-item">
                          <span className={`activity-dot ${activity.tone}`} />
                          <div>
                            <p>{activity.title}</p>
                            <small>{activity.meta} - {activity.when}</small>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </section>
          )}

          {activeTab === "students" && (
            <section>
              <div className="table-header-row list-header">
                <div>
                  <h2 style={{ margin: 0 }}>All Students</h2>
                  <div className="list-stat-chips">
                    <span className="stat-chip neutral">Total: {studentStats.total}</span>
                    <span className="stat-chip success">Active: {studentStats.active}</span>
                    <span className="stat-chip danger">Inactive: {studentStats.inactive}</span>
                  </div>
                </div>
                <div className="table-tools">
                  <input
                    value={studentSearch}
                    onChange={(e) => setStudentSearch(e.target.value)}
                    placeholder="Search name, class, roll, parent"
                  />
                  <select value={studentExportClass} onChange={(e) => setStudentExportClass(e.target.value)}>
                    <option value="">Select Class For Export</option>
                    {promotionClassOptions.map((className) => (
                      <option key={className} value={className}>{className}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="table-btn"
                    onClick={exportStudentsToExcel}
                    disabled={!studentExportClass || exportableStudentRows.length === 0}
                    title={studentExportClass ? `Export ${exportableStudentRows.length} student(s)` : "Select class first"}
                  >
                    Export Excel
                  </button>
                  <button className="table-btn" onClick={startStudentCreate}>Add Student</button>
                </div>
              </div>

              <div className="dash-table-wrap list-table-card">
                <table className="dash-table fancy-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Class</th>
                      <th>Roll</th>
                      <th>Parent</th>
                      <th>Parent Contact</th>
                      <th>Monthly Fee</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStudents.length === 0 && (
                      <tr><td colSpan={8}>No students found.</td></tr>
                    )}
                    {pagedStudents.map((student) => {
                      const monthly = student.fees?.monthlyFees?.[0]?.amount || 0;
                      return (
                        <tr key={student.id}>
                          <td>
                            <div className="list-primary-cell">
                              {student.photoUrl ? (
                                <img src={student.photoUrl} alt={student.name} className="mini-avatar" />
                              ) : (
                                <span className="mini-avatar placeholder"><AppIcon name="child" size={12} /></span>
                              )}
                              <button className="name-link-btn" onClick={() => navigate(`/admin/students/${student.id}`)}>{student.name}</button>
                            </div>
                          </td>
                          <td>{student.class} - {student.section || "NA"}</td>
                          <td>{student.rollNo || "-"}</td>
                          <td>{student.parent?.name || "-"}</td>
                          <td>{student.parent?.phone || student.parent?.email || "-"}</td>
                          <td>{money(monthly)}</td>
                          <td>
                            <span className={student.isActive ? "status-pill paid" : "status-pill due"}>
                              {student.isActive ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td>
                            <div className="icon-actions">
                              <button
                                type="button"
                                className="icon-action-btn"
                                title="Edit Student"
                                onClick={() => startStudentEdit(student)}
                              >
                                <AppIcon name="edit" />
                              </button>
                              <button
                                type="button"
                                className="icon-action-btn info"
                                title="Add Sibling"
                                onClick={() => startSiblingCreate(student)}
                              >
                                <AppIcon name="account" />
                              </button>
                              <button
                                type="button"
                                className="icon-action-btn warn"
                                title="Delete Student"
                                onClick={() => deleteStudent(student.id)}
                              >
                                <AppIcon name="trash" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="table-tools" style={{ marginTop: 10 }}>
                <button type="button" className="table-btn" onClick={() => setStudentPage((p) => Math.max(1, p - 1))} disabled={studentPage <= 1}>Prev</button>
                <span style={{ alignSelf: "center" }}>Page {Math.min(studentPage, studentTotalPages)} of {studentTotalPages}</span>
                <button type="button" className="table-btn" onClick={() => setStudentPage((p) => Math.min(studentTotalPages, p + 1))} disabled={studentPage >= studentTotalPages}>Next</button>
              </div>
            </section>
          )}

          {activeTab === "student-form" && (
            <section>
              <h2>{studentForm.id ? "Edit Student" : "Add Student Form"}</h2>
              <form className="admin-form" onSubmit={submitStudent}>
                <div className="form-field"><label>Student Name *</label><input value={studentForm.name} onChange={(e) => setStudentForm((p) => ({ ...p, name: e.target.value }))} required /></div>
                <div className="form-field"><label>Student Photo URL</label><input value={studentForm.photoUrl} onChange={(e) => setStudentForm((p) => ({ ...p, photoUrl: e.target.value }))} placeholder="https://..." /></div>
                <div className="form-field">
                  <label>Upload Student Photo</label>
                  <input type="file" accept="image/*" onChange={handleStudentPhotoFile} />
                </div>
                <div className="form-field">
                  <label>Preview</label>
                  {studentForm.photoUrl ? (
                    <img src={studentForm.photoUrl} alt="Student preview" className="profile-avatar" />
                  ) : (
                    <span className="dash-subtitle" style={{ padding: 0 }}>No image selected</span>
                  )}
                </div>
                <div className="form-field"><label>Class *</label><input value={studentForm.class} onChange={(e) => setStudentForm((p) => ({ ...p, class: e.target.value }))} required /></div>
                <div className="form-field">
                  <label>Section</label>
                  <select value={studentForm.section} onChange={(e) => setStudentForm((p) => ({ ...p, section: e.target.value }))}>
                    <option value="">Select Section</option>
                    {SECTION_OPTIONS.map((section) => <option key={section} value={section}>{section}</option>)}
                  </select>
                </div>
                <div className="form-field"><label>Roll Number</label><input value={studentForm.rollNo} onChange={(e) => setStudentForm((p) => ({ ...p, rollNo: e.target.value }))} /></div>
                <div className="form-field"><label>Admission Number</label><input value={studentForm.admissionNo} onChange={(e) => setStudentForm((p) => ({ ...p, admissionNo: e.target.value }))} /></div>
                <div className="form-field"><label>Academic Year</label><input value={studentForm.academicYear} onChange={(e) => setStudentForm((p) => ({ ...p, academicYear: e.target.value }))} placeholder="2025-2026" /></div>
                <div className="form-field"><label>Date Of Birth</label><input type="date" value={studentForm.dateOfBirth} onChange={(e) => setStudentForm((p) => ({ ...p, dateOfBirth: e.target.value }))} /></div>
                <div className="form-field"><label>Gender</label><select value={studentForm.gender} onChange={(e) => setStudentForm((p) => ({ ...p, gender: e.target.value }))}><option value="">Select</option><option value="male">Male</option><option value="female">Female</option><option value="other">Other</option></select></div>
                <div className="form-field"><label>Blood Group</label><input value={studentForm.bloodGroup} onChange={(e) => setStudentForm((p) => ({ ...p, bloodGroup: e.target.value }))} /></div>
                <div className="form-field"><label>Monthly Fee *</label><input type="number" min="0" value={studentForm.monthlyFee} onChange={(e) => setStudentForm((p) => ({ ...p, monthlyFee: e.target.value }))} required /></div>
                <div className="form-field"><label>Annual Fee</label><input type="number" min="0" value={studentForm.annualFee} onChange={(e) => setStudentForm((p) => ({ ...p, annualFee: e.target.value }))} /></div>
                <div className="form-field"><label>Admission Fee</label><input type="number" min="0" value={studentForm.admissionFee} onChange={(e) => setStudentForm((p) => ({ ...p, admissionFee: e.target.value }))} /></div>
                <div className="form-field">
                  <label>Transport Enabled</label>
                  <select value={studentForm.transportEnabled ? "yes" : "no"} onChange={(e) => setStudentForm((p) => ({ ...p, transportEnabled: e.target.value === "yes" }))}>
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                </div>
                {studentForm.transportEnabled && (
                  <>
                    <div className="form-field"><label>Transport Monthly Fee</label><input type="number" min="0" value={studentForm.transportMonthlyFee} onChange={(e) => setStudentForm((p) => ({ ...p, transportMonthlyFee: e.target.value }))} /></div>
                    <div className="form-field"><label>Route Name</label><input value={studentForm.transportRouteName} onChange={(e) => setStudentForm((p) => ({ ...p, transportRouteName: e.target.value }))} /></div>
                    <div className="form-field"><label>Pickup Point</label><input value={studentForm.transportPickupPoint} onChange={(e) => setStudentForm((p) => ({ ...p, transportPickupPoint: e.target.value }))} /></div>
                    <div className="form-field"><label>Drop Point</label><input value={studentForm.transportDropPoint} onChange={(e) => setStudentForm((p) => ({ ...p, transportDropPoint: e.target.value }))} /></div>
                    <div className="form-field"><label>Vehicle No</label><input value={studentForm.transportVehicleNo} onChange={(e) => setStudentForm((p) => ({ ...p, transportVehicleNo: e.target.value }))} /></div>
                  </>
                )}
                <div className="form-field"><label>Address</label><input value={studentForm.address} onChange={(e) => setStudentForm((p) => ({ ...p, address: e.target.value }))} /></div>
                <div className="form-field"><label>City</label><input value={studentForm.city} onChange={(e) => setStudentForm((p) => ({ ...p, city: e.target.value }))} /></div>
                <div className="form-field"><label>State</label><input value={studentForm.state} onChange={(e) => setStudentForm((p) => ({ ...p, state: e.target.value }))} /></div>
                <div className="form-field"><label>Country</label><input value={studentForm.country} onChange={(e) => setStudentForm((p) => ({ ...p, country: e.target.value }))} /></div>
                <div className="form-field"><label>Pincode</label><input value={studentForm.pincode} onChange={(e) => setStudentForm((p) => ({ ...p, pincode: e.target.value }))} /></div>
                <div className="form-field"><label>Emergency Contact Name</label><input value={studentForm.emergencyContactName} onChange={(e) => setStudentForm((p) => ({ ...p, emergencyContactName: e.target.value }))} /></div>
                <div className="form-field"><label>Emergency Contact Phone</label><input value={studentForm.emergencyContactPhone} onChange={(e) => setStudentForm((p) => ({ ...p, emergencyContactPhone: e.target.value }))} /></div>
                <div className="form-field"><label>Parent Name *</label><input value={studentForm.parentName} onChange={(e) => setStudentForm((p) => ({ ...p, parentName: e.target.value }))} required /></div>
                <div className="form-field"><label>Parent Email *</label><input type="email" value={studentForm.parentEmail} onChange={(e) => setStudentForm((p) => ({ ...p, parentEmail: e.target.value }))} required /></div>
                <div className="form-field"><label>Parent Phone</label><input value={studentForm.parentPhone} onChange={(e) => setStudentForm((p) => ({ ...p, parentPhone: e.target.value }))} /></div>
                <div className="form-field"><label>Parent Address</label><input value={studentForm.parentAddress} onChange={(e) => setStudentForm((p) => ({ ...p, parentAddress: e.target.value }))} /></div>
                <div className="form-field">
                  <label>{studentForm.id ? "Parent Password (required for new email)" : "Parent Password *"}</label>
                  <div className="password-wrap">
                    <input type={showPasswords.parent ? "text" : "password"} value={studentForm.parentPassword} onChange={(e) => setStudentForm((p) => ({ ...p, parentPassword: e.target.value }))} required={!studentForm.id} />
                    <button type="button" className="password-toggle" onClick={() => setShowPasswords((p) => ({ ...p, parent: !p.parent }))}>
                      <AppIcon name={showPasswords.parent ? "eyeOff" : "eye"} />
                    </button>
                  </div>
                </div>
                <div className="form-field"><label>Notes</label><input value={studentForm.notes} onChange={(e) => setStudentForm((p) => ({ ...p, notes: e.target.value }))} /></div>

                {studentForm.id && (
                  <div className="form-field" style={{ gridColumn: "span 2" }}>
                    <label>
                      <input type="checkbox" checked={studentForm.regenerateSchedule} onChange={(e) => setStudentForm((p) => ({ ...p, regenerateSchedule: e.target.checked }))} /> Regenerate full monthly schedule (resets all months to pending)
                    </label>
                  </div>
                )}

                <div className="admin-form-actions">
                  <button type="submit" disabled={savingStudent}>{savingStudent ? "Saving..." : (studentForm.id ? "Update Student" : "Save Student")}</button>
                  <button type="button" className="ghost" onClick={() => setStudentForm(initialStudentForm)}>Reset</button>
                </div>
              </form>
            </section>
          )}

          {activeTab === "student-attendance" && (
            <section>
              <div className="table-header-row list-header attendance-header-top">
                <div>
                  <h2 style={{ margin: 0 }}>Student Attendance</h2>
                  <div className="list-stat-chips">
                    <span className="stat-chip success">Present: {studentAttendanceTotals.present || 0}</span>
                    <span className="stat-chip danger">Absent: {studentAttendanceTotals.absent || 0}</span>
                    <span className="stat-chip neutral">Leave: {studentAttendanceTotals.leave || 0}</span>
                    <span className="stat-chip neutral">Not Marked: {studentAttendanceTotals.notMarked || 0}</span>
                  </div>
                </div>
              </div>
              <div className="table-header-row attendance-header-filters">
                <div className="table-tools">
                  <input type="date" value={studentAttendanceDate} onChange={(e) => setStudentAttendanceDate(e.target.value)} />
                  <input type="month" value={studentAttendanceMonth} onChange={(e) => setStudentAttendanceMonth(e.target.value)} />
                  <select value={`${studentAttendanceClass}__${studentAttendanceSection}`} onChange={(e) => {
                    const [className, section] = String(e.target.value).split("__");
                    setStudentAttendanceClass(className || "");
                    setStudentAttendanceSection(section || "");
                  }}>
                    <option value="__">All Classes</option>
                    {studentClassOptions.map((row) => (
                      <option key={`${row.className}__${row.section}`} value={`${row.className}__${row.section}`}>
                        {row.className} - {row.section || "NA"}
                      </option>
                    ))}
                  </select>
                  <input value={studentAttendanceSearch} onChange={(e) => setStudentAttendanceSearch(e.target.value)} placeholder="Search student" />
                </div>
              </div>
              <div className="table-header-row attendance-header-filters">
                <div className="table-tools">
                  <button
                    type="button"
                    className={studentAttendanceView === "daily" ? "table-btn" : "table-btn ghost"}
                    onClick={() => setStudentAttendanceView("daily")}
                  >
                    Daily Marking
                  </button>
                  <button
                    type="button"
                    className={studentAttendanceView === "monthly" ? "table-btn" : "table-btn ghost"}
                    onClick={() => setStudentAttendanceView("monthly")}
                  >
                    Monthly Summary
                  </button>
                </div>
              </div>
              <p className="dash-subtitle" style={{ padding: 0, marginBottom: 10 }}>
                Attendance is auto-saved instantly when you click `P`, `A`, or `L`.
              </p>

              {studentAttendanceView === "daily" && (
              <div className="dash-table-wrap list-table-card" style={{ marginBottom: 12 }}>
                <table className="dash-table fancy-table">
                  <thead>
                    <tr>
                      <th>Student</th>
                      <th>Class</th>
                      <th>Roll</th>
                      <th>Status</th>
                      <th>Mark</th>
                    </tr>
                  </thead>
                  <tbody>
                    {studentAttendanceLoading && <tr><td colSpan={5}>Loading attendance...</td></tr>}
                    {!studentAttendanceLoading && studentAttendanceRows.length === 0 && <tr><td colSpan={5}>No students found.</td></tr>}
                    {!studentAttendanceLoading && studentAttendanceRows.map((student) => (
                      <tr key={student.id}>
                        <td>
                          <div className="list-primary-cell">
                            {student.photoUrl ? (
                              <img src={student.photoUrl} alt={student.name} className="mini-avatar" />
                            ) : (
                              <span className="mini-avatar placeholder"><AppIcon name="child" size={12} /></span>
                            )}
                            <span>{student.name}</span>
                          </div>
                        </td>
                        <td>{student.class} - {student.section || "NA"}</td>
                        <td>{student.rollNo || "-"}</td>
                        <td>
                          <span className={student.attendanceStatus === "present" ? "status-pill paid" : student.attendanceStatus === "absent" ? "status-pill due" : student.attendanceStatus === "leave" ? "status-pill pending" : "status-pill pending"}>
                            {student.attendanceStatus === "not_marked" ? "Not Marked" : student.attendanceStatus}
                          </span>
                        </td>
                        <td>
                          <div className="icon-actions attendance-actions">
                            <button type="button" className={`mini-tag-btn ${student.attendanceStatus === "present" ? "active ok" : ""}`} disabled={markingStudentAttendanceId === String(student.id)} onClick={() => markStudentAttendance(student.id, "present")}>P</button>
                            <button type="button" className={`mini-tag-btn ${student.attendanceStatus === "absent" ? "active warn" : ""}`} disabled={markingStudentAttendanceId === String(student.id)} onClick={() => markStudentAttendance(student.id, "absent")}>A</button>
                            <button type="button" className={`mini-tag-btn ${student.attendanceStatus === "leave" ? "active" : ""}`} disabled={markingStudentAttendanceId === String(student.id)} onClick={() => markStudentAttendance(student.id, "leave")}>L</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              )}
              {studentAttendanceView === "daily" && (
                <div className="table-tools" style={{ marginBottom: 12 }}>
                  <button type="button" className="table-btn" onClick={() => setStudentAttendancePage((p) => Math.max(1, p - 1))} disabled={studentAttendancePagination.page <= 1}>Prev</button>
                  <span style={{ alignSelf: "center" }}>
                    Page {studentAttendancePagination.page} of {studentAttendancePagination.totalPages} | Records: {studentAttendancePagination.totalRecords}
                  </span>
                  <button type="button" className="table-btn" onClick={() => setStudentAttendancePage((p) => Math.min(studentAttendancePagination.totalPages, p + 1))} disabled={studentAttendancePagination.page >= studentAttendancePagination.totalPages}>Next</button>
                </div>
              )}

              {studentAttendanceView === "monthly" && (
              <div className="dash-table-wrap list-table-card">
                <table className="dash-table fancy-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Class</th>
                      <th>Roll</th>
                      <th>Present</th>
                      <th>Absent</th>
                      <th>Leave</th>
                      <th>Marked</th>
                      <th>Attendance %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {studentAttendanceMonthlyLoading && <tr><td colSpan={8}>Loading monthly summary...</td></tr>}
                    {!studentAttendanceMonthlyLoading && studentAttendanceMonthlyRows.length === 0 && <tr><td colSpan={8}>No monthly data found.</td></tr>}
                    {!studentAttendanceMonthlyLoading && studentAttendanceMonthlyRows.map((row) => (
                      <tr key={row.id}>
                        <td>{row.name}</td>
                        <td>{row.class} - {row.section || "NA"}</td>
                        <td>{row.rollNo || "-"}</td>
                        <td>{row.present}</td>
                        <td>{row.absent}</td>
                        <td>{row.leave}</td>
                        <td>{row.markedDays}</td>
                        <td>{row.attendancePercent}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              )}
              {studentAttendanceView === "monthly" && (
                <div className="table-tools" style={{ marginTop: 10 }}>
                  <button type="button" className="table-btn" onClick={() => setStudentAttendanceMonthlyPage((p) => Math.max(1, p - 1))} disabled={studentAttendanceMonthlyPagination.page <= 1}>Prev</button>
                  <span style={{ alignSelf: "center" }}>
                    Page {studentAttendanceMonthlyPagination.page} of {studentAttendanceMonthlyPagination.totalPages} | Records: {studentAttendanceMonthlyPagination.totalRecords}
                  </span>
                  <button type="button" className="table-btn" onClick={() => setStudentAttendanceMonthlyPage((p) => Math.min(studentAttendanceMonthlyPagination.totalPages, p + 1))} disabled={studentAttendanceMonthlyPagination.page >= studentAttendanceMonthlyPagination.totalPages}>Next</button>
                </div>
              )}
            </section>
          )}

          {activeTab === "student-promotion" && (
            <section>
              <div className="table-header-row list-header">
                <div>
                  <h2 style={{ margin: 0 }}>Student Promotion</h2>
                  <p className="dash-breadcrumb" style={{ margin: "6px 0 0" }}>
                    Promote students to next class/year and regenerate monthly fee schedule in one action.
                  </p>
                </div>
              </div>

              <div className="dash-panel" style={{ marginBottom: 12 }}>
                <div className="admin-form">
                  <div className="form-field">
                    <label>From Class *</label>
                    <select
                      value={promotionFilter.fromClass}
                      onChange={(e) => {
                        const fromClass = e.target.value;
                        setPromotionFilter((prev) => ({ ...prev, fromClass, fromSection: "" }));
                      }}
                    >
                      <option value="">Select Class</option>
                      {promotionClassOptions.map((className) => (
                        <option key={className} value={className}>{className}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-field">
                    <label>From Section</label>
                    <select
                      value={promotionFilter.fromSection}
                      onChange={(e) => setPromotionFilter((prev) => ({ ...prev, fromSection: e.target.value }))}
                    >
                      <option value="">All Sections</option>
                      {promotionSectionOptions.map((section) => (
                        <option key={section} value={section}>{section}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-field">
                    <label>Current Academic Year</label>
                    <select
                      value={promotionFilter.currentYear}
                      onChange={(e) => setPromotionFilter((prev) => ({ ...prev, currentYear: e.target.value }))}
                    >
                      <option value="">All Years</option>
                      {promotionYearOptions.map((year) => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-field">
                    <label>Next Academic Year *</label>
                    <input
                      value={promotionFilter.nextYear}
                      onChange={(e) => setPromotionFilter((prev) => ({ ...prev, nextYear: e.target.value }))}
                      placeholder="2026-27"
                    />
                  </div>
                  <div className="form-field">
                    <label>Reset Roll Numbers</label>
                    <select
                      value={promotionFilter.resetRollNumbers ? "yes" : "no"}
                      onChange={(e) => setPromotionFilter((prev) => ({ ...prev, resetRollNumbers: e.target.value === "yes" }))}
                    >
                      <option value="yes">Yes (Auto Sequence)</option>
                      <option value="no">No (Manual)</option>
                    </select>
                  </div>
                  <div className="form-field">
                    <label>Start Roll No</label>
                    <input
                      type="number"
                      min="1"
                      value={promotionFilter.startRollNo}
                      onChange={(e) => setPromotionFilter((prev) => ({ ...prev, startRollNo: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="table-tools" style={{ marginTop: 10 }}>
                  <button type="button" className="table-btn" onClick={fetchPromotionPreview} disabled={promotionLoading}>
                    {promotionLoading ? "Loading..." : "Load Promotion Preview"}
                  </button>
                  <button
                    type="button"
                    className="table-btn ghost"
                    onClick={() => setPromotionRows((prev) => prev.map((row) => ({ ...row, promote: true })))}
                    disabled={promotionRows.length === 0}
                  >
                    Select All
                  </button>
                  <button
                    type="button"
                    className="table-btn ghost"
                    onClick={() => setPromotionRows((prev) => prev.map((row) => ({ ...row, promote: false })))}
                    disabled={promotionRows.length === 0}
                  >
                    Unselect All
                  </button>
                  <button type="button" className="table-btn" onClick={applyBulkPromotion} disabled={promotionSaving || promotionRows.length === 0}>
                    {promotionSaving ? "Applying..." : "Apply Bulk Promotion"}
                  </button>
                </div>
              </div>

              <div className="dash-table-wrap list-table-card">
                <table className="dash-table fancy-table">
                  <thead>
                    <tr>
                      <th>Promote</th>
                      <th>Student</th>
                      <th>Current</th>
                      <th>Next Class</th>
                      <th>Next Section</th>
                      <th>Next Roll</th>
                      <th>Monthly Fee</th>
                      <th>Transport Fee</th>
                    </tr>
                  </thead>
                  <tbody>
                    {promotionRows.length === 0 && <tr><td colSpan={8}>Load preview to see students.</td></tr>}
                    {promotionRows.map((row) => (
                      <tr key={row.studentId}>
                        <td>
                          <input
                            type="checkbox"
                            checked={!!row.promote}
                            onChange={(e) => setPromotionRows((prev) => prev.map((item) => item.studentId === row.studentId ? { ...item, promote: e.target.checked } : item))}
                          />
                        </td>
                        <td>
                          <strong>{row.name}</strong>
                          <div className="dash-subtitle" style={{ padding: 0 }}>{row.parentName || "-"}</div>
                        </td>
                        <td>
                          {row.currentClass}-{row.currentSection || "NA"} | Roll {row.currentRollNo || "-"}
                          <div className="dash-subtitle" style={{ padding: 0 }}>{row.currentYear || "-"}</div>
                        </td>
                        <td>
                          <input
                            value={row.nextClass}
                            onChange={(e) => setPromotionRows((prev) => prev.map((item) => item.studentId === row.studentId ? { ...item, nextClass: e.target.value } : item))}
                          />
                        </td>
                        <td>
                          <select
                            value={row.nextSection || ""}
                            onChange={(e) => setPromotionRows((prev) => prev.map((item) => item.studentId === row.studentId ? { ...item, nextSection: e.target.value } : item))}
                          >
                            <option value="">NA</option>
                            {SECTION_OPTIONS.map((section) => <option key={section} value={section}>{section}</option>)}
                          </select>
                        </td>
                        <td>
                          <input
                            type="number"
                            min="1"
                            value={row.nextRollNo}
                            disabled={promotionFilter.resetRollNumbers}
                            onChange={(e) => setPromotionRows((prev) => prev.map((item) => item.studentId === row.studentId ? { ...item, nextRollNo: e.target.value } : item))}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            min="0"
                            value={row.monthlyFee}
                            onChange={(e) => setPromotionRows((prev) => prev.map((item) => item.studentId === row.studentId ? { ...item, monthlyFee: e.target.value } : item))}
                          />
                        </td>
                        <td>
                          {row.transportEnabled ? (
                            <input
                              type="number"
                              min="0"
                              value={row.transportMonthlyFee}
                              onChange={(e) => setPromotionRows((prev) => prev.map((item) => item.studentId === row.studentId ? { ...item, transportMonthlyFee: e.target.value } : item))}
                            />
                          ) : (
                            <span>-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {promotionSummary && (
                <div className="dash-panel" style={{ marginTop: 12 }}>
                  <h3 style={{ marginTop: 0 }}>Promotion Result</h3>
                  <p className="dash-breadcrumb" style={{ margin: "0 0 10px" }}>
                    Promoted: {promotionSummary.promotedCount || 0} | Failed: {promotionSummary.failedCount || 0}
                  </p>
                  {(promotionSummary.failed || []).length > 0 && (
                    <div className="dash-table-wrap">
                      <table className="dash-table">
                        <thead><tr><th>Student</th><th>Reason</th></tr></thead>
                        <tbody>
                          {(promotionSummary.failed || []).map((row, idx) => (
                            <tr key={`${row.studentId || idx}`}>
                              <td>{row.name || row.studentId}</td>
                              <td>{row.reason || "Unknown error"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </section>
          )}

          {activeTab === "transfer-certificate" && (
            <section>
              <div className="dashboard-lower-grid">
                <div className="dash-panel">
                  <h3>Generate Transfer Certificate</h3>
                  <form className="admin-form" onSubmit={submitTransferCertificate}>
                    <div className="form-field">
                      <label>Student *</label>
                      <select
                        value={tcForm.studentId}
                        onChange={(e) => setTcForm((prev) => ({ ...prev, studentId: e.target.value }))}
                        required
                      >
                        <option value="">Select Student</option>
                        {students.filter((student) => student.isActive).map((student) => (
                          <option key={student.id} value={student.id}>
                            {student.name} ({student.class}-{student.section || "NA"}) Roll {student.rollNo || "-"}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-field">
                      <label>Issue Date *</label>
                      <input
                        type="date"
                        value={tcForm.issueDate}
                        onChange={(e) => setTcForm((prev) => ({ ...prev, issueDate: e.target.value }))}
                        required
                      />
                    </div>
                    <div className="form-field">
                      <label>Last Attendance Date</label>
                      <input
                        type="date"
                        value={tcForm.lastAttendanceDate}
                        onChange={(e) => setTcForm((prev) => ({ ...prev, lastAttendanceDate: e.target.value }))}
                      />
                    </div>
                    <div className="form-field">
                      <label>Conduct</label>
                      <input
                        value={tcForm.conduct}
                        onChange={(e) => setTcForm((prev) => ({ ...prev, conduct: e.target.value }))}
                        placeholder="Good"
                      />
                    </div>
                    <div className="form-field" style={{ gridColumn: "span 2" }}>
                      <label>Reason *</label>
                      <input
                        value={tcForm.reason}
                        onChange={(e) => setTcForm((prev) => ({ ...prev, reason: e.target.value }))}
                        placeholder="Family relocation / admission in another school"
                        required
                      />
                    </div>
                    <div className="form-field" style={{ gridColumn: "span 2" }}>
                      <label>Destination School</label>
                      <input
                        value={tcForm.destinationSchool}
                        onChange={(e) => setTcForm((prev) => ({ ...prev, destinationSchool: e.target.value }))}
                        placeholder="School name (optional)"
                      />
                    </div>
                    <div className="form-field" style={{ gridColumn: "span 2" }}>
                      <label>Remarks</label>
                      <input
                        value={tcForm.remarks}
                        onChange={(e) => setTcForm((prev) => ({ ...prev, remarks: e.target.value }))}
                        placeholder="Any additional remarks"
                      />
                    </div>
                    <div className="form-field" style={{ gridColumn: "span 2" }}>
                      <label>
                        <input
                          type="checkbox"
                          checked={tcForm.deactivateStudent}
                          onChange={(e) => setTcForm((prev) => ({ ...prev, deactivateStudent: e.target.checked }))}
                        /> Deactivate student after TC generation
                      </label>
                    </div>
                    <div className="admin-form-actions">
                      <button type="submit" disabled={savingTc}>{savingTc ? "Generating..." : "Generate TC"}</button>
                      <button
                        type="button"
                        className="ghost"
                        onClick={() => setTcForm({ ...initialTcForm, issueDate: getTodayInput() })}
                      >
                        Reset
                      </button>
                    </div>
                  </form>
                </div>

                <div className="dash-panel">
                  <h3>TC Records</h3>
                  <div className="table-tools" style={{ marginBottom: 10 }}>
                    <select value={tcStatusFilter} onChange={(e) => { setTcStatusFilter(e.target.value); setTcPage(1); }}>
                      <option value="">All Status</option>
                      <option value="issued">Issued</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                    <input
                      value={tcSearch}
                      onChange={(e) => { setTcSearch(e.target.value); setTcPage(1); }}
                      placeholder="Search TC No / Student / Admission No"
                    />
                  </div>
                  <p className="dash-subtitle" style={{ padding: 0 }}>
                    Generate, preview, print, and manage Transfer Certificates.
                  </p>
                </div>
              </div>

              <div className="dash-table-wrap list-table-card" style={{ marginTop: 12 }}>
                <table className="dash-table fancy-table">
                  <thead>
                    <tr>
                      <th>TC No</th>
                      <th>Student</th>
                      <th>Class</th>
                      <th>Issue Date</th>
                      <th>Status</th>
                      <th>Reason</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tcRows.length === 0 && <tr><td colSpan={7}>No transfer certificates found.</td></tr>}
                    {tcRows.map((row) => (
                      <tr key={row.id}>
                        <td>{row.tcNo || "-"}</td>
                        <td>{row.student?.name || "-"}</td>
                        <td>{row.student?.class || "-"}-{row.student?.section || "NA"}</td>
                        <td>{row.issueDate ? new Date(row.issueDate).toLocaleDateString() : "-"}</td>
                        <td>
                          <span className={row.status === "issued" ? "status-pill paid" : "status-pill due"}>
                            {row.status || "-"}
                          </span>
                        </td>
                        <td>{row.reason || "-"}</td>
                        <td>
                          <div className="icon-actions">
                            <button type="button" className="icon-action-btn" title="Preview" onClick={() => openTcPreview(row.id)}>
                              <AppIcon name="eye" />
                            </button>
                            <button
                              type="button"
                              className="icon-action-btn"
                              title="Print"
                              onClick={() => printTransferCertificate(row)}
                            >
                              <AppIcon name="assignment" />
                            </button>
                            {row.status === "issued" && (
                              <button
                                type="button"
                                className="icon-action-btn warn"
                                title="Cancel TC"
                                onClick={() => setTcCancelModal({ open: true, certificateId: row.id, tcNo: row.tcNo || "", reason: "" })}
                              >
                                <AppIcon name="close" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="table-tools" style={{ marginTop: 10 }}>
                <button
                  type="button"
                  className="table-btn"
                  onClick={() => setTcPage((p) => Math.max(1, p - 1))}
                  disabled={tcPagination.page <= 1}
                >
                  Prev
                </button>
                <span style={{ alignSelf: "center" }}>
                  Page {tcPagination.page} of {tcPagination.totalPages} | Records: {tcPagination.totalRecords}
                </span>
                <button
                  type="button"
                  className="table-btn"
                  onClick={() => setTcPage((p) => Math.min(tcPagination.totalPages || 1, p + 1))}
                  disabled={tcPagination.page >= tcPagination.totalPages}
                >
                  Next
                </button>
              </div>
            </section>
          )}

          {activeTab === "exams" && (
            <section>
              <div className="dashboard-lower-grid">
                <div className="dash-panel">
                  <h3>Create Exam</h3>
                  <form className="admin-form" onSubmit={submitExam}>
                    <div className="form-field"><label>Exam Title *</label><input value={examForm.title} onChange={(e) => setExamForm((p) => ({ ...p, title: e.target.value }))} required /></div>
                    <div className="form-field"><label>Subject</label><input value={examForm.subject} onChange={(e) => setExamForm((p) => ({ ...p, subject: e.target.value }))} /></div>
                    <div className="form-field"><label>Class *</label><input value={examForm.className} onChange={(e) => setExamForm((p) => ({ ...p, className: e.target.value }))} required /></div>
                    <div className="form-field">
                      <label>Section</label>
                      <select value={examForm.section} onChange={(e) => setExamForm((p) => ({ ...p, section: e.target.value }))}>
                        <option value="">Select Section</option>
                        {SECTION_OPTIONS.map((section) => <option key={section} value={section}>{section}</option>)}
                      </select>
                    </div>
                    <div className="form-field"><label>Max Marks</label><input type="number" min="1" value={examForm.maxMarks} onChange={(e) => setExamForm((p) => ({ ...p, maxMarks: e.target.value }))} /></div>
                    <div className="form-field"><label>Exam Date</label><input type="date" value={examForm.examDate} onChange={(e) => setExamForm((p) => ({ ...p, examDate: e.target.value }))} /></div>
                    <div className="form-field"><label>Academic Year</label><input value={examForm.academicYear} onChange={(e) => setExamForm((p) => ({ ...p, academicYear: e.target.value }))} placeholder="2026-2027" /></div>
                    <div className="admin-form-actions">
                      <button type="submit" disabled={savingExam}>{savingExam ? "Saving..." : "Create Exam"}</button>
                      <button type="button" className="ghost" onClick={() => setExamForm(initialExamForm)}>Reset</button>
                    </div>
                  </form>
                </div>
                <div className="dash-panel">
                  <h3>Exam List</h3>
                  <div className="table-tools" style={{ marginBottom: 10 }}>
                    <select value={selectedExamId} onChange={(e) => setSelectedExamId(e.target.value)}>
                      <option value="">Select Exam</option>
                      {exams.map((exam) => (
                        <option key={exam.id} value={exam.id}>
                          {exam.title} - {exam.subject || "General"} ({exam.className}-{exam.section || "NA"})
                        </option>
                      ))}
                    </select>
                  </div>
                  {selectedExamId && (
                    <button type="button" className="table-btn" onClick={saveExamMarks} disabled={savingExamMarks}>
                      {savingExamMarks ? "Saving Marks..." : "Save Marks"}
                    </button>
                  )}
                </div>
              </div>

              <div className="dash-table-wrap list-table-card" style={{ marginTop: 12 }}>
                <table className="dash-table fancy-table">
                  <thead>
                    <tr>
                      <th>Student</th>
                      <th>Roll</th>
                      <th>Marks</th>
                      <th>Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!selectedExamId && <tr><td colSpan={4}>Select an exam to enter marks.</td></tr>}
                    {selectedExamId && examMarksRows.length === 0 && <tr><td colSpan={4}>No students found for selected exam class/section.</td></tr>}
                    {selectedExamId && examMarksRows.map((row) => (
                      <tr key={row.studentId}>
                        <td>{row.name}</td>
                        <td>{row.rollNo || "-"}</td>
                        <td>
                          <input
                            type="number"
                            min="0"
                            value={row.marks}
                            onChange={(e) => setExamMarksRows((prev) => prev.map((item) => (item.studentId === row.studentId ? { ...item, marks: e.target.value } : item)))}
                          />
                        </td>
                        <td>
                          <input
                            value={row.remarks}
                            onChange={(e) => setExamMarksRows((prev) => prev.map((item) => (item.studentId === row.studentId ? { ...item, remarks: e.target.value } : item)))}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {activeTab === "hall-tickets" && (
            <section>
              <div className="dashboard-lower-grid">
                <div className="dash-panel">
                  <h3>Create / Update Hall Ticket</h3>
                  <form className="admin-form" onSubmit={saveHallTicket}>
                    <div className="form-field">
                      <label>Exam *</label>
                      <select value={hallTicketForm.examId} onChange={(e) => setHallTicketForm((p) => ({ ...p, examId: e.target.value, studentId: "" }))} required>
                        <option value="">Select Exam</option>
                        {hallTicketExamOptions.map((exam) => (
                          <option key={exam.id} value={exam.id}>
                            {exam.title} - {exam.subject || "General"} ({exam.className}-{exam.section || "NA"})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-field">
                      <label>Student *</label>
                      <select value={hallTicketForm.studentId} onChange={(e) => setHallTicketForm((p) => ({ ...p, studentId: e.target.value }))} required>
                        <option value="">Select Student</option>
                        {hallTicketStudentOptions.map((student) => (
                          <option key={student.id} value={student.id}>
                            {student.name} ({student.class}-{student.section || "NA"}) Roll {student.rollNo || "-"}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-field"><label>Seat Number</label><input value={hallTicketForm.seatNumber} onChange={(e) => setHallTicketForm((p) => ({ ...p, seatNumber: e.target.value }))} /></div>
                    <div className="form-field"><label>Room</label><input value={hallTicketForm.room} onChange={(e) => setHallTicketForm((p) => ({ ...p, room: e.target.value }))} /></div>
                    <div className="form-field"><label>Center Name</label><input value={hallTicketForm.centerName} onChange={(e) => setHallTicketForm((p) => ({ ...p, centerName: e.target.value }))} /></div>
                    <div className="form-field" style={{ gridColumn: "span 2" }}>
                      <label>Instructions</label>
                      <input value={hallTicketForm.instructions} onChange={(e) => setHallTicketForm((p) => ({ ...p, instructions: e.target.value }))} />
                    </div>
                    <div className="admin-form-actions">
                      <button type="submit" disabled={savingHallTicket}>{savingHallTicket ? "Saving..." : "Save Hall Ticket"}</button>
                      <button type="button" className="ghost" onClick={() => setHallTicketForm(initialHallTicketForm)}>Reset</button>
                    </div>
                  </form>
                </div>
                <div className="dash-panel">
                  <h3>Filter Hall Tickets</h3>
                  <div className="table-tools" style={{ marginBottom: 10 }}>
                    <select value={hallTicketExamFilter} onChange={(e) => setHallTicketExamFilter(e.target.value)}>
                      <option value="">All Exams</option>
                      {hallTicketExamOptions.map((exam) => (
                        <option key={exam.id} value={exam.id}>
                          {exam.title} - {exam.className}-{exam.section || "NA"}
                        </option>
                      ))}
                    </select>
                  </div>
                  <p className="dash-subtitle" style={{ padding: 0 }}>
                    Create hall ticket for each student-exam pair. Parents will see these in parent portal.
                  </p>
                </div>
              </div>
              <div className="dash-table-wrap list-table-card" style={{ marginTop: 12 }}>
                <table className="dash-table fancy-table">
                  <thead>
                    <tr><th>Exam</th><th>Student</th><th>Date</th><th>Seat</th><th>Room</th><th>Center</th></tr>
                  </thead>
                  <tbody>
                    {hallTicketRows.length === 0 && <tr><td colSpan={6}>No hall-ticket rows found.</td></tr>}
                    {hallTicketRows.map((row, index) => (
                      <tr key={`${row.exam?.id || index}-${row.student?.id || index}`}>
                        <td>{row.exam?.title || "-"} ({row.exam?.subject || "General"})</td>
                        <td>{row.student?.name || "-"}</td>
                        <td>{row.exam?.examDate ? new Date(row.exam.examDate).toLocaleDateString() : "-"}</td>
                        <td>{row.hallTicket?.seatNumber || "-"}</td>
                        <td>{row.hallTicket?.room || "-"}</td>
                        <td>{row.hallTicket?.centerName || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {activeTab === "teachers" && (
            <section>
              <div className="table-header-row list-header">
                <div>
                  <h2 style={{ margin: 0 }}>All Teachers</h2>
                  <div className="list-stat-chips">
                    <span className="stat-chip neutral">Total: {teacherStats.total}</span>
                    <span className="stat-chip success">Active: {teacherStats.active}</span>
                    <span className="stat-chip danger">Inactive: {teacherStats.inactive}</span>
                  </div>
                </div>
                <div className="table-tools">
                  <input
                    value={teacherSearch}
                    onChange={(e) => setTeacherSearch(e.target.value)}
                    placeholder="Search name, employee ID, subject"
                  />
                  <button className="table-btn" onClick={startTeacherCreate}>Add Teacher</button>
                </div>
              </div>

              <div className="dash-table-wrap list-table-card">
                <table className="dash-table fancy-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Employee ID</th>
                      <th>Contact</th>
                      <th>Subjects</th>
                      <th>Class Teacher Of</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTeachers.length === 0 && <tr><td colSpan={7}>No teachers found.</td></tr>}
                    {pagedTeachers.map((teacher) => (
                      <tr key={teacher.id}>
                        <td>
                          <div className="list-primary-cell">
                            {teacher.photoUrl ? (
                              <img src={teacher.photoUrl} alt={teacher.name} className="mini-avatar" />
                            ) : (
                              <span className="mini-avatar placeholder"><AppIcon name="teachers" size={12} /></span>
                            )}
                            <span>{teacher.name}</span>
                          </div>
                        </td>
                        <td>{teacher.employeeId || "-"}</td>
                        <td>{teacher.phone || teacher.email || "-"}</td>
                        <td>{(teacher.subjects || []).join(", ") || "-"}</td>
                        <td>{teacher.classTeacherOf || "-"}</td>
                        <td>
                          <span className={teacher.isActive ? "status-pill paid" : "status-pill due"}>
                            {teacher.isActive ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td>
                          <div className="icon-actions">
                            <button
                              type="button"
                              className="icon-action-btn"
                              title="Edit Teacher"
                              onClick={() => startTeacherEdit(teacher)}
                            >
                              <AppIcon name="edit" />
                            </button>
                            <button
                              type="button"
                              className="icon-action-btn warn"
                              title="Delete Teacher"
                              onClick={() => deleteTeacher(teacher.id)}
                            >
                              <AppIcon name="trash" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="table-tools" style={{ marginTop: 10 }}>
                <button type="button" className="table-btn" onClick={() => setTeacherPage((p) => Math.max(1, p - 1))} disabled={teacherPage <= 1}>Prev</button>
                <span style={{ alignSelf: "center" }}>Page {Math.min(teacherPage, teacherTotalPages)} of {teacherTotalPages}</span>
                <button type="button" className="table-btn" onClick={() => setTeacherPage((p) => Math.min(teacherTotalPages, p + 1))} disabled={teacherPage >= teacherTotalPages}>Next</button>
              </div>
            </section>
          )}

          {activeTab === "teacher-attendance" && (
            <section>
              <div className="table-header-row list-header attendance-header-top">
                <div>
                  <h2 style={{ margin: 0 }}>Teacher Attendance & Salary</h2>
                  <div className="list-stat-chips">
                    <span className="stat-chip neutral">Teachers: {attendanceData.totals?.totalTeachers || 0}</span>
                    <span className="stat-chip success">Present: {attendanceData.totals?.presentDays || 0}</span>
                    <span className="stat-chip danger">Absent: {attendanceData.totals?.absentDays || 0}</span>
                    <span className="stat-chip neutral">Payout: {money(attendanceData.totals?.estimatedPayout || 0)}</span>
                    <span className="stat-chip neutral">Working Days: {attendanceData.workingDays || 0}</span>
                  </div>
                </div>
              </div>

              <div className="table-header-row attendance-header-filters">
                <div className="table-tools">
                  <input
                    type="month"
                    value={attendanceMonth}
                    onChange={(e) => {
                      const nextMonth = e.target.value;
                      setAttendanceMonth(nextMonth);
                      if (nextMonth && !attendanceDate.startsWith(nextMonth)) {
                        setAttendanceDate(`${nextMonth}-01`);
                      }
                    }}
                  />
                  <input type="date" value={attendanceDate} onChange={(e) => setAttendanceDate(e.target.value)} />
                  <input
                    value={attendanceSearch}
                    onChange={(e) => setAttendanceSearch(e.target.value)}
                    placeholder="Search teacher name, ID, email"
                  />
                </div>
              </div>

              {isAttendanceDateSunday && (
                <p className="dash-error" style={{ marginTop: 0, marginBottom: 12 }}>
                  Selected date is Sunday. Attendance marking is disabled.
                </p>
              )}

              <div className="dash-table-wrap list-table-card">
                <table className="dash-table fancy-table">
                  <thead>
                    <tr>
                      <th>Teacher</th>
                      <th>Employee ID</th>
                      <th>Monthly Salary</th>
                      <th>Selected Day</th>
                      <th>Mark Attendance</th>
                      <th>Month Summary</th>
                      <th>Estimated Salary</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!loadingAttendance && attendanceTeachers.length === 0 && <tr><td colSpan={7}>No teachers found.</td></tr>}
                    {loadingAttendance && <tr><td colSpan={7}>Loading attendance...</td></tr>}
                    {!loadingAttendance && attendanceTeachers.map((teacher) => {
                      const currentStatus = getAttendanceStatus(teacher, attendanceDate);
                      return (
                        <tr key={teacher.id}>
                          <td>
                            <div className="list-primary-cell">
                              {teacher.photoUrl ? (
                                <img src={teacher.photoUrl} alt={teacher.name} className="mini-avatar" />
                              ) : (
                                <span className="mini-avatar placeholder"><AppIcon name="teachers" size={12} /></span>
                              )}
                              <span>{teacher.name}</span>
                            </div>
                          </td>
                          <td>{teacher.employeeId || "-"}</td>
                          <td>{money(teacher.monthlySalary || 0)}</td>
                          <td>
                            <span className={isAttendanceDateSunday ? "status-pill pending" : currentStatus === "present" ? "status-pill paid" : currentStatus === "absent" ? "status-pill due" : "status-pill pending"}>
                              {isAttendanceDateSunday ? "Sunday / Holiday" : (currentStatus || "Not Marked")}
                            </span>
                          </td>
                          <td>
                            <div className="icon-actions attendance-actions">
                              {[
                                { key: "present", label: "P", className: "ok" },
                                { key: "absent", label: "A", className: "warn" },
                                { key: "halfday", label: "H", className: "" },
                                { key: "leave", label: "L", className: "" }
                              ].map((item) => (
                                <button
                                  key={item.key}
                                  type="button"
                                  className={`mini-tag-btn ${currentStatus === item.key ? "active" : ""} ${item.className}`.trim()}
                                  onClick={() => markTeacherAttendance(teacher, item.key)}
                                  disabled={isAttendanceDateSunday || markingAttendanceId === `${teacher.id}:${item.key}`}
                                  title={`Mark ${item.key}`}
                                >
                                  {markingAttendanceId === `${teacher.id}:${item.key}` ? "..." : item.label}
                                </button>
                              ))}
                            </div>
                          </td>
                          <td>
                            <small>
                              P: {teacher.salarySummary?.presentDays || 0} | A: {teacher.salarySummary?.absentDays || 0} | H: {teacher.salarySummary?.halfDays || 0} | L: {teacher.salarySummary?.leaveDays || 0}
                            </small>
                          </td>
                          <td><strong>{money(teacher.salarySummary?.payableSalary || 0)}</strong></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="dash-subtitle" style={{ paddingLeft: 2, paddingTop: 10 }}>
                Salary formula: Monthly Salary x (Present + Leave + 0.5 x Halfday) / {attendanceData.workingDays || 0} working days (Sundays excluded).
              </p>
            </section>
          )}

          {activeTab === "leave-requests" && (
            <section>
              <div className="table-header-row list-header">
                <div>
                  <h2 style={{ margin: 0 }}>Teacher Leave Requests</h2>
                  <p className="dash-subtitle" style={{ padding: 0 }}>Review and approve/reject teacher leaves.</p>
                </div>
                <div className="table-tools">
                  <select value={leaveStatusFilter} onChange={(e) => setLeaveStatusFilter(e.target.value)}>
                    <option value="">All</option>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
              </div>
              <div className="dash-table-wrap list-table-card">
                <table className="dash-table fancy-table">
                  <thead>
                    <tr><th>Teacher</th><th>Type</th><th>From</th><th>To</th><th>Reason</th><th>Status</th><th>Action</th></tr>
                  </thead>
                  <tbody>
                    {leaveRequests.length === 0 && <tr><td colSpan={7}>No leave requests found.</td></tr>}
                    {leaveRequests.map((row) => (
                      <tr key={row._id}>
                        <td>{row.requester?.name || row.requester?.email || "-"}</td>
                        <td>{row.leaveType || "-"}</td>
                        <td>{row.startDate ? new Date(row.startDate).toLocaleDateString() : "-"}</td>
                        <td>{row.endDate ? new Date(row.endDate).toLocaleDateString() : "-"}</td>
                        <td>{row.reason || "-"}</td>
                        <td><span className={row.status === "approved" ? "status-pill paid" : row.status === "rejected" ? "status-pill due" : "status-pill pending"}>{row.status}</span></td>
                        <td>
                          {row.status === "pending" ? (
                            <div className="icon-actions">
                              <button type="button" className="icon-action-btn success" onClick={() => reviewLeaveRequest(row._id, "approved")} disabled={reviewingLeaveId === String(row._id)} title="Approve">
                                <AppIcon name="check" />
                              </button>
                              <button type="button" className="icon-action-btn warn" onClick={() => reviewLeaveRequest(row._id, "rejected")} disabled={reviewingLeaveId === String(row._id)} title="Reject">
                                <AppIcon name="close" />
                              </button>
                            </div>
                          ) : (
                            <span>-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {activeTab === "teacher-form" && (
            <section>
              <h2>{teacherForm.id ? "Edit Teacher" : "Add Teacher Form"}</h2>
              <form className="admin-form" onSubmit={submitTeacher}>
                <div className="form-field"><label>Teacher Name *</label><input value={teacherForm.name} onChange={(e) => setTeacherForm((p) => ({ ...p, name: e.target.value }))} required /></div>
                <div className="form-field"><label>Teacher Photo URL</label><input value={teacherForm.photoUrl} onChange={(e) => setTeacherForm((p) => ({ ...p, photoUrl: e.target.value }))} placeholder="https://..." /></div>
                <div className="form-field">
                  <label>Upload Teacher Photo</label>
                  <input type="file" accept="image/*" onChange={handleTeacherPhotoFile} />
                </div>
                <div className="form-field">
                  <label>Preview</label>
                  {teacherForm.photoUrl ? (
                    <img src={teacherForm.photoUrl} alt="Teacher preview" className="profile-avatar" />
                  ) : (
                    <span className="dash-subtitle" style={{ padding: 0 }}>No image selected</span>
                  )}
                </div>
                <div className="form-field"><label>Employee ID</label><input value={teacherForm.employeeId} onChange={(e) => setTeacherForm((p) => ({ ...p, employeeId: e.target.value }))} /></div>
                <div className="form-field"><label>Email</label><input type="email" value={teacherForm.email} onChange={(e) => setTeacherForm((p) => ({ ...p, email: e.target.value }))} /></div>
                <div className="form-field"><label>Phone</label><input value={teacherForm.phone} onChange={(e) => setTeacherForm((p) => ({ ...p, phone: e.target.value }))} /></div>
                <div className="form-field"><label>Gender</label><select value={teacherForm.gender} onChange={(e) => setTeacherForm((p) => ({ ...p, gender: e.target.value }))}><option value="">Select</option><option value="male">Male</option><option value="female">Female</option><option value="other">Other</option></select></div>
                <div className="form-field"><label>Date Of Birth</label><input type="date" value={teacherForm.dateOfBirth} onChange={(e) => setTeacherForm((p) => ({ ...p, dateOfBirth: e.target.value }))} /></div>
                <div className="form-field"><label>Qualification</label><input value={teacherForm.qualification} onChange={(e) => setTeacherForm((p) => ({ ...p, qualification: e.target.value }))} /></div>
                <div className="form-field"><label>Experience (years)</label><input type="number" min="0" value={teacherForm.experienceYears} onChange={(e) => setTeacherForm((p) => ({ ...p, experienceYears: e.target.value }))} /></div>
                <div className="form-field"><label>Monthly Salary (INR)</label><input type="number" min="0" value={teacherForm.monthlySalary} onChange={(e) => setTeacherForm((p) => ({ ...p, monthlySalary: e.target.value }))} /></div>
                <div className="form-field"><label>Subjects (comma separated)</label><input value={teacherForm.subjects} onChange={(e) => setTeacherForm((p) => ({ ...p, subjects: e.target.value }))} placeholder="Math, Science" /></div>
                <div className="form-field">
                  <label>Class Teacher Of (Class)</label>
                  <input value={teacherForm.classTeacherClass} onChange={(e) => setTeacherForm((p) => ({ ...p, classTeacherClass: e.target.value }))} placeholder="8" />
                </div>
                <div className="form-field">
                  <label>Class Teacher Of (Section)</label>
                  <select value={teacherForm.classTeacherSection} onChange={(e) => setTeacherForm((p) => ({ ...p, classTeacherSection: e.target.value }))}>
                    <option value="">Select Section</option>
                    {SECTION_OPTIONS.map((section) => <option key={section} value={section}>{section}</option>)}
                  </select>
                </div>
                <div className="form-field"><label>Address</label><input value={teacherForm.address} onChange={(e) => setTeacherForm((p) => ({ ...p, address: e.target.value }))} /></div>
                <div className="form-field"><label>City</label><input value={teacherForm.city} onChange={(e) => setTeacherForm((p) => ({ ...p, city: e.target.value }))} /></div>
                <div className="form-field"><label>State</label><input value={teacherForm.state} onChange={(e) => setTeacherForm((p) => ({ ...p, state: e.target.value }))} /></div>
                <div className="form-field"><label>Country</label><input value={teacherForm.country} onChange={(e) => setTeacherForm((p) => ({ ...p, country: e.target.value }))} /></div>
                <div className="form-field"><label>Pincode</label><input value={teacherForm.pincode} onChange={(e) => setTeacherForm((p) => ({ ...p, pincode: e.target.value }))} /></div>
                <div className="form-field">
                  <label>{teacherForm.id ? "Login Password (optional)" : "Login Password"}</label>
                  <div className="password-wrap">
                    <input type={showPasswords.teacher ? "text" : "password"} value={teacherForm.password} onChange={(e) => setTeacherForm((p) => ({ ...p, password: e.target.value }))} />
                    <button type="button" className="password-toggle" onClick={() => setShowPasswords((p) => ({ ...p, teacher: !p.teacher }))}>
                      <AppIcon name={showPasswords.teacher ? "eyeOff" : "eye"} />
                    </button>
                  </div>
                </div>
                <div className="form-field"><label>Notes</label><input value={teacherForm.notes} onChange={(e) => setTeacherForm((p) => ({ ...p, notes: e.target.value }))} /></div>
                <div className="form-field" style={{ gridColumn: "span 2" }}>
                  <label>
                    <input type="checkbox" checked={teacherForm.isActive} onChange={(e) => setTeacherForm((p) => ({ ...p, isActive: e.target.checked }))} /> Teacher Active
                  </label>
                </div>

                <div className="admin-form-actions">
                  <button type="submit" disabled={savingTeacher}>{savingTeacher ? "Saving..." : (teacherForm.id ? "Update Teacher" : "Save Teacher")}</button>
                  <button type="button" className="ghost" onClick={() => setTeacherForm(initialTeacherForm)}>Reset</button>
                </div>
              </form>
            </section>
          )}

          {activeTab === "fee-collection" && (
            <section>
              <div className="table-header-row list-header">
                <div>
                  <h2 style={{ margin: 0 }}>Students Collection Fees Table</h2>
                  <div className="list-stat-chips">
                    <span className="stat-chip neutral">Records: {feeStats.total}</span>
                    <span className="stat-chip success">Paid: {feeStats.paid}</span>
                    <span className="stat-chip danger">Pending: {feeStats.pending}</span>
                  </div>
                </div>
                <div className="table-tools">
                  <select value={feeAcademicYear} onChange={(e) => setFeeAcademicYear(e.target.value)}>
                    {feeAcademicYearOptions.length === 0 && <option value="">No Years</option>}
                    {feeAcademicYearOptions.map((year) => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                  <select value={feeMonthKey} onChange={(e) => setFeeMonthKey(e.target.value)}>
                    {filteredFeeMonthOptions.length === 0 && <option value="">No Months</option>}
                    {filteredFeeMonthOptions.map((month) => (
                      <option key={month.monthKey} value={month.monthKey}>{month.monthLabel}</option>
                    ))}
                  </select>
                  <input
                    value={feeSearchId}
                    onChange={(e) => setFeeSearchId(e.target.value)}
                    placeholder="# ID Search Here..."
                  />
                  <input
                    value={feeSearchPhone}
                    onChange={(e) => setFeeSearchPhone(e.target.value)}
                    placeholder="Phone Number..."
                  />
                </div>
              </div>

              <div className="dash-table-wrap list-table-card">
                <table className="dash-table fancy-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th>Parents Name</th>
                      <th>Class</th>
                      <th>Section</th>
                      <th>Month</th>
                      <th>Fees</th>
                      <th>Status</th>
                      <th>Phone</th>
                      <th>E-mail</th>
                      <th>Date</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredFeeStudents.length === 0 && <tr><td colSpan={12}>No students found.</td></tr>}
                    {pagedFeeStudents.map((student) => {
                      const parent = student.parent || {};
                      const month = (student.fees?.monthlyFees || []).find((item) => item.monthKey === feeMonthKey);
                      const status = month?.status === "paid" ? "paid" : "pending";
                      const feeValue = Number(month?.amount || 0);
                      return (
                        <tr key={student.id}>
                          <td>#{student.rollNo || student.admissionNo || String(student.id).slice(-4)}</td>
                          <td>
                            <div className="list-primary-cell">
                              {student.photoUrl ? (
                                <img src={student.photoUrl} alt={student.name} className="mini-avatar" />
                              ) : (
                                <span className="mini-avatar placeholder"><AppIcon name="child" size={12} /></span>
                              )}
                              <span>{student.name}</span>
                            </div>
                          </td>
                          <td>{parent.name || "-"}</td>
                          <td>{student.class || "-"}</td>
                          <td>{student.section || "-"}</td>
                          <td>{month?.monthLabel || "-"}</td>
                          <td>{money(feeValue || 0)}</td>
                          <td>
                            <span className={status === "paid" ? "status-pill paid" : "status-pill due"}>
                              {status === "paid" ? "Paid" : "Pending"}
                            </span>
                          </td>
                          <td>{parent.phone || "-"}</td>
                          <td>{parent.email || "-"}</td>
                          <td>{month?.paidAt ? new Date(month.paidAt).toLocaleDateString() : (month?.dueDate ? new Date(month.dueDate).toLocaleDateString() : "-")}</td>
                          <td>
                            <div className="icon-actions">
                              <button type="button" className="icon-action-btn" title="View Payment Details" onClick={() => openPaymentDetails(student)}>
                                <AppIcon name="eye" />
                              </button>
                              <button type="button" className="icon-action-btn" title="Create Payment" onClick={() => startOfflinePaymentForStudent(student)}>
                                <AppIcon name="fee" />
                              </button>
                              <button
                                type="button"
                                className="icon-action-btn success"
                                title={status === "paid" ? "Already Paid" : "Quick Mark Paid"}
                                onClick={() => requestQuickMarkFeeStatus(student, "paid")}
                                disabled={status === "paid"}
                              >
                                <AppIcon name="check" />
                              </button>
                              <button
                                type="button"
                                className="icon-action-btn warn"
                                title={status === "paid" ? "Quick Mark Pending" : "Already Pending"}
                                onClick={() => requestQuickMarkFeeStatus(student, "pending")}
                                disabled={status !== "paid"}
                              >
                                <AppIcon name="close" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="table-tools" style={{ marginTop: 10 }}>
                <button type="button" className="table-btn" onClick={() => setFeePage((p) => Math.max(1, p - 1))} disabled={feePage <= 1}>Prev</button>
                <span style={{ alignSelf: "center" }}>Page {Math.min(feePage, feeTotalPages)} of {feeTotalPages}</span>
                <button type="button" className="table-btn" onClick={() => setFeePage((p) => Math.min(feeTotalPages, p + 1))} disabled={feePage >= feeTotalPages}>Next</button>
              </div>
            </section>
          )}

          {activeTab === "annual-collection" && (
            <section>
              <div className="table-header-row list-header">
                <div>
                  <h2 style={{ margin: 0 }}>Students Annual Fee Table</h2>
                  <div className="list-stat-chips">
                    <span className="stat-chip neutral">Records: {annualStats.total}</span>
                    <span className="stat-chip success">Paid: {annualStats.paid}</span>
                    <span className="stat-chip danger">Pending: {annualStats.pending}</span>
                  </div>
                </div>
                <div className="table-tools">
                  <input
                    value={annualSearchId}
                    onChange={(e) => setAnnualSearchId(e.target.value)}
                    placeholder="# ID Search Here..."
                  />
                  <input
                    value={annualSearchPhone}
                    onChange={(e) => setAnnualSearchPhone(e.target.value)}
                    placeholder="Phone Number..."
                  />
                </div>
              </div>

              <div className="dash-table-wrap list-table-card">
                <table className="dash-table fancy-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th>Parents Name</th>
                      <th>Class</th>
                      <th>Section</th>
                      <th>Academic Year</th>
                      <th>Annual Fee</th>
                      <th>Status</th>
                      <th>Phone</th>
                      <th>E-mail</th>
                      <th>Date</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAnnualStudents.length === 0 && <tr><td colSpan={12}>No students found.</td></tr>}
                    {pagedAnnualStudents.map((student) => {
                      const parent = student.parent || {};
                      const annualFee = student.fees?.annualFee || {};
                      const status = annualFee.status === "paid" ? "paid" : "pending";
                      return (
                        <tr key={`${student.id}-annual`}>
                          <td>#{student.rollNo || student.admissionNo || String(student.id).slice(-4)}</td>
                          <td>
                            <div className="list-primary-cell">
                              {student.photoUrl ? (
                                <img src={student.photoUrl} alt={student.name} className="mini-avatar" />
                              ) : (
                                <span className="mini-avatar placeholder"><AppIcon name="child" size={12} /></span>
                              )}
                              <span>{student.name}</span>
                            </div>
                          </td>
                          <td>{parent.name || "-"}</td>
                          <td>{student.class || "-"}</td>
                          <td>{student.section || "-"}</td>
                          <td>{annualFee.academicYear || student.academicYear || "-"}</td>
                          <td>{money(annualFee.amount || 0)}</td>
                          <td>
                            <span className={status === "paid" ? "status-pill paid" : "status-pill due"}>
                              {status === "paid" ? "Paid" : "Pending"}
                            </span>
                          </td>
                          <td>{parent.phone || "-"}</td>
                          <td>{parent.email || "-"}</td>
                          <td>{annualFee.paidAt ? new Date(annualFee.paidAt).toLocaleDateString() : (annualFee.dueDate ? new Date(annualFee.dueDate).toLocaleDateString() : "-")}</td>
                          <td>
                            <div className="icon-actions">
                              <button type="button" className="icon-action-btn" title="View Payment Details" onClick={() => openPaymentDetails(student, "annual")}>
                                <AppIcon name="eye" />
                              </button>
                              <button type="button" className="icon-action-btn" title="Create Annual Payment" onClick={() => startOfflinePaymentForStudent(student, "annual")}>
                                <AppIcon name="fee" />
                              </button>
                              <button
                                type="button"
                                className="icon-action-btn success"
                                title={status === "paid" ? "Already Paid" : "Quick Mark Paid"}
                                onClick={() => requestQuickMarkFeeStatus(student, "paid", "annual")}
                                disabled={status === "paid"}
                              >
                                <AppIcon name="check" />
                              </button>
                              <button
                                type="button"
                                className="icon-action-btn warn"
                                title={status === "paid" ? "Quick Mark Pending" : "Already Pending"}
                                onClick={() => requestQuickMarkFeeStatus(student, "pending", "annual")}
                                disabled={status !== "paid"}
                              >
                                <AppIcon name="close" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="table-tools" style={{ marginTop: 10 }}>
                <button type="button" className="table-btn" onClick={() => setAnnualPage((p) => Math.max(1, p - 1))} disabled={annualPage <= 1}>Prev</button>
                <span style={{ alignSelf: "center" }}>Page {Math.min(annualPage, annualTotalPages)} of {annualTotalPages}</span>
                <button type="button" className="table-btn" onClick={() => setAnnualPage((p) => Math.min(annualTotalPages, p + 1))} disabled={annualPage >= annualTotalPages}>Next</button>
              </div>
            </section>
          )}

          {activeTab === "admission-collection" && (
            <section>
              <div className="table-header-row list-header">
                <div>
                  <h2 style={{ margin: 0 }}>Students Admission Fee Table</h2>
                  <div className="list-stat-chips">
                    <span className="stat-chip neutral">Records: {admissionStats.total}</span>
                    <span className="stat-chip success">Paid: {admissionStats.paid}</span>
                    <span className="stat-chip danger">Pending: {admissionStats.pending}</span>
                  </div>
                </div>
                <div className="table-tools">
                  <input
                    value={admissionSearchId}
                    onChange={(e) => setAdmissionSearchId(e.target.value)}
                    placeholder="# ID Search Here..."
                  />
                  <input
                    value={admissionSearchPhone}
                    onChange={(e) => setAdmissionSearchPhone(e.target.value)}
                    placeholder="Phone Number..."
                  />
                </div>
              </div>

              <div className="dash-table-wrap list-table-card">
                <table className="dash-table fancy-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th>Parents Name</th>
                      <th>Class</th>
                      <th>Section</th>
                      <th>Academic Year</th>
                      <th>Admission Fee</th>
                      <th>Status</th>
                      <th>Phone</th>
                      <th>E-mail</th>
                      <th>Date</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAdmissionStudents.length === 0 && <tr><td colSpan={12}>No students found.</td></tr>}
                    {pagedAdmissionStudents.map((student) => {
                      const parent = student.parent || {};
                      const admissionFee = student.fees?.admissionFee || {};
                      const status = admissionFee.status === "paid" ? "paid" : "pending";
                      return (
                        <tr key={`${student.id}-admission`}>
                          <td>#{student.rollNo || student.admissionNo || String(student.id).slice(-4)}</td>
                          <td>
                            <div className="list-primary-cell">
                              {student.photoUrl ? (
                                <img src={student.photoUrl} alt={student.name} className="mini-avatar" />
                              ) : (
                                <span className="mini-avatar placeholder"><AppIcon name="child" size={12} /></span>
                              )}
                              <span>{student.name}</span>
                            </div>
                          </td>
                          <td>{parent.name || "-"}</td>
                          <td>{student.class || "-"}</td>
                          <td>{student.section || "-"}</td>
                          <td>{admissionFee.academicYear || student.academicYear || "-"}</td>
                          <td>{money(admissionFee.amount || 0)}</td>
                          <td>
                            <span className={status === "paid" ? "status-pill paid" : "status-pill due"}>
                              {status === "paid" ? "Paid" : "Pending"}
                            </span>
                          </td>
                          <td>{parent.phone || "-"}</td>
                          <td>{parent.email || "-"}</td>
                          <td>{admissionFee.paidAt ? new Date(admissionFee.paidAt).toLocaleDateString() : (admissionFee.dueDate ? new Date(admissionFee.dueDate).toLocaleDateString() : "-")}</td>
                          <td>
                            <div className="icon-actions">
                              <button type="button" className="icon-action-btn" title="View Payment Details" onClick={() => openPaymentDetails(student, "admission")}>
                                <AppIcon name="eye" />
                              </button>
                              <button type="button" className="icon-action-btn" title="Create Admission Payment" onClick={() => startOfflinePaymentForStudent(student, "admission")}>
                                <AppIcon name="fee" />
                              </button>
                              <button
                                type="button"
                                className="icon-action-btn success"
                                title={status === "paid" ? "Already Paid" : "Quick Mark Paid"}
                                onClick={() => requestQuickMarkFeeStatus(student, "paid", "admission")}
                                disabled={status === "paid"}
                              >
                                <AppIcon name="check" />
                              </button>
                              <button
                                type="button"
                                className="icon-action-btn warn"
                                title={status === "paid" ? "Quick Mark Pending" : "Already Pending"}
                                onClick={() => requestQuickMarkFeeStatus(student, "pending", "admission")}
                                disabled={status !== "paid"}
                              >
                                <AppIcon name="close" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="table-tools" style={{ marginTop: 10 }}>
                <button type="button" className="table-btn" onClick={() => setAdmissionPage((p) => Math.max(1, p - 1))} disabled={admissionPage <= 1}>Prev</button>
                <span style={{ alignSelf: "center" }}>Page {Math.min(admissionPage, admissionTotalPages)} of {admissionTotalPages}</span>
                <button type="button" className="table-btn" onClick={() => setAdmissionPage((p) => Math.min(admissionTotalPages, p + 1))} disabled={admissionPage >= admissionTotalPages}>Next</button>
              </div>
            </section>
          )}

          {activeTab === "transport-collection" && (
            <section>
              <div className="table-header-row list-header">
                <div>
                  <h2 style={{ margin: 0 }}>Transport Collection Table</h2>
                  <div className="list-stat-chips">
                    <span className="stat-chip neutral">Records: {transportStats.total}</span>
                    <span className="stat-chip success">Paid: {transportStats.paid}</span>
                    <span className="stat-chip danger">Pending: {transportStats.pending}</span>
                  </div>
                </div>
                <div className="table-tools">
                  <select value={transportMonthKey} onChange={(e) => setTransportMonthKey(e.target.value)}>
                    {transportMonthOptions.length === 0 && <option value="">No Months</option>}
                    {transportMonthOptions.map((month) => (
                      <option key={month.monthKey} value={month.monthKey}>{month.monthLabel}</option>
                    ))}
                  </select>
                  <input
                    value={transportSearchId}
                    onChange={(e) => setTransportSearchId(e.target.value)}
                    placeholder="# ID Search Here..."
                  />
                  <input
                    value={transportSearchPhone}
                    onChange={(e) => setTransportSearchPhone(e.target.value)}
                    placeholder="Phone Number..."
                  />
                </div>
              </div>

              <div className="dash-table-wrap list-table-card">
                <table className="dash-table fancy-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th>Parents Name</th>
                      <th>Class</th>
                      <th>Section</th>
                      <th>Route</th>
                      <th>Month</th>
                      <th>Transport Fee</th>
                      <th>Status</th>
                      <th>Phone</th>
                      <th>E-mail</th>
                      <th>Date</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTransportStudents.length === 0 && <tr><td colSpan={13}>No transport students found.</td></tr>}
                    {pagedTransportStudents.map((student) => {
                      const parent = student.parent || {};
                      const month = (student.transport?.monthlyFees || []).find((item) => item.monthKey === transportMonthKey);
                      const status = month?.status === "paid" ? "paid" : "pending";
                      const feeValue = Number(month?.amount || 0);
                      return (
                        <tr key={`${student.id}-transport`}>
                          <td>#{student.rollNo || student.admissionNo || String(student.id).slice(-4)}</td>
                          <td>
                            <div className="list-primary-cell">
                              {student.photoUrl ? (
                                <img src={student.photoUrl} alt={student.name} className="mini-avatar" />
                              ) : (
                                <span className="mini-avatar placeholder"><AppIcon name="child" size={12} /></span>
                              )}
                              <span>{student.name}</span>
                            </div>
                          </td>
                          <td>{parent.name || "-"}</td>
                          <td>{student.class || "-"}</td>
                          <td>{student.section || "-"}</td>
                          <td>{student.transport?.routeName || "-"}</td>
                          <td>{month?.monthLabel || "-"}</td>
                          <td>{money(feeValue || 0)}</td>
                          <td>
                            <span className={status === "paid" ? "status-pill paid" : "status-pill due"}>
                              {status === "paid" ? "Paid" : "Pending"}
                            </span>
                          </td>
                          <td>{parent.phone || "-"}</td>
                          <td>{parent.email || "-"}</td>
                          <td>{month?.paidAt ? new Date(month.paidAt).toLocaleDateString() : (month?.dueDate ? new Date(month.dueDate).toLocaleDateString() : "-")}</td>
                          <td>
                            <div className="icon-actions">
                              <button type="button" className="icon-action-btn" title="Create Transport Payment" onClick={() => startOfflinePaymentForStudent(student, "transport")}>
                                <AppIcon name="transport" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="table-tools" style={{ marginTop: 10 }}>
                <button type="button" className="table-btn" onClick={() => setTransportPage((p) => Math.max(1, p - 1))} disabled={transportPage <= 1}>Prev</button>
                <span className="dash-breadcrumb">Page {Math.min(transportPage, transportTotalPages)} of {transportTotalPages}</span>
                <button type="button" className="table-btn" onClick={() => setTransportPage((p) => Math.min(transportTotalPages, p + 1))} disabled={transportPage >= transportTotalPages}>Next</button>
              </div>
            </section>
          )}

          {["upi-proofs", "annual-upi-proofs", "admission-upi-proofs", "transport-upi-proofs"].includes(activeTab) && (
            <section>
              <div className="table-header-row list-header">
                <div>
                  <h2 style={{ margin: 0 }}>
                    {activeTab === "transport-upi-proofs"
                      ? "Transport UPI Payment Proofs"
                      : activeTab === "annual-upi-proofs"
                        ? "Annual UPI Payment Proofs"
                        : activeTab === "admission-upi-proofs"
                          ? "Admission UPI Payment Proofs"
                        : "Tuition UPI Payment Proofs"}
                  </h2>
                  <p className="dash-breadcrumb" style={{ margin: "6px 0 0" }}>
                    Verify screenshot and payment details before approving.
                  </p>
                </div>
                <div className="table-tools">
                  <select value={proofStatus} onChange={(e) => setProofStatus(e.target.value)}>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
              </div>

              <div className="dash-table-wrap list-table-card">
                <table className="dash-table fancy-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Student</th>
                      <th>Type</th>
                      <th>Coverage</th>
                      <th>Amount</th>
                      <th>Txn ID</th>
                      <th>Parent</th>
                      <th>Screenshot</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {proofs.length === 0 && <tr><td colSpan={10}>No proofs found.</td></tr>}
                    {proofs.map((proof) => (
                      <tr key={proof.id}>
                        <td>{new Date(proof.createdAt).toLocaleDateString()}</td>
                        <td>{proof.student?.name || "-"}</td>
                        <td>{getFeeTypeLabel(proof.feeType)}</td>
                        <td>
                          {(proof.coverageStartKey || proof.monthKey) === (proof.coverageEndKey || proof.monthKey)
                            ? (proof.coverageStartKey || proof.monthKey)
                            : `${proof.coverageStartKey || proof.monthKey} to ${proof.coverageEndKey || proof.monthKey}`}
                          <div className="dash-subtitle" style={{ padding: 0 }}>
                            {["annual", "admission"].includes(proof.feeType)
                              ? `${getFeeTypeLabel(proof.feeType)} payment`
                              : `${Number(proof.monthsToCover || proof.coveredMonths?.length || 1)} month(s)`}
                          </div>
                        </td>
                        <td>{money(proof.amount)}</td>
                        <td>{proof.txnId}</td>
                        <td>{proof.parent?.name || proof.parent?.email || "-"}</td>
                        <td>
                          {proof.screenshotUrl ? (
                            <button type="button" className="table-btn" onClick={() => openProofPreview(proof)}>View</button>
                          ) : "-"}
                        </td>
                        <td>
                          <span className={proof.status === "approved" ? "status-pill paid" : proof.status === "rejected" ? "status-pill due" : "status-pill pending"}>
                            {proof.status}
                          </span>
                        </td>
                        <td>
                          <div className="icon-actions">
                            <button
                              type="button"
                              className="icon-action-btn success"
                              title="Approve"
                              onClick={() => requestProofDecision(proof, "approve")}
                              disabled={proof.status !== "pending"}
                            >
                              <AppIcon name="check" />
                            </button>
                            <button
                              type="button"
                              className="icon-action-btn warn"
                              title="Reject"
                              onClick={() => requestProofDecision(proof, "reject")}
                              disabled={proof.status !== "pending"}
                            >
                              <AppIcon name="close" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {activeTab === "payment-history" && (
            <section>
              <div className="table-header-row list-header payment-history-header">
                <div>
                  <h2 style={{ margin: 0 }}>Payment History</h2>
                  <div className="list-stat-chips">
                    <span className="stat-chip neutral">Records: {paymentHistoryPagination.totalRecords || paymentHistoryRows.length}</span>
                    <span className="stat-chip success">Paid: {paymentHistoryStats.paid?.count || 0}</span>
                    <span className="stat-chip neutral">Total: {money(paymentHistoryStats.totalAmount || 0)}</span>
                  </div>
                </div>
              </div>

              <div className="payment-history-filter-card">
                <div className="table-tools payment-history-tools">
                  <select
                    value={paymentReportFilters.className}
                    onChange={(e) => setPaymentReportFilters((prev) => ({ ...prev, className: e.target.value }))}
                    title="Report class"
                  >
                    <option value="">Select Class For Report</option>
                    {promotionClassOptions.map((className) => (
                      <option key={className} value={className}>Class {className}</option>
                    ))}
                  </select>
                  <input
                    type="month"
                    value={paymentReportFilters.month}
                    onChange={(e) => setPaymentReportFilters((prev) => ({ ...prev, month: e.target.value }))}
                    title="Report month"
                  />
                  <select
                    value={paymentReportFilters.feeType}
                    onChange={(e) => setPaymentReportFilters((prev) => ({ ...prev, feeType: e.target.value }))}
                    title="Report fee type"
                  >
                    <option value="tuition">Tuition</option>
                    <option value="transport">Transport</option>
                  </select>
                  <button
                    type="button"
                    className="table-btn"
                    onClick={printMonthlyPaymentsPdf}
                    disabled={!paymentReportFilters.className || !paymentReportFilters.month || paymentHistoryLoading}
                    title={paymentReportFilters.className && paymentReportFilters.month ? "Open class-wise PDF report" : "Select class and month first"}
                  >
                    Export PDF
                  </button>
                  <button
                    type="button"
                    className="table-btn"
                    onClick={exportMonthlyPaymentsToExcel}
                    disabled={!paymentReportFilters.className || !paymentReportFilters.month || paymentHistoryLoading}
                    title={paymentReportFilters.className && paymentReportFilters.month ? "Download class-wise Excel report" : "Select class and month first"}
                  >
                    Export Excel
                  </button>
                </div>
                <div className="table-tools payment-history-tools">
                  <input
                    className="payment-history-search"
                    value={paymentHistoryFilters.search}
                    onChange={(e) => setPaymentHistoryFilters((prev) => ({ ...prev, search: e.target.value }))}
                    placeholder="Search student, parent, ID..."
                  />
                  <select
                    value={paymentHistoryFilters.feeType}
                    onChange={(e) => setPaymentHistoryFilters((prev) => ({ ...prev, feeType: e.target.value }))}
                  >
                    <option value="">All Fee Types</option>
                    <option value="tuition">Tuition</option>
                    <option value="annual">Annual</option>
                    <option value="admission">Admission</option>
                    <option value="transport">Transport</option>
                  </select>
                  <select
                    value={paymentHistoryFilters.status}
                    onChange={(e) => setPaymentHistoryFilters((prev) => ({ ...prev, status: e.target.value }))}
                  >
                    <option value="">All Status</option>
                    <option value="paid">Paid</option>
                    <option value="created">Created</option>
                    <option value="failed">Failed</option>
                  </select>
                  <select
                    value={paymentHistoryFilters.method}
                    onChange={(e) => setPaymentHistoryFilters((prev) => ({ ...prev, method: e.target.value }))}
                  >
                    <option value="">All Methods</option>
                    <option value="Cash">Cash</option>
                    <option value="UPI">UPI</option>
                    <option value="Offline">Offline</option>
                    <option value="Bank Transfer">Bank Transfer</option>
                    <option value="Card">Card</option>
                    <option value="Cheque">Cheque</option>
                  </select>
                  <input
                    value={paymentHistoryFilters.academicYear}
                    onChange={(e) => setPaymentHistoryFilters((prev) => ({ ...prev, academicYear: e.target.value }))}
                    placeholder="Academic Year"
                  />
                  <input
                    type="date"
                    value={paymentHistoryFilters.from}
                    onChange={(e) => setPaymentHistoryFilters((prev) => ({ ...prev, from: e.target.value }))}
                    title="Paid from"
                  />
                  <input
                    type="date"
                    value={paymentHistoryFilters.to}
                    onChange={(e) => setPaymentHistoryFilters((prev) => ({ ...prev, to: e.target.value }))}
                    title="Paid to"
                  />
                  <button
                    type="button"
                    className="table-btn payment-history-reset"
                    onClick={() => {
                      setPaymentHistoryFilters({ search: "", feeType: "", status: "paid", method: "", month: "", academicYear: "", from: "", to: "" });
                      setPaymentReportFilters({ className: "", month: "", feeType: "tuition" });
                    }}
                  >
                    Reset
                  </button>
                </div>
              </div>

              <div className="dash-table-wrap list-table-card">
                <table className="dash-table fancy-table">
                  <thead>
                    <tr>
                      <th>Date & Time</th>
                      <th>Student</th>
                      <th>Parent</th>
                      <th>Type</th>
                      <th>Period</th>
                      <th>Amount</th>
                      <th>Method</th>
                      <th>Screenshot</th>
                      <th>Status</th>
                      <th>Payment ID</th>
                      <th>Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paymentHistoryLoading && <tr><td colSpan={11}>Loading payment history...</td></tr>}
                    {!paymentHistoryLoading && paymentHistoryRows.length === 0 && <tr><td colSpan={11}>No payments found.</td></tr>}
                    {!paymentHistoryLoading && paymentHistoryRows.map((payment) => (
                      <tr key={payment._id}>
                        <td>{formatPaymentDateTime(payment.paidAt || payment.createdAt)}</td>
                        <td>
                          <strong>{payment.student?.name || "-"}</strong><br />
                          <span className="small-muted">Class {payment.student?.class || "-"}-{payment.student?.section || "NA"} | Roll {payment.student?.rollNo || "-"}</span>
                        </td>
                        <td>
                          {payment.parent?.name || "-"}<br />
                          <span className="small-muted">{payment.parent?.phone || payment.parent?.email || "-"}</span>
                        </td>
                        <td>{getFeeTypeLabel(payment.feeType)}</td>
                        <td>{["annual", "admission"].includes(payment.feeType) ? (payment.academicYear || payment.monthKey || "-") : (payment.monthKey || "-")}</td>
                        <td>{money(payment.amount || 0)}</td>
                        <td>{payment.paymentMethod || "-"}</td>
                        <td>
                          {payment.paymentProof?.screenshotUrl ? (
                            <button type="button" className="table-btn" onClick={() => openPaymentHistoryProof(payment)}>View</button>
                          ) : (
                            <span className="small-muted">Not uploaded</span>
                          )}
                        </td>
                        <td>
                          <span className={payment.status === "paid" ? "status-pill paid" : payment.status === "failed" ? "status-pill due" : "status-pill pending"}>
                            {payment.status || "-"}
                          </span>
                        </td>
                        <td>{payment.razorpay_payment_id || payment.razorpay_order_id || "-"}</td>
                        <td>{payment.remarks || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="table-tools" style={{ marginTop: 10 }}>
                <button type="button" className="table-btn" onClick={() => setPaymentHistoryPage((p) => Math.max(1, p - 1))} disabled={paymentHistoryPage <= 1}>Prev</button>
                <span className="dash-breadcrumb">Page {Math.min(paymentHistoryPage, paymentHistoryPagination.totalPages || 1)} of {paymentHistoryPagination.totalPages || 1}</span>
                <button type="button" className="table-btn" onClick={() => setPaymentHistoryPage((p) => Math.min(paymentHistoryPagination.totalPages || 1, p + 1))} disabled={paymentHistoryPage >= (paymentHistoryPagination.totalPages || 1)}>Next</button>
              </div>
            </section>
          )}

          {activeTab === "create-payment" && (
            <section>
              <div className="dash-panel">
                <h3 style={{ marginTop: 0 }}>Payment Information</h3>
                <form className="admin-form" onSubmit={submitOfflinePayment}>
                  <div className="form-field">
                    <label>Name</label>
                    <select
                      value={offlinePaymentForm.studentId}
                      onChange={(e) => {
                        const studentId = e.target.value;
                        const student = students.find((item) => String(item.id) === String(studentId));
                        const months = offlinePaymentForm.feeType === "transport"
                          ? (student?.transport?.monthlyFees || [])
                          : ["annual", "admission"].includes(offlinePaymentForm.feeType)
                            ? [getOneTimeFeeEntry(student, offlinePaymentForm.feeType)].filter(Boolean)
                          : (student?.fees?.monthlyFees || []);
                        const preferredMonth = months.find((month) => month.status !== "paid")
                          || months[0];
                        setOfflinePaymentForm((prev) => ({
                          ...prev,
                          studentId,
                          monthKey: preferredMonth?.monthKey || "",
                          amount: preferredMonth?.amount || ""
                        }));
                      }}
                      required
                    >
                      <option value="">Select Student</option>
                      {students.map((student) => <option key={student.id} value={student.id}>{student.name}</option>)}
                    </select>
                  </div>
                  <div className="form-field">
                    <label>Fee Type</label>
                    <select
                      value={offlinePaymentForm.feeType}
                      onChange={(e) => {
                        const feeType = e.target.value;
                        const months = feeType === "transport"
                          ? (paymentStudent?.transport?.monthlyFees || [])
                          : ["annual", "admission"].includes(feeType)
                            ? [getOneTimeFeeEntry(paymentStudent, feeType)].filter(Boolean)
                          : (paymentStudent?.fees?.monthlyFees || []);
                        const preferredMonth = months.find((month) => month.status !== "paid") || months[0];
                        setOfflinePaymentForm((prev) => ({
                          ...prev,
                          feeType,
                          monthKey: preferredMonth?.monthKey || "",
                          amount: preferredMonth?.amount || ""
                        }));
                      }}
                    >
                      <option value="tuition">Tuition</option>
                      <option value="annual">Annual</option>
                      <option value="admission">Admission</option>
                      <option value="transport" disabled={!paymentStudent?.transport?.enabled}>Transport</option>
                    </select>
                  </div>
                  <div className="form-field">
                    <label>ID</label>
                    <input value={paymentStudent ? (paymentStudent.rollNo || paymentStudent.admissionNo || paymentStudent.id) : ""} readOnly />
                  </div>
                  <div className="form-field">
                    <label>Class</label>
                    <input value={paymentStudent?.class || ""} readOnly />
                  </div>
                  <div className="form-field">
                    <label>Section</label>
                    <input value={paymentStudent?.section || ""} readOnly />
                  </div>
                  <div className="form-field">
                    <label>{getPaymentPeriodLabel(offlinePaymentForm.feeType)}</label>
                    <select
                      value={offlinePaymentForm.monthKey}
                      onChange={(e) => {
                        const monthKey = e.target.value;
                        const month = paymentMonthOptions.find((item) => item.monthKey === monthKey);
                        setOfflinePaymentForm((prev) => ({
                          ...prev,
                          monthKey,
                          amount: month ? month.amount : prev.amount
                        }));
                      }}
                      required
                    >
                      <option value="">{`Select ${getPaymentPeriodLabel(offlinePaymentForm.feeType)}`}</option>
                      {paymentMonthOptions.map((month) => (
                        <option key={month.monthKey} value={month.monthKey}>
                          {month.monthLabel} ({month.status === "paid" ? "Paid" : "Pending"})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-field">
                    <label>Total Fee</label>
                    <input
                      type="number"
                      min="0"
                      value={offlinePaymentForm.amount}
                      onChange={(e) => setOfflinePaymentForm((prev) => ({ ...prev, amount: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="form-field">
                    <label>Payment Method</label>
                    <select
                      value={offlinePaymentForm.paymentMethod}
                      onChange={(e) => setOfflinePaymentForm((prev) => ({ ...prev, paymentMethod: e.target.value }))}
                    >
                      <option value="Cash">Cash</option>
                      <option value="UPI">UPI</option>
                      <option value="Bank Transfer">Bank Transfer</option>
                      <option value="Card">Card</option>
                      <option value="Cheque">Cheque</option>
                    </select>
                  </div>
                  <div className="form-field">
                    <label>Status</label>
                    <select
                      value={offlinePaymentForm.status}
                      onChange={(e) => setOfflinePaymentForm((prev) => ({ ...prev, status: e.target.value }))}
                    >
                      <option value="paid">Paid</option>
                      <option value="pending">Pending</option>
                    </select>
                  </div>
                  <div className="form-field">
                    <label>Date</label>
                    <input
                      type="date"
                      value={offlinePaymentForm.paymentDate}
                      onChange={(e) => setOfflinePaymentForm((prev) => ({ ...prev, paymentDate: e.target.value }))}
                    />
                  </div>
                  <div className="form-field" style={{ gridColumn: "span 2" }}>
                    <label>Remarks</label>
                    <input
                      value={offlinePaymentForm.remarks}
                      onChange={(e) => setOfflinePaymentForm((prev) => ({ ...prev, remarks: e.target.value }))}
                      placeholder="Optional notes for offline entry"
                    />
                  </div>
                  <div className="admin-form-actions">
                    <button type="submit" disabled={savingOfflinePayment}>{savingOfflinePayment ? "Saving..." : "Save"}</button>
                    <button type="button" className="ghost" onClick={() => setOfflinePaymentForm(initialOfflinePaymentForm)}>Reset</button>
                  </div>
                </form>
              </div>
            </section>
          )}

          {activeTab === "inventory" && (
            <section>
              <div className="admin-metric-grid inventory-metric-grid">
                <article className="metric-card">
                  <h4>Total Items</h4>
                  <strong>{inventorySummary.totalItems || 0}</strong>
                </article>
                <article className="metric-card">
                  <h4>Total Units</h4>
                  <strong>{Number(inventorySummary.totalUnits || 0).toLocaleString("en-IN")}</strong>
                </article>
                <article className="metric-card">
                  <h4>Stock Value</h4>
                  <strong>{money(inventorySummary.totalValue || 0)}</strong>
                </article>
                <article className="metric-card">
                  <h4>Low / Out</h4>
                  <strong>{inventorySummary.lowStockCount || 0} / {inventorySummary.outOfStockCount || 0}</strong>
                </article>
              </div>

              <div className="dashboard-lower-grid inventory-grid">
                <div className="dash-panel">
                  <h3 style={{ marginTop: 0 }}>{inventoryForm.id ? "Edit Inventory Item" : "Add Inventory Item"}</h3>
                  <form className="admin-form" onSubmit={submitInventoryForm}>
                    <div className="form-field"><label>Item Name *</label><input value={inventoryForm.name} onChange={(e) => setInventoryForm((prev) => ({ ...prev, name: e.target.value }))} required /></div>
                    <div className="form-field"><label>SKU</label><input value={inventoryForm.sku} onChange={(e) => setInventoryForm((prev) => ({ ...prev, sku: e.target.value }))} /></div>
                    <div className="form-field"><label>Category</label><input value={inventoryForm.category} onChange={(e) => setInventoryForm((prev) => ({ ...prev, category: e.target.value }))} /></div>
                    <div className="form-field"><label>Sub Category</label><input value={inventoryForm.subCategory} onChange={(e) => setInventoryForm((prev) => ({ ...prev, subCategory: e.target.value }))} /></div>
                    <div className="form-field"><label>Unit</label><input value={inventoryForm.unit} onChange={(e) => setInventoryForm((prev) => ({ ...prev, unit: e.target.value }))} placeholder="pcs, box, kg" /></div>
                    <div className="form-field"><label>Opening Stock</label><input type="number" min="0" value={inventoryForm.quantity} onChange={(e) => setInventoryForm((prev) => ({ ...prev, quantity: e.target.value }))} /></div>
                    <div className="form-field"><label>Min Stock</label><input type="number" min="0" value={inventoryForm.minStock} onChange={(e) => setInventoryForm((prev) => ({ ...prev, minStock: e.target.value }))} /></div>
                    <div className="form-field"><label>Reorder Level</label><input type="number" min="0" value={inventoryForm.reorderLevel} onChange={(e) => setInventoryForm((prev) => ({ ...prev, reorderLevel: e.target.value }))} /></div>
                    <div className="form-field"><label>Unit Cost (INR)</label><input type="number" min="0" value={inventoryForm.unitCost} onChange={(e) => setInventoryForm((prev) => ({ ...prev, unitCost: e.target.value }))} /></div>
                    <div className="form-field"><label>Location</label><input value={inventoryForm.location} onChange={(e) => setInventoryForm((prev) => ({ ...prev, location: e.target.value }))} placeholder="Room / rack" /></div>
                    <div className="form-field"><label>Supplier Name</label><input value={inventoryForm.supplierName} onChange={(e) => setInventoryForm((prev) => ({ ...prev, supplierName: e.target.value }))} /></div>
                    <div className="form-field"><label>Supplier Phone</label><input value={inventoryForm.supplierPhone} onChange={(e) => setInventoryForm((prev) => ({ ...prev, supplierPhone: e.target.value }))} /></div>
                    <div className="form-field" style={{ gridColumn: "span 2" }}><label>Notes</label><input value={inventoryForm.notes} onChange={(e) => setInventoryForm((prev) => ({ ...prev, notes: e.target.value }))} /></div>
                    <div className="admin-form-actions">
                      <button type="submit" disabled={savingInventory}>{savingInventory ? "Saving..." : inventoryForm.id ? "Update Item" : "Create Item"}</button>
                      <button type="button" className="ghost" onClick={startInventoryCreate}>Reset</button>
                    </div>
                  </form>
                </div>

                <div className="dash-panel">
                  <h3 style={{ marginTop: 0 }}>Quick Stock Adjust</h3>
                  <form className="admin-form inventory-adjust-form" onSubmit={submitStockAdjust}>
                    <div className="form-field" style={{ gridColumn: "span 2" }}>
                      <label>Item *</label>
                      <select
                        value={inventoryAdjustForm.itemId}
                        onChange={(e) => {
                          const itemId = e.target.value;
                          const item = inventoryItems.find((row) => String(row.id) === String(itemId)) || null;
                          setSelectedInventoryItem(item);
                          setInventoryAdjustForm((prev) => ({
                            ...prev,
                            itemId,
                            unitCost: item?.unitCost || prev.unitCost
                          }));
                          fetchInventoryMovements(itemId).catch(() => {});
                        }}
                        required
                      >
                        <option value="">Select Item</option>
                        {inventoryItems.map((item) => <option key={item.id} value={item.id}>{item.name} ({item.sku || "No SKU"})</option>)}
                      </select>
                    </div>
                    <div className="form-field"><label>Type</label>
                      <select value={inventoryAdjustForm.type} onChange={(e) => setInventoryAdjustForm((prev) => ({ ...prev, type: e.target.value }))}>
                        <option value="in">Stock In</option>
                        <option value="out">Stock Out</option>
                        <option value="set">Set Exact</option>
                      </select>
                    </div>
                    <div className="form-field"><label>Quantity *</label><input type="number" min="1" value={inventoryAdjustForm.quantity} onChange={(e) => setInventoryAdjustForm((prev) => ({ ...prev, quantity: e.target.value }))} required /></div>
                    <div className="form-field"><label>Unit Cost</label><input type="number" min="0" value={inventoryAdjustForm.unitCost} onChange={(e) => setInventoryAdjustForm((prev) => ({ ...prev, unitCost: e.target.value }))} /></div>
                    <div className="form-field"><label>Reference No</label><input value={inventoryAdjustForm.referenceNo} onChange={(e) => setInventoryAdjustForm((prev) => ({ ...prev, referenceNo: e.target.value }))} placeholder="Bill / GRN / slip no." /></div>
                    <div className="form-field" style={{ gridColumn: "span 2" }}><label>Reason</label><input value={inventoryAdjustForm.reason} onChange={(e) => setInventoryAdjustForm((prev) => ({ ...prev, reason: e.target.value }))} placeholder="Purchase / issue to class / damaged / correction" /></div>
                    <div className="admin-form-actions">
                      <button type="submit" disabled={savingInventoryAdjust}>{savingInventoryAdjust ? "Updating..." : "Update Stock"}</button>
                    </div>
                  </form>
                </div>
              </div>

              <div className="table-header-row list-header">
                <div>
                  <h2 style={{ margin: 0 }}>Inventory Items</h2>
                  <div className="list-stat-chips">
                    <span className="stat-chip neutral">Records: {inventoryItems.length}</span>
                    <span className="stat-chip success">Categories: {inventorySummary.categoriesCount || 0}</span>
                    <span className="stat-chip danger">Low/Out: {(inventorySummary.lowStockCount || 0) + (inventorySummary.outOfStockCount || 0)}</span>
                  </div>
                </div>
                <div className="table-tools">
                  <input placeholder="Search item / SKU / category" value={inventorySearch} onChange={(e) => setInventorySearch(e.target.value)} />
                  <select value={inventoryCategoryFilter} onChange={(e) => setInventoryCategoryFilter(e.target.value)}>
                    <option value="">All Categories</option>
                    {inventoryCategories.map((category) => <option key={category} value={category}>{category}</option>)}
                  </select>
                  <select value={inventoryStatusFilter} onChange={(e) => setInventoryStatusFilter(e.target.value)}>
                    <option value="all">All Status</option>
                    <option value="ok">In Stock</option>
                    <option value="low">Low Stock</option>
                    <option value="out">Out Of Stock</option>
                  </select>
                </div>
              </div>

              <div className="dash-table-wrap list-table-card">
                <table className="dash-table fancy-table">
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th>Category</th>
                      <th>Stock</th>
                      <th>Min</th>
                      <th>Unit Cost</th>
                      <th>Value</th>
                      <th>Location</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inventoryItems.length === 0 && <tr><td colSpan={9}>No inventory items found.</td></tr>}
                    {pagedInventoryItems.map((item) => (
                      <tr key={item.id}>
                        <td>
                          <strong>{item.name}</strong><br />
                          <span className="small-muted">SKU: {item.sku || "-"}</span>
                        </td>
                        <td>{item.category || "-"}</td>
                        <td>{item.quantity} {item.unit || ""}</td>
                        <td>{item.minStock}</td>
                        <td>{money(item.unitCost)}</td>
                        <td>{money(item.stockValue)}</td>
                        <td>{item.location || "-"}</td>
                        <td>
                          <span className={item.status === "ok" ? "status paid" : item.status === "low" ? "status pending" : "status due"}>
                            {item.status === "ok" ? "In Stock" : item.status === "low" ? "Low Stock" : "Out"}
                          </span>
                        </td>
                        <td>
                          <div className="icon-actions">
                            <button type="button" className="icon-action-btn info" title="Edit Item" onClick={() => startInventoryEdit(item)}>
                              <AppIcon name="edit" />
                            </button>
                            <button type="button" className="icon-action-btn" title="Adjust Stock" onClick={() => startStockAdjust(item)}>
                              <AppIcon name="assignment" />
                            </button>
                            <button type="button" className="icon-action-btn warn" title="Archive Item" onClick={() => requestDeleteInventory(item.id)}>
                              <AppIcon name="trash" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="table-tools" style={{ marginTop: 10 }}>
                <button type="button" className="table-btn" onClick={() => setInventoryPage((p) => Math.max(1, p - 1))} disabled={inventoryPage <= 1}>Prev</button>
                <span className="dash-breadcrumb">Page {Math.min(inventoryPage, inventoryTotalPages)} of {inventoryTotalPages}</span>
                <button type="button" className="table-btn" onClick={() => setInventoryPage((p) => Math.min(inventoryTotalPages, p + 1))} disabled={inventoryPage >= inventoryTotalPages}>Next</button>
              </div>

              <div className="dash-panel" style={{ marginTop: 12 }}>
                <h3 style={{ marginTop: 0 }}>Recent Stock Movements {selectedInventoryItem?.name ? `- ${selectedInventoryItem.name}` : ""}</h3>
                <div className="dash-table-wrap">
                  <table className="dash-table">
                    <thead><tr><th>Date</th><th>Type</th><th>Qty</th><th>From</th><th>To</th><th>Reason</th><th>Ref</th></tr></thead>
                    <tbody>
                      {inventoryMovements.length === 0 && <tr><td colSpan={7}>No stock movements found for selected item.</td></tr>}
                      {inventoryMovements.map((row) => (
                        <tr key={row.id}>
                          <td>{new Date(row.createdAt).toLocaleString()}</td>
                          <td>{String(row.type || "").toUpperCase()}</td>
                          <td>{row.quantity}</td>
                          <td>{row.previousQuantity}</td>
                          <td>{row.newQuantity}</td>
                          <td>{row.reason || "-"}</td>
                          <td>{row.referenceNo || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>
          )}

          {activeTab === "timetable" && (
            <section>
              <div className="dashboard-lower-grid">
                <div className="dash-panel">
                  <h3>Create Timetable Entry</h3>
                  <form className="admin-form" onSubmit={submitTimetableEntry}>
                    <div className="form-field"><label>Class *</label><input value={timetableForm.className} onChange={(e) => setTimetableForm((p) => ({ ...p, className: e.target.value }))} required /></div>
                    <div className="form-field">
                      <label>Section</label>
                      <select value={timetableForm.section} onChange={(e) => setTimetableForm((p) => ({ ...p, section: e.target.value }))}>
                        <option value="">Select Section</option>
                        {SECTION_OPTIONS.map((section) => <option key={section} value={section}>{section}</option>)}
                      </select>
                    </div>
                    <div className="form-field"><label>Day (1-7)</label><input type="number" min="1" max="7" value={timetableForm.dayOfWeek} onChange={(e) => setTimetableForm((p) => ({ ...p, dayOfWeek: e.target.value }))} /></div>
                    <div className="form-field"><label>Period</label><input type="number" min="1" value={timetableForm.period} onChange={(e) => setTimetableForm((p) => ({ ...p, period: e.target.value }))} /></div>
                    <div className="form-field"><label>Start Time</label><input type="time" value={timetableForm.startTime} onChange={(e) => setTimetableForm((p) => ({ ...p, startTime: e.target.value }))} /></div>
                    <div className="form-field"><label>End Time</label><input type="time" value={timetableForm.endTime} onChange={(e) => setTimetableForm((p) => ({ ...p, endTime: e.target.value }))} /></div>
                    <div className="form-field"><label>Subject</label><input value={timetableForm.subject} onChange={(e) => setTimetableForm((p) => ({ ...p, subject: e.target.value }))} /></div>
                    <div className="form-field"><label>Teacher Name</label><input value={timetableForm.teacherName} onChange={(e) => setTimetableForm((p) => ({ ...p, teacherName: e.target.value }))} /></div>
                    <div className="form-field"><label>Room</label><input value={timetableForm.room} onChange={(e) => setTimetableForm((p) => ({ ...p, room: e.target.value }))} /></div>
                    <div className="admin-form-actions">
                      <button type="submit" disabled={savingTimetable}>{savingTimetable ? "Saving..." : "Save Entry"}</button>
                      <button type="button" className="ghost" onClick={() => setTimetableForm(initialTimetableForm)}>Reset</button>
                    </div>
                  </form>
                </div>
                <div className="dash-panel">
                  <h3>Timetable Preview</h3>
                  <p className="dash-subtitle" style={{ padding: 0 }}>Create entries with same class/section/day/period to overwrite quickly.</p>
                </div>
              </div>
              <div className="dash-table-wrap list-table-card" style={{ marginTop: 12 }}>
                <table className="dash-table fancy-table">
                  <thead><tr><th>Class</th><th>Section</th><th>Day</th><th>Period</th><th>Time</th><th>Subject</th><th>Teacher</th><th>Room</th><th>Action</th></tr></thead>
                  <tbody>
                    {timetableEntries.length === 0 && <tr><td colSpan={9}>No timetable entries found.</td></tr>}
                    {timetableEntries.map((row) => (
                      <tr key={row._id}>
                        <td>{row.className}</td>
                        <td>{row.section || "-"}</td>
                        <td>{row.dayOfWeek}</td>
                        <td>{row.period}</td>
                        <td>{row.startTime || "-"} {row.endTime ? `- ${row.endTime}` : ""}</td>
                        <td>{row.subject || "-"}</td>
                        <td>{row.teacherName || "-"}</td>
                        <td>{row.room || "-"}</td>
                        <td>
                          <button type="button" className="icon-action-btn warn" onClick={() => deleteTimetableEntry(row._id)} title="Delete">
                            <AppIcon name="trash" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {activeTab === "defaulters" && (
            <section>
              <div className="table-header-row list-header">
                <div>
                  <h2 style={{ margin: 0 }}>Fee Defaulters</h2>
                  <p className="dash-breadcrumb" style={{ margin: "6px 0 0" }}>Track pending fees and send reminders.</p>
                </div>
                <div className="table-tools">
                  <input type="month" value={defaulterMonth} onChange={(e) => setDefaulterMonth(e.target.value)} />
                </div>
              </div>
              <div className="dash-table-wrap list-table-card">
                <table className="dash-table fancy-table">
                  <thead><tr><th>Student</th><th>Class</th><th>Roll</th><th>Parent</th><th>Phone</th><th>Month</th><th>Due</th><th>Action</th></tr></thead>
                  <tbody>
                    {defaulterRows.length === 0 && <tr><td colSpan={8}>No defaulters for selected month.</td></tr>}
                    {defaulterRows.map((row) => (
                      <tr key={`${row.studentId}-${row.monthKey}`}>
                        <td>{row.name}</td>
                        <td>{row.class}-{row.section || "NA"}</td>
                        <td>{row.rollNo || "-"}</td>
                        <td>{row.parent?.name || "-"}</td>
                        <td>{row.parent?.phone || "-"}</td>
                        <td>{row.monthKey}</td>
                        <td>{money(row.amount)}</td>
                        <td>
                          <button type="button" className="table-btn" onClick={() => sendDefaulterReminder(row)} disabled={sendingReminderId === String(row.studentId)}>
                            {sendingReminderId === String(row.studentId) ? "Sending..." : "Send Reminder"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {activeTab === "transport-defaulters" && (
            <section>
              <div className="table-header-row list-header">
                <div>
                  <h2 style={{ margin: 0 }}>Transport Defaulters</h2>
                  <p className="dash-breadcrumb" style={{ margin: "6px 0 0" }}>Track pending transport fees and send reminders.</p>
                </div>
                <div className="table-tools">
                  <input type="month" value={transportDefaulterMonth} onChange={(e) => setTransportDefaulterMonth(e.target.value)} />
                </div>
              </div>
              <div className="dash-table-wrap list-table-card">
                <table className="dash-table fancy-table">
                  <thead><tr><th>Student</th><th>Class</th><th>Roll</th><th>Route</th><th>Parent</th><th>Phone</th><th>Month</th><th>Due</th><th>Action</th></tr></thead>
                  <tbody>
                    {transportDefaulterRows.length === 0 && <tr><td colSpan={9}>No transport defaulters for selected month.</td></tr>}
                    {transportDefaulterRows.map((row) => (
                      <tr key={`transport-${row.studentId}-${row.monthKey}`}>
                        <td>{row.name}</td>
                        <td>{row.class}-{row.section || "NA"}</td>
                        <td>{row.rollNo || "-"}</td>
                        <td>{row.routeName || "-"}</td>
                        <td>{row.parent?.name || "-"}</td>
                        <td>{row.parent?.phone || "-"}</td>
                        <td>{row.monthKey}</td>
                        <td>{money(row.amount)}</td>
                        <td>
                          <button
                            type="button"
                            className="table-btn"
                            onClick={() => sendTransportDefaulterReminder(row)}
                            disabled={sendingReminderId === `transport-${String(row.studentId)}`}
                          >
                            {sendingReminderId === `transport-${String(row.studentId)}` ? "Sending..." : "Send Reminder"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {activeTab === "archive" && (
            <section>
              <div className="dashboard-lower-grid">
                <div className="dash-panel">
                  <h3>Archive Old Records</h3>
                  <form className="admin-form" onSubmit={(e) => { e.preventDefault(); runArchive(); }}>
                    <div className="form-field">
                      <label>Archive Records Before Date *</label>
                      <input type="date" value={archiveBeforeDate} onChange={(e) => setArchiveBeforeDate(e.target.value)} required />
                    </div>
                    <div className="form-field" style={{ gridColumn: "span 2" }}>
                      <label>Entities</label>
                      <div className="icon-actions" style={{ flexWrap: "wrap", gap: 8 }}>
                        {Object.keys(archiveEntities).map((key) => (
                          <label key={key} className="mini-tag-btn" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                            <input
                              type="checkbox"
                              checked={archiveEntities[key]}
                              onChange={(e) => setArchiveEntities((prev) => ({ ...prev, [key]: e.target.checked }))}
                            />
                            {key}
                          </label>
                        ))}
                      </div>
                    </div>
                    <div className="admin-form-actions">
                      <button type="submit" disabled={runningArchive}>{runningArchive ? "Archiving..." : "Run Archive"}</button>
                    </div>
                  </form>
                  <p className="dash-subtitle" style={{ padding: 0 }}>
                    This is soft archive only. Data is retained and can be restored by DB query if needed.
                  </p>
                </div>
                <div className="dash-panel">
                  <h3>Archive Summary</h3>
                  <div className="dash-table-wrap">
                    <table className="dash-table">
                      <thead><tr><th>Entity</th><th>Active</th><th>Archived</th></tr></thead>
                      <tbody>
                        {archiveSummary.length === 0 && <tr><td colSpan={3}>No summary available.</td></tr>}
                        {archiveSummary.map((row) => (
                          <tr key={row.entity}>
                            <td>{row.entity}</td>
                            <td>{row.active || 0}</td>
                            <td>{row.archived || 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </section>
          )}

          {activeTab === "notices" && (
            <section>
              <div className="dashboard-lower-grid">
                <div className="dash-panel">
                  <h3>Create Notice</h3>
                  <form className="admin-form" onSubmit={submitNotice}>
                    <div className="form-field"><label>Title *</label><input value={noticeForm.title} onChange={(e) => setNoticeForm((p) => ({ ...p, title: e.target.value }))} required /></div>
                    <div className="form-field"><label>Notice Date</label><input type="date" value={noticeForm.noticeDate} onChange={(e) => setNoticeForm((p) => ({ ...p, noticeDate: e.target.value }))} /></div>
                    <div className="form-field" style={{ gridColumn: "span 2" }}><label>Details</label><input value={noticeForm.details} onChange={(e) => setNoticeForm((p) => ({ ...p, details: e.target.value }))} /></div>
                    <div className="form-field">
                      <label>Send To *</label>
                      <select value={noticeForm.targetType} onChange={(e) => {
                        const targetType = e.target.value;
                        setNoticeTeacherSearch("");
                        setNoticeStudentSearch("");
                        setNoticeForm((p) => ({ ...p, targetType, targetTeachers: [], targetStudents: [] }));
                      }}>
                        <option value="all">All</option>
                        <option value="teachers">Teachers</option>
                        <option value="students">Students</option>
                      </select>
                    </div>
                    {noticeForm.targetType === "teachers" && (
                      <div className="form-field" style={{ gridColumn: "span 2" }}>
                        <label>Select Teachers (leave empty for all teachers)</label>
                        <input
                          className="select2-search"
                          placeholder="Search teachers..."
                          value={noticeTeacherSearch}
                          onChange={(e) => setNoticeTeacherSearch(e.target.value)}
                        />
                        <select
                          multiple
                          className="select2-like"
                          value={noticeForm.targetTeachers}
                          onChange={(e) => {
                            const values = Array.from(e.target.selectedOptions).map((opt) => opt.value);
                            setNoticeForm((p) => ({ ...p, targetTeachers: values }));
                          }}
                        >
                          {filteredNoticeTeachers.map((teacher) => (
                            <option key={teacher.id} value={teacher.id}>{teacher.name} ({teacher.email || "no-email"})</option>
                          ))}
                        </select>
                      </div>
                    )}
                    {noticeForm.targetType === "students" && (
                      <div className="form-field" style={{ gridColumn: "span 2" }}>
                        <label>Select Students (leave empty for all students)</label>
                        <input
                          className="select2-search"
                          placeholder="Search students..."
                          value={noticeStudentSearch}
                          onChange={(e) => setNoticeStudentSearch(e.target.value)}
                        />
                        <select
                          multiple
                          className="select2-like"
                          value={noticeForm.targetStudents}
                          onChange={(e) => {
                            const values = Array.from(e.target.selectedOptions).map((opt) => opt.value);
                            setNoticeForm((p) => ({ ...p, targetStudents: values }));
                          }}
                        >
                          {filteredNoticeStudents.map((student) => (
                            <option key={student.id} value={student.id}>{student.name} ({student.class}-{student.section || "NA"})</option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div className="admin-form-actions">
                      <button type="submit" disabled={savingNotice}>{savingNotice ? "Saving..." : (noticeForm.id ? "Update Notice" : "Create Notice")}</button>
                      {noticeForm.id && <button type="button" className="ghost" onClick={() => setNoticeForm({ ...initialNoticeForm })}>Cancel</button>}
                    </div>
                  </form>
                </div>
                <div className="dash-panel">
                  <h3>Notice Board</h3>
                  <div className="notice-feed">
                    {notices.length === 0 && <p>No notices available.</p>}
                    {notices.map((notice) => (
                      <article key={notice.id} className="notice-feed-item">
                        <h3>{notice.title}</h3>
                        <p>{notice.details || "-"}</p>
                        <small>
                          {new Date(notice.noticeDate || notice.createdAt).toLocaleDateString()} | Target: {notice.targetType === "teachers" && (notice.targetTeachers || []).length === 0 ? "all teachers" : notice.targetType === "students" && (notice.targetStudents || []).length === 0 ? "all students" : (notice.targetType || "all")}
                        </small>
                        <div style={{ marginTop: 8 }}>
                          <button type="button" className="table-btn" onClick={() => startNoticeEdit(notice)}>Edit</button>
                          <button type="button" className="table-btn danger" onClick={() => deleteNotice(notice.id)}>Delete</button>
                        </div>
                      </article>
                    ))}
                  </div>
                </div>
              </div>
            </section>
          )}

          {activeTab === "revenue" && (
            <section>
              <div className="revenue-summary-grid">
                <article className="revenue-summary-card daily">
                  <div className="revenue-summary-icon"><AppIcon name="history" size={18} /></div>
                  <div>
                    <p>Today Revenue</p>
                    <h3>{money(revenue.dailyRevenue)}</h3>
                  </div>
                </article>
                <article className="revenue-summary-card month">
                  <div className="revenue-summary-icon"><AppIcon name="fee" size={18} /></div>
                  <div>
                    <p>This Month Revenue</p>
                    <h3>{money(revenue.monthlyRevenue)}</h3>
                  </div>
                </article>
                <article className="revenue-summary-card year">
                  <div className="revenue-summary-icon"><AppIcon name="revenue" size={18} /></div>
                  <div>
                    <p>This Year Revenue</p>
                    <h3>{money(revenue.yearlyRevenue)}</h3>
                  </div>
                </article>
                <article className="revenue-summary-card neutral">
                  <div className="revenue-summary-icon"><AppIcon name="dashboard" size={18} /></div>
                  <div>
                    <p>Monthly Pending Fees</p>
                    <h3>{money(revenue.monthlyPendingFees || 0)}</h3>
                  </div>
                </article>
              </div>

              <div className="dash-panel revenue-panel">
                <div className="panel-head">
                  <h3>Monthly Revenue Performance</h3>
                  <div className="revenue-panel-meta">
                    <span>Top Month: <strong>{revenueInsights.topMonth.month}</strong></span>
                    <span>{money(revenueInsights.topMonth.total)}</span>
                  </div>
                </div>
                <div className="revenue-month-list">
                  {revenueInsights.rows.map((row) => {
                    const revenueWidth = revenueInsights.maxRevenue > 0
                      ? Math.max(4, Math.round((Number(row.total || 0) / revenueInsights.maxRevenue) * 100))
                      : 0;
                    const paymentWidth = revenueInsights.maxPayments > 0
                      ? Math.max(4, Math.round((Number(row.payments || 0) / revenueInsights.maxPayments) * 100))
                      : 0;
                    return (
                      <div key={row.month} className="revenue-month-item">
                        <div className="revenue-month-head">
                          <strong>{row.month}</strong>
                          <span>{money(row.total)}</span>
                        </div>
                        <div className="revenue-meter">
                          <div className="revenue-meter-bar money" style={{ width: `${revenueWidth}%` }} />
                        </div>
                        <div className="revenue-month-foot">
                          <span>{row.payments} payments</span>
                          <div className="revenue-meter tiny">
                            <div className="revenue-meter-bar payments" style={{ width: `${paymentWidth}%` }} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>
          )}
        </main>
      </section>

      {quickMarkConfirm.open && (
        <div className="confirm-modal-backdrop" role="dialog" aria-modal="true" aria-label="Confirm quick mark status">
          <div className="confirm-modal-card">
            <h3>Confirm Fee Status Update</h3>
            <p>
              Student: <strong>{quickMarkConfirm.student?.name}</strong>
            </p>
            <p>
              {getPaymentPeriodLabel(quickMarkConfirm.feeType)}: <strong>{quickMarkConfirm.entry?.monthLabel}</strong>
            </p>
            <p>
              {getFeeTypeLabel(quickMarkConfirm.feeType)} Fee: <strong>{money(quickMarkConfirm.entry?.amount || 0)}</strong>
            </p>
            <p>
              New Status: <strong>{quickMarkConfirm.status === "paid" ? "Paid" : "Pending"}</strong>
            </p>

            <div className="confirm-modal-actions">
              <button
                type="button"
                className="ghost"
                onClick={() => setQuickMarkConfirm({ open: false, student: null, entry: null, feeType: "tuition", status: "paid" })}
                disabled={processingQuickMark}
              >
                Decline
              </button>
              <button type="button" onClick={quickMarkFeeStatus} disabled={processingQuickMark}>
                {processingQuickMark ? "Confirming..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}

      {paymentDetailsModal.open && (
        <div className="confirm-modal-backdrop" role="dialog" aria-modal="true" aria-label="Payment details">
          <div className="confirm-modal-card payment-details-card">
            <h3>Payment Details</h3>
            {paymentDetailsModal.loading && <p>Loading payment details...</p>}
            {!paymentDetailsModal.loading && paymentDetailsModal.error && (
              <p className="dash-error">{paymentDetailsModal.error}</p>
            )}
            {!paymentDetailsModal.loading && !paymentDetailsModal.error && (
              <>
                <p>Student: <strong>{paymentDetailsModal.student?.name || "-"}</strong></p>
                <p>Class: <strong>{paymentDetailsModal.student?.class || "-"}-{paymentDetailsModal.student?.section || "-"}</strong></p>
                <p>Fee Type: <strong>{getFeeTypeLabel(paymentDetailsModal.feeType)}</strong></p>
                <p>{getPaymentPeriodLabel(paymentDetailsModal.feeType)}: <strong>{paymentDetailsModal.entry?.monthLabel || "-"}</strong></p>
                <p>{getFeeTypeLabel(paymentDetailsModal.feeType)} Fee: <strong>{money(paymentDetailsModal.entry?.amount || 0)}</strong></p>
                <p>Status: <strong>{paymentDetailsModal.entry?.status === "paid" ? "Paid" : "Pending"}</strong></p>
                {paymentDetailsModal.transaction ? (
                  <>
                    <p>Payment Method: <strong>{paymentDetailsModal.transaction.paymentMethod || "-"}</strong></p>
                    <p>Paid Amount: <strong>{money(paymentDetailsModal.transaction.amount || 0)}</strong></p>
                    <p>Date: <strong>{paymentDetailsModal.transaction.paidAt ? new Date(paymentDetailsModal.transaction.paidAt).toLocaleString() : "-"}</strong></p>
                    <p>Payment ID: <strong>{paymentDetailsModal.transaction.razorpay_payment_id || "-"}</strong></p>
                    <p>Order ID: <strong>{paymentDetailsModal.transaction.razorpay_order_id || "-"}</strong></p>
                    <p>Remarks: <strong>{paymentDetailsModal.transaction.remarks || "-"}</strong></p>
                  </>
                ) : (
                  <p>No payment transaction record found for this {getPaymentPeriodLabel(paymentDetailsModal.feeType).toLowerCase()}.</p>
                )}
              </>
            )}

            <div className="confirm-modal-actions">
              <button
                type="button"
                className="ghost"
                onClick={() => setPaymentDetailsModal({ open: false, loading: false, error: "", student: null, entry: null, feeType: "tuition", transaction: null })}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {actionConfirm.open && (
        <div className="confirm-modal-backdrop" role="dialog" aria-modal="true" aria-label="Confirm action">
          <div className="confirm-modal-card">
            <h3>{actionConfirm.title || "Confirm Action"}</h3>
            <p>{actionConfirm.message || "Are you sure?"}</p>
            <div className="confirm-modal-actions">
              <button
                type="button"
                className="ghost"
                onClick={() => setActionConfirm({ open: false, title: "", message: "", actionType: "", targetId: "" })}
                disabled={processingActionConfirm}
              >
                Decline
              </button>
              <button type="button" onClick={runConfirmedAction} disabled={processingActionConfirm}>
                {processingActionConfirm ? "Processing..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
      {proofPreview.open && (
        <div className="confirm-modal-backdrop" role="dialog" aria-modal="true" aria-label="Payment proof preview">
          <div className="confirm-modal-card payment-details-card proof-preview-card">
            <button
              type="button"
              className="profile-modal-close"
              onClick={closeProofPreview}
              aria-label="Close payment proof preview"
            >
              <AppIcon name="close" />
            </button>
            <div className="proof-preview-toolbar">
              <div>
                <h3>Payment Screenshot</h3>
                <span>{getFeeTypeLabel(proofPreview.proof?.feeType)} proof for {proofPreview.proof?.student?.name || "student"}</span>
              </div>
              <div className="proof-zoom-actions">
                <button type="button" className="table-btn" onClick={() => adjustProofZoom((proofPreview.zoom || 1) - 0.25)} title="Zoom out">
                  <AppIcon name="search" /> -
                </button>
                <button type="button" className="table-btn" onClick={() => adjustProofZoom(1)} title="Reset zoom">
                  {Math.round((proofPreview.zoom || 1) * 100)}%
                </button>
                <button type="button" className="table-btn" onClick={() => adjustProofZoom((proofPreview.zoom || 1) + 0.25)} title="Zoom in">
                  <AppIcon name="search" /> +
                </button>
                {proofPreview.proof?.screenshotUrl && (
                  <a className="table-btn" href={proofPreview.proof.screenshotUrl} target="_blank" rel="noreferrer">
                    Open Full Size
                  </a>
                )}
              </div>
            </div>
            {proofPreview.proof?.screenshotUrl && !proofPreview.imageFailed ? (
              <div className="proof-image-viewport">
                <img
                  src={proofPreview.proof.screenshotUrl}
                  alt="Payment proof"
                  className="proof-image"
                  style={{ width: `${Math.round((proofPreview.zoom || 1) * 100)}%` }}
                  onError={() => setProofPreview((prev) => ({ ...prev, imageFailed: true }))}
                />
              </div>
            ) : proofPreview.proof?.screenshotUrl ? (
              <div className="proof-preview-fallback">
                <p>Preview is not available in the modal for this proof.</p>
                <a
                  href={proofPreview.proof.screenshotUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="table-btn"
                >
                  Open Proof Image
                </a>
              </div>
            ) : (
              <p>No screenshot available.</p>
            )}
            <div className="proof-preview-meta">
              <div className="proof-preview-grid">
                <div className="proof-preview-item">
                  <span>Student</span>
                  <strong>{proofPreview.proof?.student?.name || "-"}</strong>
                </div>
                <div className="proof-preview-item">
                  <span>Coverage</span>
                  <strong>
                    {(proofPreview.proof?.coverageStartKey || proofPreview.proof?.monthKey || "-") === (proofPreview.proof?.coverageEndKey || proofPreview.proof?.monthKey || "-")
                      ? (proofPreview.proof?.coverageStartKey || proofPreview.proof?.monthKey || "-")
                      : `${proofPreview.proof?.coverageStartKey || proofPreview.proof?.monthKey || "-"} to ${proofPreview.proof?.coverageEndKey || proofPreview.proof?.monthKey || "-"}`
                    }
                  </strong>
                </div>
                <div className="proof-preview-item">
                  <span>Months</span>
                  <strong>{Number(proofPreview.proof?.monthsToCover || proofPreview.proof?.coveredMonths?.length || 1)}</strong>
                </div>
                <div className="proof-preview-item">
                  <span>Amount</span>
                  <strong>{money(proofPreview.proof?.amount || 0)}</strong>
                </div>
                <div className="proof-preview-item">
                  <span>Txn ID</span>
                  <strong>{proofPreview.proof?.txnId || "-"}</strong>
                </div>
                <div className="proof-preview-item proof-preview-item-full">
                  <span>Remarks</span>
                  <strong>{proofPreview.proof?.remarks || "-"}</strong>
                </div>
              </div>
            </div>
            <div className="confirm-modal-actions">
              <button type="button" className="ghost" onClick={closeProofPreview}>Close</button>
            </div>
          </div>
        </div>
      )}

      {tcPreview.open && (
        <div className="confirm-modal-backdrop" role="dialog" aria-modal="true" aria-label="Transfer certificate preview">
          <div className="confirm-modal-card payment-details-card">
            <h3>Transfer Certificate</h3>
            {!tcPreview.certificate ? (
              <p>No certificate data available.</p>
            ) : (
              <>
                <p>TC No: <strong>{tcPreview.certificate.tcNo || "-"}</strong></p>
                <p>Status: <strong>{tcPreview.certificate.status || "-"}</strong></p>
                <p>Issue Date: <strong>{tcPreview.certificate.issueDate ? new Date(tcPreview.certificate.issueDate).toLocaleDateString() : "-"}</strong></p>
                <p>Student: <strong>{tcPreview.certificate.student?.name || "-"}</strong></p>
                <p>Class: <strong>{tcPreview.certificate.student?.class || "-"}-{tcPreview.certificate.student?.section || "NA"}</strong></p>
                <p>Roll: <strong>{tcPreview.certificate.student?.rollNo || "-"}</strong></p>
                <p>Admission No: <strong>{tcPreview.certificate.student?.admissionNo || "-"}</strong></p>
                <p>Parent: <strong>{tcPreview.certificate.student?.parentName || "-"}</strong></p>
                <p>Last Attendance: <strong>{tcPreview.certificate.lastAttendanceDate ? new Date(tcPreview.certificate.lastAttendanceDate).toLocaleDateString() : "-"}</strong></p>
                <p>Reason: <strong>{tcPreview.certificate.reason || "-"}</strong></p>
                <p>Destination School: <strong>{tcPreview.certificate.destinationSchool || "-"}</strong></p>
                <p>Conduct: <strong>{tcPreview.certificate.conduct || "-"}</strong></p>
                <p>Remarks: <strong>{tcPreview.certificate.remarks || "-"}</strong></p>
                {tcPreview.certificate.status === "cancelled" && (
                  <p>Cancel Reason: <strong>{tcPreview.certificate.cancelReason || "-"}</strong></p>
                )}
              </>
            )}
            <div className="confirm-modal-actions">
              <button
                type="button"
                className="ghost"
                onClick={() => setTcPreview({ open: false, certificate: null })}
              >
                Close
              </button>
              {tcPreview.certificate && (
                <button
                  type="button"
                  onClick={() => printTransferCertificate(tcPreview.certificate)}
                >
                  Print TC
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {tcCancelModal.open && (
        <div className="confirm-modal-backdrop" role="dialog" aria-modal="true" aria-label="Cancel transfer certificate">
          <div className="confirm-modal-card">
            <h3>Cancel Transfer Certificate</h3>
            <p>TC No: <strong>{tcCancelModal.tcNo || "-"}</strong></p>
            <p>This will mark certificate as cancelled and keep an audit record.</p>
            <div className="form-field">
              <label>Reason</label>
              <input
                value={tcCancelModal.reason}
                onChange={(e) => setTcCancelModal((prev) => ({ ...prev, reason: e.target.value }))}
                placeholder="Reason for cancellation"
              />
            </div>
            <div className="confirm-modal-actions">
              <button
                type="button"
                className="ghost"
                onClick={() => setTcCancelModal({ open: false, certificateId: "", tcNo: "", reason: "" })}
                disabled={processingTcCancel}
              >
                Close
              </button>
              <button type="button" onClick={cancelTransferCertificate} disabled={processingTcCancel}>
                {processingTcCancel ? "Cancelling..." : "Confirm Cancel"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
